/* Order Register — Processing Team sub-panel (Iter 146).
 * Upload an Excel Order Register, list every parsed row in a
 * Database-A-style table, add / edit orders manually, filter by
 * customer, mode or medicine, and export the current view back to
 * Excel.
 */
import { TB_TOOLS, OR_WRITE_TOOLS } from "@/components/orderRegisterTools";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { OrderUploadWizard } from "@/components/OrderUploadWizard";
import { OverwriteHistory } from "@/components/OverwriteHistory";
import { ClientCodeInput } from "@/components/ClientCodeInput";
import { ReasonFilter } from "@/components/ReasonFilter";
import { PendingCenter } from "@/components/PendingCenter";
import { BulkReasonDialog } from "@/components/BulkReasonDialog";
import { InvoiceSlipCheckDialog } from "@/components/InvoiceSlipCheckDialog";
import { PendingSlipsButton } from "@/components/PendingSlipsButton";
import { TeamPresetPublishDialog } from "@/components/TeamPresetPublishDialog";
import { RunningInvoiceBadge } from "@/components/RunningInvoiceBadge";
import { EmiratesTrackingUpdate } from "@/components/EmiratesTrackingUpdate";
import { MergedParcelCheck } from "@/components/MergedParcelCheck";
import { PartnerAdmin } from "@/components/PartnerAdmin";
import { PartnerOrdersBadge } from "@/components/PartnerOrdersBadge";
import { PendingByUserButton } from "@/components/PendingByUser";
import { SecondNameSuggestInput } from "@/components/SecondNameSuggestInput";
import { MedicineSuggestInput } from "@/components/MedicineSuggestInput";
import { ReadyBadge } from "@/components/ReadyBadge";
import { DocPrintBar, SelectBox, useDocSelection } from "@/components/DocPrintBar";
import { NoWeightBadge, EmiTrackBadge, MemberPicker, CorrectionsReport, ReleaseInvoiceButton, ReleasedTag, ReleaseHistory } from "@/components/TodayInvExtras";
import { ReasonChips, ReasonPromptHost, askChangeReason } from "@/components/ReasonPrompt";
import { QtyPackingPromptHost, askPacking } from "@/components/QtyPackingPrompt";
import { DupToolbar, DupFlagBadge } from "@/components/OrDupTools";
import { OrderHealthBoard } from "@/components/OrderHealthBoard";
import { PendingServiceReport } from "@/components/PendingServiceReport";
import { PpUserBar, HandbackTag } from "@/components/PpUserBar";
import { QueryHover } from "@/components/QueryHover";
import { OrToolbarMore } from "@/components/OrToolbarMore";
import { OrReadingMode } from "@/components/OrReadingMode";
import { ParcelQueryDialog, ParcelQueryBadge, openParcelQuery } from "@/components/ParcelQueryDialog";
import { QueryToolbar } from "@/components/QueryToolbar";
import { OrderSheetButton } from "@/components/OrderSheetButton";
import { ParcelNoteBadge, hlCell as hc, rowHlStyle } from "@/components/ParcelNotes";
import { AddProductsSection } from "@/components/OrAddProducts";
import { MobileSheetWrap, useIsMobile } from "@/components/MobileKit";
import { OrMobileCards } from "@/components/OrMobileCards";
import { OutsourcedTag } from "@/components/OutsourcedTag";
import { PhotoMissingTag } from "@/components/PhotoReminders";
import { HubMoneyTag, HubTrackNote } from "@/components/HubMoneyTag";
import { openPage } from "@/components/BackBar";
import { JumpToWorkingButton, WorkingTag, workLook, ColourDot } from "@/components/WorkingParcel";
import { TargetCard } from "@/components/TargetCard";
import { LeaderboardButton } from "@/components/Leaderboard";
import {
  Plus, Search, Upload, Download, Trash2, Pencil, RefreshCw,
  PanelTopClose, PanelTopOpen, Ban, Undo2,
  ChevronLeft, ChevronRight, FileSpreadsheet, Printer, EyeOff,
  Wand2, BookmarkPlus, Sparkles, Clock, Truck, X, Package, Lock,
  ScrollText, AlertTriangle, DollarSign, ExternalLink, SlidersHorizontal, ChevronDown,
  MessageSquarePlus, FlaskConical,
  MessageSquareWarning,
} from "lucide-react";

// Iter 286 — Hide Buttons: every Order Register toolbar tool, grouped for the
// customize menu. Hidden tools are removed via CSS (component state preserved)
// and the choice is stored server-side per user (ui_prefs.or_toolbar_hidden).
const TB_TOOL_MAP = Object.fromEntries(
  TB_TOOLS.flatMap(g => g.items.map(it => [it.id, it])));

const PAGE_SIZE = 50;
const sameJson = (a, b) => { try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; } };

/* Search box keeps its own text so typing doesn't re-render the whole register per keystroke. */
const OrSearchInput = ({ value, onCommit }) => {
  const [v, setV] = useState(value);
  const last = useRef(value);
  useEffect(() => { if (value !== last.current) { last.current = value; setV(value); } }, [value]);
  useEffect(() => {
    if (v === last.current) return undefined;
    const t = setTimeout(() => { last.current = v; onCommit(v); }, v.trim() ? 250 : 0);
    return () => clearTimeout(t);
  }, [v, onCommit]);
  return (
    <Input value={v} autoComplete="off" name="or-search" onChange={(e) => setV(e.target.value)}
      placeholder="Search customer, code, country, tracking, medicine…"
      className="pl-8 h-9" data-testid="or-search" />
  );
};
const MODE_OPTIONS = ["", "CMS", "DHL", "DIFF_SVC", "EMIRATES", "EMS", "EMS-SERVICE", "FEDEX", "FREESHIP", "ITPS", "RMS", "SPEED POST"];

const emptyDraft = () => ({
  date: "", code: "", customer_name: "", address: "",
  medicine: "", quantity: "", tracking: "",
  mode: "", notes: "", second_name: "",
  reason: "", invoice_no: "", invoice_date: "",
});

export default function OrderRegisterTab({ user }) {
  const isAdmin = user?.role === "admin";
  // Admin-granted rights: viewer = read-only; allowed_buttons null = all.
  const isViewer = !isAdmin && (user?.order_register_access === "viewer" || !!user?.is_query_team);
  const forbiddenTools = useMemo(() => {
    if (isAdmin) return new Set();
    const allowed = user?.order_register_allowed_buttons;
    const out = new Set(Array.isArray(allowed)
      ? TB_TOOLS.flatMap(g => g.items.map(i => i.id)).filter(id => !allowed.includes(id)) : []);
    if (isViewer) OR_WRITE_TOOLS.forEach(id => out.add(id));
    return out;
  }, [isAdmin, isViewer, user?.order_register_allowed_buttons]);
  const canManageOverwrites = ["admin", "supervisor", "manager"].includes(user?.role);
  const fileRef = useRef(null);
  const fetchKeyRef = useRef("");   // Iter 291 — view signature for the stable-queue merge
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageInfo, setPageInfo] = useState({ parcels: 0, pageCount: 1, pageParcels: 0 });
  const [trackingConflicts, setTrackingConflicts] = useState(new Set());
  // Iter 153 — Alias match confidence map: medicine (upper) -> {second_name, rank}
  const [aliasMatches, setAliasMatches] = useState({});
  const [fillingAliases, setFillingAliases] = useState(false);
  // Iter 155 — Alias suggestion tray. Per-medicine draft second_name.
  const [trayOpen, setTrayOpen] = useState(false);
  const [trayDrafts, setTrayDrafts] = useState({});   // { medicine: secondNameDraft }
  const [traySaving, setTraySaving] = useState(false);
  // Iter 281 — tray rows auto-suggested from Product Aliases when the
  // tray opens, so Save All works in one click. { medicine: true } marks
  // a draft as machine-suggested (amber) until the user edits it.
  const [traySuggested, setTraySuggested] = useState({});
  const [traySuggesting, setTraySuggesting] = useState(false);
  // Iter 236 — $ Price column: monthly INR→USD rate + margin, alias prices
  const [pricing, setPricing] = useState({ inr_per_usd: null, margin_pct: 5, month: null });
  const [priceMap, setPriceMap] = useState({});   // second_name_norm -> price (INR)
  const [pricingOpen, setPricingOpen] = useState(false);
  const [pricingDraft, setPricingDraft] = useState({ inr_per_usd: "", margin_pct: "", guard_password: "" });
  const [pricingSaving, setPricingSaving] = useState(false);

  const normName = (s) => String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  const usdOf = (secondName) => {
    const rate = parseFloat(pricing.inr_per_usd);
    if (!rate || rate <= 0) return null;
    const inr = parseFloat(String(priceMap[normName(secondName)] ?? "").replace(/[^0-9.]/g, ""));
    if (!inr || isNaN(inr)) return null;
    const margin = parseFloat(pricing.margin_pct ?? 5) || 0;
    return Math.round(((inr * (1 + margin / 100)) / rate) * 100) / 100;
  };
  const fetchPricing = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/order-register/pricing-settings`, { headers: getAuthHeader() });
      setPricing(res.data || {});
    } catch { /* pricing optional */ }
  }, []);
  const fetchPriceMap = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/product-aliases/price-map`, { headers: getAuthHeader() });
      setPriceMap(res.data.map || {});
    } catch { /* prices optional */ }
  }, []);
  useEffect(() => { fetchPricing(); fetchPriceMap(); }, [fetchPricing, fetchPriceMap]);
  const openPricing = () => {
    if (isViewer) { toast.info("View-only access — pricing can't be changed"); return; }
    setPricingDraft({
      inr_per_usd: pricing.inr_per_usd ?? "",
      margin_pct: pricing.margin_pct ?? 5,
      guard_password: "",
    });
    setPricingOpen(true);
  };
  const savePricing = async () => {
    const payload = {};
    if (String(pricingDraft.inr_per_usd).trim() !== "") payload.inr_per_usd = parseFloat(pricingDraft.inr_per_usd);
    if (String(pricingDraft.margin_pct).trim() !== "") payload.margin_pct = parseFloat(pricingDraft.margin_pct);
    if (!isAdmin) payload.guard_password = pricingDraft.guard_password;
    setPricingSaving(true);
    try {
      const res = await axios.post(`${API}/order-register/pricing-settings`, payload, { headers: getAuthHeader() });
      setPricing(res.data);
      setPricingOpen(false);
      toast.success("Pricing settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setPricingSaving(false); }
  };

  // Iter 238 — Bulk price update from the "No $ Price" filter view
  const [bulkPriceOpen, setBulkPriceOpen] = useState(false);
  const [bulkPriceDrafts, setBulkPriceDrafts] = useState({}); // second_name -> "12.50"
  const [bulkPriceSaving, setBulkPriceSaving] = useState(false);
  const unpricedNames = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const r of rows) {
      const sn = (r.second_name || "").trim();
      if (!sn) continue;
      const k = normName(sn);
      if (priceMap[k] !== undefined) continue;   // already has a price
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(sn);
    }
    return out;
  }, [rows, priceMap]);
  const openBulkPrice = () => {
    setBulkPriceDrafts({});
    setBulkPriceOpen(true);
  };
  const saveBulkPrices = async () => {
    const prices = {};
    for (const [sn, v] of Object.entries(bulkPriceDrafts)) {
      const s = String(v).trim();
      if (s) prices[sn] = s;
    }
    if (Object.keys(prices).length === 0) {
      toast.error("Enter at least one price"); return;
    }
    setBulkPriceSaving(true);
    try {
      const res = await axios.post(`${API}/product-aliases/bulk-price`,
        { prices }, { headers: getAuthHeader() });
      const d = res.data;
      toast.success(`Prices saved — ${d.updated} updated, ${d.created} created`);
      setBulkPriceOpen(false);
      fetchPriceMap();
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBulkPriceSaving(false); }
  };
  // Iter 157 — Missing-invoice bulk-fill dialog.
  const [bulkInvOpen, setBulkInvOpen] = useState(false);
  const [bulkInvNo, setBulkInvNo] = useState("");
  const [bulkInvDate, setBulkInvDate] = useState("");
  const [bulkInvSaving, setBulkInvSaving] = useState(false);
  // Iter 176 — Reason autocomplete list (server-seeded 6 reasons + free
  // text still accepted). Fetched once on mount from /reasons.
  const [reasonSuggestions, setReasonSuggestions] = useState([
    "Stock not available", "Order given", "Other substitute available",
    "Hold by client", "Release tomorrow", "Slot next week",
  ]);
  // Iter 176 — Pending Parcel report modal (Full / Service-wise / Client-wise + PDF / Excel)
  const [pendingOpen, setPendingOpen] = useState(false);
  const [pendingGrouping, setPendingGrouping] = useState("full");
  const [pendingFmt, setPendingFmt] = useState("excel");
  const [pendingRunning, setPendingRunning] = useState(false);
  // Iter 176 — Emirates Tracking Fill.
  const [emiratesFile, setEmiratesFile] = useState(null);
  const [emiratesRunning, setEmiratesRunning] = useState(false);
  const [emiratesResult, setEmiratesResult] = useState(null);
  const emiratesInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [reasonFilter, setReasonFilter] = useState("");
  const [reasonExact, setReasonExact] = useState(false);
  const [modeOptions, setModeOptions] = useState(MODE_OPTIONS);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Iter 189 — exact invoice-number filter (Today's Invoices row click)
  const [invFilter, setInvFilter] = useState("");
  // Iter 149 — blank-field presets & column hide toggles.
  const [noTracking, setNoTracking] = useState(false);
  const [noInvoice, setNoInvoice] = useState(false);
  const [overdueFilter, setOverdueFilter] = useState(false);   // false | "amber" | "red"
  const [pcOpen, setPcOpen] = useState(false);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [pendingScope, setPendingScope] = useState("with");
  const [age7Filter, setAge7Filter] = useState(false);
  const [rawOnly, setRawOnly] = useState(false);
  const [rawCount, setRawCount] = useState(null);
  const [slipCheckInv, setSlipCheckInv] = useState(null);
  const [slipRefresh, setSlipRefresh] = useState(0);
  // Completed invoices still waiting for a matching packing slip → reopen the check window.
  const [slipDeferred, setSlipDeferred] = useState(false);
  const loadSlipPending = useCallback(async (prefer) => {
    try {
      const r = await axios.get(`${API}/packing-list/slip-check/pending`, { headers: getAuthHeader() });
      const list = r.data || [];
      const pick = (prefer && list.find(x => x.invoice_key === String(prefer).toUpperCase())) || list[list.length - 1];
      if (pick) { setSlipDeferred(false); setSlipCheckInv(cur => cur || pick.invoice_key); }
    } catch { /* non-blocking */ }
  }, []);
  const openSlipFromList = (inv, status) => { setSlipDeferred(status === "deferred"); setSlipCheckInv(inv); };
  useEffect(() => { loadSlipPending(); }, [loadSlipPending]);
  // Iter 279 — ?overdue=red deep link (Ageing digest notification)
  useEffect(() => {
    const apply = () => {
      const v = new URLSearchParams(window.location.search).get("overdue");
      if (v === "red" || v === "amber") { setOverdueFilter(v); setTeamView("all"); }
      if (new URLSearchParams(window.location.search).get("dup") === "1") { setDupOnly(true); setPage(1); }
      const os = new URLSearchParams(window.location.search).get("or_search");
      if (os) { setQ(os); setTeamView("all"); setPage(1); }
      if (new URLSearchParams(window.location.search).get("qs") === "handback") { setQueryState("handback"); setPage(1); }
    };
    apply();
    window.addEventListener("or-deeplink", apply);
    return () => window.removeEventListener("or-deeplink", apply);
  }, []);
  const [noSecondName, setNoSecondName] = useState(false);
  const [noPrice, setNoPrice] = useState(false);
  // Iter 199 — Select checkbox + Edit column hidden by DEFAULT; unticking
  // shows them, Reset restores these defaults.
  const DEFAULT_HIDDEN_COLS = ['select', 'edit'];
  const [hiddenCols, setHiddenCols] = useState(() => {
    try {
      const raw = localStorage.getItem("or_hidden_cols");
      if (raw === null) return new Set([...DEFAULT_HIDDEN_COLS, 'parcelId']);
      const saved = JSON.parse(raw);
      const s = new Set(Array.isArray(saved) ? saved : DEFAULT_HIDDEN_COLS);
      s.add('parcelId');   // Iter 290 — PID hidden by default; Reset shows it
      return s;
    } catch { return new Set([...DEFAULT_HIDDEN_COLS, 'parcelId']); }
  });
  // PP users need row checkboxes for Hand Back to Team.
  useEffect(() => {
    if (user?.is_pp_user) setHiddenCols(prev => { if (!prev.has('select')) return prev; const s = new Set(prev); s.delete('select'); return s; });
  }, [user?.is_pp_user]);
  // Mirror to localStorage on every change.
  useEffect(() => {
    try { localStorage.setItem("or_hidden_cols", JSON.stringify(Array.from(hiddenCols))); }
    catch { /* quota — ignore */ }
  }, [hiddenCols]);
  const [colsMenuOpen, setColsMenuOpen] = useState(false);
  // Iter 261 — menu slate: collapse every toolbar row into one slim row
  // (toggle + global search + Today's Invoices) to save screen space.
  const isMobile = useIsMobile();
  const [slateOpen, setSlateOpen] = useState(() => !window.matchMedia("(max-width: 767px)").matches);
  const [orFiltersOpen, setOrFiltersOpen] = useState(false);
  useEffect(() => { if (isMobile) setSlateOpen(false); }, [isMobile]);
  // Iter 262 — soft-cancel flow: team cancels a parcel with their own
  // login password; cancelled rows hide from Pending and surface via
  // the Cancelled card next to Total Active.
  const [cancelledOnly, setCancelledOnly] = useState(false);
  const [dupOnly, setDupOnly] = useState(false);
  const [dbaCross, setDbaCross] = useState(false);
  const [invWaiting, setInvWaiting] = useState(false);
  const [svcBucket, setSvcBucket] = useState("");
  const [queryState, setQueryState] = useState("");
  const [dupRefresh, setDupRefresh] = useState(0);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelPassword, setCancelPassword] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restorePassword, setRestorePassword] = useState("");
  const [restoring, setRestoring] = useState(false);
  // Iter 265 — bulk cancel: one password + one optional reason for N parcels.
  const [bulkCancelOpen, setBulkCancelOpen] = useState(false);
  const [bulkCancelPassword, setBulkCancelPassword] = useState("");
  const [bulkCancelReason, setBulkCancelReason] = useState("");
  const [bulkCancelling, setBulkCancelling] = useState(false);
  const [bulkReasonOpen, setBulkReasonOpen] = useState(false);
  const colsMenuRef = useRef(null);
  const HIDABLE_COLS = ['select', 'edit', 'address', 'qty', 'parcelId', 'invDate', 'notes'];
  const allColsHidden = HIDABLE_COLS.every(k => hiddenCols.has(k));
  const isHidden = (k) => hiddenCols.has(k) || (isViewer && (k === 'edit' || k === 'select'));
  const toggleCol = (k) => setHiddenCols(prev => {
    const s = new Set(prev); if (s.has(k)) s.delete(k); else s.add(k); return s;
  });
  const toggleAllCols = () => {
    setHiddenCols(prev => allColsHidden ? new Set() : new Set(HIDABLE_COLS));
  };
  // Iter 286 — Hide Buttons: per-user toolbar customization, saved server-side
  // (ui_prefs.or_toolbar_hidden) so the layout follows the user everywhere.
  const [tbMenuOpen, setTbMenuOpen] = useState(false);
  const [tbQuickOpen, setTbQuickOpen] = useState(false);
  const tbMenuRef = useRef(null);
  const [hiddenTools, setHiddenTools] = useState(() => new Set());
  const tbSaveTimer = useRef(null);
  // Iter 287 — Toolbar Presets: named hidden-sets per user (server-saved).
  const [tbPresets, setTbPresets] = useState([]);
  const [tbActive, setTbActive] = useState("");
  // Iter 289 — admin-published Team presets (active ref = "team:<name>").
  const [teamPresets, setTeamPresets] = useState([]);
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/users/me/ui-prefs`, { headers: getAuthHeader() }),
      axios.get(`${API}/ui-team-presets`, { headers: getAuthHeader() }).catch(() => ({ data: {} })),
    ]).then(([r, tr]) => {
      const p = r.data?.ui_prefs || {};
      const team = tr.data?.presets || [];
      setTeamPresets(team);
      setTbPresets(p.or_toolbar_presets || []);
      let hidden = p.or_toolbar_hidden || [];
      let active = p.or_toolbar_active_preset || "";
      if (active.startsWith("team:")) {
        const tp = team.find(x => x.name === active.slice(5));
        if (tp) hidden = tp.hidden || [];   // admin edits reach users on next load
        else active = "";                    // deleted → fall back to own last layout
      }
      setHiddenTools(new Set(hidden));
      setTbActive(active);
    }).catch(() => {});
  }, []);
  const isTeamActive = tbActive.startsWith("team:");
  const activeLabel = isTeamActive ? tbActive.slice(5) : tbActive;
  const putPrefs = (ui_prefs) => axios.put(`${API}/users/me/ui-prefs`,
    { ui_prefs }, { headers: getAuthHeader() });
  const saveHiddenTools = (setVal, active = "") => {
    clearTimeout(tbSaveTimer.current);
    setTbActive(active);
    tbSaveTimer.current = setTimeout(() => {
      putPrefs({ or_toolbar_hidden: [...setVal], or_toolbar_active_preset: active })
        .catch(() => toast.error("Could not save toolbar layout"));
    }, 400);
  };
  const savePresets = async (list, active, okMsg) => {
    try {
      const r = await putPrefs({ or_toolbar_presets: list, or_toolbar_active_preset: active });
      setTbPresets(r.data?.ui_prefs?.or_toolbar_presets || list);
      setTbActive(active);
      if (okMsg) toast.success(okMsg);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save preset"); }
  };
  const savePresetAs = () => {
    const name = (window.prompt("Save current toolbar as preset — name:", activeLabel || "") || "").trim();
    if (!name) return;
    const rest = tbPresets.filter(p => p.name.toLowerCase() !== name.toLowerCase());
    savePresets([...rest, { name, hidden: [...hiddenTools] }], name, `Preset "${name}" saved`);
  };
  const applyPreset = (name) => {
    const p = name.startsWith("team:")
      ? teamPresets.find(x => x.name === name.slice(5))
      : tbPresets.find(x => x.name === name);
    if (!p) return;
    const s = new Set(p.hidden || []);
    setHiddenTools(s);
    saveHiddenTools(s, name);
  };
  const renamePreset = () => {
    if (!tbActive) return;
    const name = (window.prompt(`Rename preset "${tbActive}" to:`, tbActive) || "").trim();
    if (!name || name === tbActive) return;
    if (tbPresets.some(p => p.name.toLowerCase() === name.toLowerCase())) {
      toast.error(`A preset named "${name}" already exists`); return;
    }
    savePresets(tbPresets.map(p => p.name === tbActive ? { ...p, name } : p), name, "Preset renamed");
  };
  const [publishOpen, setPublishOpen] = useState(false);
  const publishTeamPreset = () => setPublishOpen(true);
  const doPublishTeamPreset = async (name, target_teams, target_user_ids) => {
    try {
      const r = await axios.put(`${API}/admin/ui-team-presets/${encodeURIComponent(name)}`,
        { hidden: [...hiddenTools], target_teams, target_user_ids }, { headers: getAuthHeader() });
      setPublishOpen(false);
      setTeamPresets(r.data?.presets || []);
      saveHiddenTools(hiddenTools, `team:${name}`);
      toast.success(`Team preset "${name}" published`);
    } catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
  };
  const unpublishTeamPreset = async () => {
    if (!isTeamActive || !window.confirm(`Unpublish team preset "${activeLabel}" for everyone?`)) return;
    try {
      const r = await axios.delete(`${API}/admin/ui-team-presets/${encodeURIComponent(activeLabel)}`,
        { headers: getAuthHeader() });
      setTeamPresets(r.data?.presets || []);
      saveHiddenTools(hiddenTools, "");
      toast.success("Team preset removed");
    } catch (e) { toast.error(e.response?.data?.detail || "Unpublish failed"); }
  };
  const deletePreset = () => {
    if (!tbActive || !window.confirm(`Delete preset "${tbActive}"?`)) return;
    savePresets(tbPresets.filter(p => p.name !== tbActive), "", "Preset deleted");
  };
  const toggleTool = (id) => setHiddenTools(prev => {
    const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id);
    saveHiddenTools(s); return s;
  });
  const showAllTools = () => { const s = new Set(); setHiddenTools(s); saveHiddenTools(s); };
  useEffect(() => {
    // Phone: menus render inline inside the Filters sheet — bring the opened one into view.
    if (!isMobile || (!tbMenuOpen && !tbQuickOpen)) return;
    const t = setTimeout(() => document.querySelector(`[data-testid="${tbMenuOpen ? "or-tools-menu" : "or-tools-quick-menu"}"]`)
      ?.scrollIntoView({ block: "start", behavior: "smooth" }), 60);
    return () => clearTimeout(t);
  }, [isMobile, tbMenuOpen, tbQuickOpen]);
  useEffect(() => {
    if (!tbMenuOpen && !tbQuickOpen) return;
    const onDown = (e) => {
      if (tbMenuRef.current && !tbMenuRef.current.contains(e.target)) {
        setTbMenuOpen(false); setTbQuickOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [tbMenuOpen, tbQuickOpen]);
  const tbHideCss = useMemo(() => [...new Set([...hiddenTools, ...forbiddenTools])].flatMap(id => {
    const sel = `[data-testid="${id}"]`;
    const rules = [`${sel}{display:none!important}`,
                   `label:has(${sel}){display:none!important}`];
    (TB_TOOL_MAP[id]?.also || []).forEach(a =>
      rules.push(`[data-testid="${a}"]{display:none!important}`));
    return rules;
  }).join("\n"), [hiddenTools, forbiddenTools]);
  // Click-outside handler for the Hide Columns popover.
  useEffect(() => {
    if (!colsMenuOpen) return;
    const onDown = (e) => {
      if (colsMenuRef.current && !colsMenuRef.current.contains(e.target)) {
        setColsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [colsMenuOpen]);
  const [debouncedQ, setDebouncedQ] = useState("");
  const fetchSeqRef = useRef(0);
  const [debouncedClient, setDebouncedClient] = useState("");
  const [debouncedReason, setDebouncedReason] = useState("");
  // Group-level sort (mirrors Pending Parcels Iter-125)
  const [sortKey, setSortKey] = useState(null);   // date | code | customer_name | tracking | mode
  const [sortDir, setSortDir] = useState("asc");
  const cycleSort = (k) => {
    if (sortKey !== k) { setSortKey(k); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    setSortKey(null); setSortDir("asc");
  };
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());  // ids for bulk delete
  // Iter 205 - Scoped bulk-delete (this page / current filters /
  // date-wise / client-wise / country-wise / all data).
  const [delMenuOpen, setDelMenuOpen] = useState(false);
  const delMenuRef = useRef(null);
  const [delScopeOpen, setDelScopeOpen] = useState(false);
  const [delScope, setDelScope] = useState("");   // filtered|all|date|client|country
  const [delPreviewParcels, setDelPreviewParcels] = useState(null);
  const [delDateFrom, setDelDateFrom] = useState("");
  const [delDateTo, setDelDateTo] = useState("");
  const [delClient, setDelClient] = useState("");
  const [delCountry, setDelCountry] = useState("");
  const [delPassword, setDelPassword] = useState("");
  const [delPreview, setDelPreview] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const [countryGroups, setCountryGroups] = useState(
    ["USA", "AUSTRALIA", "UK", "SOUTH_KOREA", "JAPAN", "CHINA", "OTHERS"]);
  useEffect(() => {
    if (!delMenuOpen) return;
    const onDown = (e) => {
      if (delMenuRef.current && !delMenuRef.current.contains(e.target)) {
        setDelMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [delMenuOpen]);
  // Iter 208 - Scoped export (this page / current filters / date-wise /
  // client-wise / country-wise / all data). Read-only, no password.
  const [expMenuOpen, setExpMenuOpen] = useState(false);
  const expMenuRef = useRef(null);
  const [expScopeOpen, setExpScopeOpen] = useState(false);
  const [expScope, setExpScope] = useState("");   // filtered|all|date|client|country
  const [expDateFrom, setExpDateFrom] = useState("");
  const [expDateTo, setExpDateTo] = useState("");
  const [expClient, setExpClient] = useState("");
  const [expSingle, setExpSingle] = useState(false);
  const [expCountry, setExpCountry] = useState("");
  const [expPreview, setExpPreview] = useState(null);
  const [expBusy, setExpBusy] = useState(false);
  useEffect(() => {
    if (!expMenuOpen) return;
    const onDown = (e) => {
      if (expMenuRef.current && !expMenuRef.current.contains(e.target)) {
        setExpMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [expMenuOpen]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [addItems, setAddItems] = useState([]);
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Debounce search inputs
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 50);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedClient(clientFilter.trim().toUpperCase()), 350);
    return () => clearTimeout(t);
  }, [clientFilter]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedReason(reasonFilter.trim()), 350);
    return () => clearTimeout(t);
  }, [reasonFilter]);

  // ---------- Iter 183 — Multi-Parcel Invoice Management (state) ----------
  // Declared BEFORE fetchRows because the fetch callback closes over
  // `invoiceStatusFilter` and `fetchInvoiceStats`.
  const MAX_PARCELS = 12;
  const MAX_PARCELS_EMI = 30; // Iter 201 — Emirates invoices
  const parcelCap = (mode) => /EMIRATES/i.test(mode || "") ? MAX_PARCELS_EMI : MAX_PARCELS;
  const [invoiceStats, setInvoiceStats] = useState(null);
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState(""); // "" | "in_progress" | "completed" | "completed_today" | "has_invoice"
  // Iter 206 - team default view: only show pending work (not-completed
  // invoices, and rows still without an invoice_no). Stored per-user in
  // localStorage so a manager who prefers "All" isn't reset on each visit.
  const [teamView, setTeamView] = useState(() => {
    try { return localStorage.getItem("or_teamview") || "pending"; }
    catch { return "pending"; }
  });
  useEffect(() => {
    try { localStorage.setItem("or_teamview", teamView); } catch (e) {}
  }, [teamView]);
  const [hiddenByView, setHiddenByView] = useState(0);
  // Iter 274 — browsers' password managers sometimes autofill the login
  // email into the Reason / Client / Search box when a password field
  // opens, silently filtering the table to nothing. Clear such values.
  useEffect(() => {
    const em = (user?.email || "").trim().toLowerCase();
    if (!em) return;
    const bad = (v) => (v || "").trim().toLowerCase() === em;
    if (bad(reasonFilter)) { setReasonFilter(""); toast.info("Cleared an auto-filled login email from the Reason filter"); }
    if (bad(clientFilter)) setClientFilter("");
    if (bad(q)) setQ("");
  }, [reasonFilter, clientFilter, q, user?.email]);
  const [expView, setExpView] = useState("");    // export dialog: ''|pending|completed|in_progress
  const [invMgr, setInvMgr] = useState(null);            // invoice doc from API (parcels auto-derived)
  const [invMgrStatus, setInvMgrStatus] = useState("in_progress");
  const [invMgrBusy, setInvMgrBusy] = useState(false);
  // Iter 220 — invoice-owner map for the inline "In use by X" chip and
  // the invoice manager owner row; admin handover dropdown.
  const [invOwners, setInvOwners] = useState({});        // {INVKEY: {owner_name, owner_email, owner_id}}
  const [ownerFilter, setOwnerFilter] = useState("");    // active-owner email filter
  const [sortMode, setSortMode] = useState(() => localStorage.getItem("orSortMode") || "priority");
  const [scopeUser, setScopeUser] = useState(null);       // {id, name} — Pending by User drill-down
  const [activeOwners, setActiveOwners] = useState([]);  // dropdown options
  const [handoverTo, setHandoverTo] = useState("");
  const [handoverBusy, setHandoverBusy] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);    // for admin handover dropdown

  // Iter 188/189/191 — Today's Invoices popup (yesterday+today default range)
  // Declared before fetchRows so fetchRows can refresh the badge safely.
  const _isoYesterday = () => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  };
  const _isoToday = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  };
  const [todayInvOpen, setTodayInvOpen] = useState(false);
  const [todayInvFrom, setTodayInvFrom] = useState(_isoYesterday());
  const [todayInvTo, setTodayInvTo] = useState(_isoToday());
  const [todayInvData, setTodayInvData] = useState(null);
  const [todayInvMember, setTodayInvMember] = useState("");
  const [pendingSlotMode, setPendingSlotMode] = useState('summary'); // 'summary' | 'detailed'
  const [todayInvBusy, setTodayInvBusy] = useState(false);
  const todaySel = useDocSelection();
  const [todaySummary, setTodaySummary] = useState(null); // toolbar badge/chip
  const prevPendingRef = useRef(null);
  const fetchTodaySummary = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/order-register/invoices-by-date`,
        { headers: getAuthHeader(),
          params: { date_from: _isoYesterday(), date_to: _isoToday() } });
      const n = r.data?.pending_no_invoice ?? 0;
      if (prevPendingRef.current !== null && n > prevPendingRef.current) {
        toast.warning(
          `Pending no-invoice parcels rose to ${n} (was ${prevPendingRef.current})`);
      }
      prevPendingRef.current = n;
      setTodaySummary(prev => sameJson(prev, r.data) ? prev : r.data);
    } catch { /* badge stays hidden */ }
  }, []);
  const openTodayInvoices = async (fromOverride, toOverride, memberOverride) => {
    setTodayInvOpen(true);
    setTodayInvBusy(true);
    const f = fromOverride !== undefined ? fromOverride : todayInvFrom;
    const t = toOverride !== undefined ? toOverride : todayInvTo;
    const mem = memberOverride !== undefined ? memberOverride : todayInvMember;
    try {
      const params = {};
      if (f) params.date_from = f;
      if (t) params.date_to = t;
      if (mem) params.member = mem;
      const r = await axios.get(`${API}/order-register/invoices-by-date`,
        { headers: getAuthHeader(), params });
      setTodayInvData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load invoices");
    } finally { setTodayInvBusy(false); }
  };
  // Iter 190 — download the popup table for the current range (Excel/PDF)
  const downloadInvoicesReport = async (fmt) => {
    try {
      const params = { fmt };
      if (todayInvFrom) params.date_from = todayInvFrom;
      if (todayInvTo) params.date_to = todayInvTo;
      if (todayInvMember) params.member = todayInvMember;
      const r = await axios.get(`${API}/order-register/invoices-report`,
        { headers: getAuthHeader(), params, responseType: "blob" });
      const dispo = r.headers["content-disposition"] || "";
      const m = dispo.match(/filename="?([^";]+)"?/);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = m ? m[1] : `INVOICES.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Report download failed");
    }
  };

  const fetchInvoiceStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/order-register/invoice-stats`, { headers: getAuthHeader() });
      setInvoiceStats(prev => sameJson(prev, r.data) ? prev : r.data);
    } catch { setInvoiceStats(null); }
  }, []);

  // Iter 207 - keep the Pending badge count fresh while others work.
  // Iter 243 — also fires immediately on mount (fetchRows no longer calls
  // these on every keystroke) and after local mutations.
  useEffect(() => {
    fetchInvoiceStats();
    fetchTodaySummary();
    const iv = setInterval(() => { if (document.hidden) return; fetchInvoiceStats(); fetchTodaySummary(); }, 30000);
    return () => clearInterval(iv);
  }, [fetchInvoiceStats, fetchTodaySummary]);

  // Iter 314 — each member's working parcel (last one they gave an invoice #).
  const [workMap, setWorkMap] = useState({});
  const [workList, setWorkList] = useState([]);
  const loadWorking = useCallback(() => {
    axios.get(`${API}/order-register/working-parcels`, { headers: getAuthHeader() })
      .then(r => {
        const m = {};
        for (const w of r.data.working || []) for (const id of w.ids || []) m[id] = w;
        setWorkMap(prev => sameJson(prev, m) ? prev : m);
        setWorkList(prev => sameJson(prev, r.data.working || []) ? prev : (r.data.working || []));
      }).catch(() => {});
  }, []);
  useEffect(() => { const t = setInterval(() => { if (!document.hidden) loadWorking(); }, 30000); return () => clearInterval(t); }, [loadWorking]);
  const fetchRows = useCallback(async () => {
    const seq = ++fetchSeqRef.current;  // a slower earlier response must never overwrite a newer view
    setLoading(true);
    axios.get(`${API}/order-register`, { headers: getAuthHeader(), params: { raw_only: true, limit: 1 } })
      .then(r => setRawCount(r.data?.total ?? null)).catch(() => {});
    try {
      const params = { skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE };
      if (debouncedQ) params.q = debouncedQ;
      if (modeFilter) params.mode = modeFilter;
      if (debouncedClient) params.client_code = debouncedClient;
      if (debouncedReason) params.reason = debouncedReason;
      if (debouncedReason && reasonExact) params.reason_exact = true;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (invFilter) params.invoice_no = invFilter;
      if (noTracking) params.no_tracking = true;
      if (noInvoice) params.no_invoice = true;
      if (noSecondName) params.no_second_name = true;
      if (noPrice) params.no_price = true;
      if (overdueFilter) params.overdue = overdueFilter === "red" ? "red" : "amber";
      if (pendingOnly) { params.pending_only = true; params.pending_scope = pendingScope; }
      if (age7Filter) params.age7 = true;
      if (rawOnly) params.raw_only = true;
      if (ownerFilter) params.owner_email = ownerFilter;
      params.sort = sortMode;
      if (scopeUser?.id) params.scope_user = scopeUser.id;
      if (svcBucket) params.svc_bucket = svcBucket;
      if (queryState) params.query_state = queryState;
      // Iter 262 — Cancelled view overrides every other status filter.
      if (dupOnly || dbaCross || invWaiting) {
        if (dupOnly) params.dup_only = true;
        if (dbaCross) params.dba_cross = true;
        if (invWaiting) params.inv_waiting = true;
      } else if (cancelledOnly) {
        params.cancelled_only = true;
      } else {
      // Iter 206 - card filters override the segment toggle; the segment
      // toggle is the default team view.
      // Iter 273 — a card filter (Uncompleted / Completed Today / Total
      // Active) REPLACES the team view; sending both made Completed Today
      // + Pending always return 0 rows.
      if (!invoiceStatusFilter && (teamView === "pending" || teamView === "completed")) {
        params.invoice_status = teamView;
      }
      if (invoiceStatusFilter === "in_progress" || invoiceStatusFilter === "completed") {
        params.invoice_status = invoiceStatusFilter;
      } else if (invoiceStatusFilter === "completed_today") {
        params.completed_today = true;
      } else if (invoiceStatusFilter === "has_invoice") {
        params.has_invoice = true;
      }
      }
      const res = await axios.get(`${API}/order-register`, { headers: getAuthHeader(), params });
      if (seq !== fetchSeqRef.current) return;
      // Iter 291 — stable working queue: a refetch used to replace the
      // whole page in created_at order, so whenever ANOTHER team uploaded
      // orders (or an invoice got completed) every row jumped position and
      // the parcel sequence "reshuffled" mid-work. Now refreshed data is
      // merged INTO the existing row order: rows still on this page keep
      // their slot (content updated), vanished rows drop out, and brand
      // new arrivals append after them. Any view change (filter / page /
      // tab switch) resets the order because the fetch-key differs.
      const fresh = res.data.rows || [];
      const fetchKey = JSON.stringify(params);
      const sameView = fetchKeyRef.current === fetchKey;
      fetchKeyRef.current = fetchKey;
      // Iter 272 — merge by PARCEL, not by row: a new product row that
      // arrives for an existing parcel is slotted inside that parcel (never
      // appended at the bottom), and no row is ever truncated — the server
      // already returns whole parcels per page.
      const pk = (r) => (r.tracking || '').trim().toUpperCase()
        || [r.date, r.code, r.customer_name, r.address].map(v => (v || '').trim().toUpperCase()).join('|');
      setRows(prev => {
        if (!prev.length || !sameView) return fresh;
        const freshBy = new Map();
        for (const f of fresh) {
          const k = pk(f);
          if (!freshBy.has(k)) freshBy.set(k, []);
          freshBy.get(k).push(f);
        }
        const out = [];
        const used = new Set();
        for (const p of prev) {
          const k = pk(p);
          if (used.has(k) || !freshBy.has(k)) continue;
          used.add(k);
          out.push(...freshBy.get(k));
        }
        for (const [k, list] of freshBy) if (!used.has(k)) out.push(...list);
        return out;
      });
      setPageInfo({ parcels: res.data.parcels || 0, pageCount: res.data.page_count || 1,
                    pageParcels: res.data.page_parcels || 0 });
      setTotal(res.data.total || 0);
      setHiddenByView(res.data.hidden_by_view || 0);
      // Iter 243 — stats/summary are global counts; don't refetch them on
      // every search keystroke (they run on mount, after mutations, and
      // on the 30s poll).
      // Fire-and-forget conflict check — mark trackings that already
      // appear in Database A so ops sees a green ✓ ("already shipped").
      const tks = (res.data.rows || []).map(r => (r.tracking || '').toUpperCase()).filter(Boolean);
      if (tks.length) {
        axios.post(`${API}/order-register/tracking-check`, { trackings: tks },
          { headers: getAuthHeader() })
          .then(r => setTrackingConflicts(new Set(r.data.matches || [])))
          .catch(() => setTrackingConflicts(new Set()));
      } else {
        setTrackingConflicts(new Set());
      }
      // Iter 153 — batch alias lookup for match-confidence chips.
      const meds = Array.from(new Set(
        (res.data.rows || []).map(r => (r.medicine || '').trim()).filter(Boolean)));
      if (meds.length) {
        axios.post(`${API}/product-aliases/bulk-lookup`, { medicines: meds },
          { headers: getAuthHeader() })
          .then(r => setAliasMatches(r.data.results || {}))
          .catch(() => setAliasMatches({}));
      } else {
        setAliasMatches({});
      }
      // Iter 220 — batch invoice-owner lookup so the "In use by X" chip
      // renders next to every active invoice cell without per-row calls.
      const invs = Array.from(new Set(
        (res.data.rows || []).map(r => (r.invoice_no || '').toUpperCase().trim())
          .filter(Boolean)));
      if (invs.length) {
        axios.post(`${API}/order-register/invoice-owners`,
          { invoice_nos: invs }, { headers: getAuthHeader() })
          .then(r => setInvOwners(r.data.owners || {}))
          .catch(() => setInvOwners({}));
      } else {
        setInvOwners({});
      }
      // Iter 220 — keep the Owner filter dropdown stocked with everyone
      // who currently owns an active invoice.
      axios.get(`${API}/order-register/active-owners`, { headers: getAuthHeader() })
        .then(r => setActiveOwners(r.data.owners || []))
        .catch(() => setActiveOwners([]));
      loadWorking();
    } catch (e) {
      if (seq === fetchSeqRef.current) toast.error(e.response?.data?.detail || "Failed to load orders");
    } finally { if (seq === fetchSeqRef.current) setLoading(false); }
  }, [debouncedQ, modeFilter, debouncedClient, debouncedReason, dateFrom, dateTo,
      invFilter, noTracking, noInvoice, noSecondName, noPrice, invoiceStatusFilter, teamView, page, reasonExact, overdueFilter, pendingOnly, pendingScope, age7Filter, rawOnly,
      ownerFilter, cancelledOnly, dupOnly, dbaCross, invWaiting, svcBucket, queryState, fetchInvoiceStats, fetchTodaySummary, sortMode, scopeUser]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Iter 262 — cancel a whole parcel with the caller's own login
  // password. Warning dialog shows customer + tracking before confirm.
  const cancelParcel = async () => {
    if (!cancelTarget) return;
    if (!cancelReason.trim()) { toast.error("Pick or write a reason for the cancel"); return; }
    if (!cancelPassword) {
      toast.error("Enter your login password to confirm the cancel");
      return;
    }
    setCancelling(true);
    try {
      const r = await axios.post(
        `${API}/order-register/${cancelTarget.id}/cancel`,
        { password: cancelPassword,
          reason: cancelReason.trim() }, { headers: getAuthHeader() });
      const n = r.data?.cancelled_rows ?? 0;
      const undoRow = cancelTarget;
      toast.success(`Parcel cancelled — ${n} row${n === 1 ? "" : "s"} moved to Cancelled`,
        { action: { label: "Undo", onClick: () => { setRestoreTarget(undoRow); setRestorePassword(""); } } });
      setCancelTarget(null);
      setCancelPassword("");
      setCancelReason("");
      fetchRows();
      fetchInvoiceStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    } finally { setCancelling(false); }
  };

  // Iter 264 — undo a cancel via the same /restore endpoint. Separate
  // dialog state so it never collides with the cancel one.
  const restoreParcel = async () => {
    if (!restoreTarget) return;
    if (!restorePassword) {
      toast.error("Enter your login password to confirm the restore");
      return;
    }
    setRestoring(true);
    try {
      const r = await axios.post(
        `${API}/order-register/${restoreTarget.id}/restore`,
        { password: restorePassword }, { headers: getAuthHeader() });
      const n = r.data?.restored_rows ?? 0;
      toast.success(`Parcel restored — ${n} row${n === 1 ? "" : "s"} returned to the register`);
      setRestoreTarget(null);
      setRestorePassword("");
      fetchRows();
      fetchInvoiceStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    } finally { setRestoring(false); }
  };

  // Iter 265 — bulk cancel: send every selected row id; server collapses
  // to parcel identity so a multi-row parcel is cancelled once.
  const bulkCancel = async () => {
    if (selected.size === 0) {
      toast.info("Select rows to cancel");
      return;
    }
    if (!bulkCancelReason.trim()) {
      toast.error("Pick or write a reason for the bulk cancel");
      return;
    }
    if (!bulkCancelPassword) {
      toast.error("Enter your login password to confirm the bulk cancel");
      return;
    }
    setBulkCancelling(true);
    try {
      const r = await axios.post(`${API}/order-register/bulk-cancel`, {
        ids: Array.from(selected),
        password: bulkCancelPassword,
        reason: bulkCancelReason.trim(),
      }, { headers: getAuthHeader() });
      const p = r.data?.parcels ?? 0;
      const rn = r.data?.rows ?? 0;
      toast.success(`Cancelled ${p} parcel${p === 1 ? "" : "s"} · ${rn} row${rn === 1 ? "" : "s"}`);
      setBulkCancelOpen(false);
      setBulkCancelPassword("");
      setBulkCancelReason("");
      setSelected(new Set());
      fetchRows();
      fetchInvoiceStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk cancel failed");
    } finally { setBulkCancelling(false); }
  };
  // Iter 182 — Reset page to 1 when a filter/search value changes AND
  // remember the fingerprint so we don't double-fetch: this effect only
  // fires when the filter shape genuinely changes, keeping search
  // in-lockstep with pagination across every page.
  // Clear every filter and show one invoice's rows (working-parcel jump + team invoice chips).
  const prevTeamView = useRef(null);
  const focusInvoice = (inv, search) => {
    if (prevTeamView.current === null) prevTeamView.current = teamView;
    setQ(search || ""); setModeFilter(""); setClientFilter(""); setReasonFilter(""); setReasonExact(false);
    setPendingOnly(false); setAge7Filter(false); setRawOnly(false); setDateFrom(""); setDateTo(""); setScopeUser(null);
    setNoTracking(false); setNoInvoice(false); setNoSecondName(false); setNoPrice(false); setOverdueFilter(false);
    setInvoiceStatusFilter(""); setOwnerFilter(""); setCancelledOnly(false);
    setTeamView("all"); setInvFilter(String(inv || "").toUpperCase()); setPage(1);
  };

  const filterFingerprint = `${debouncedQ}|${modeFilter}|${debouncedClient}|${debouncedReason}|${dateFrom}|${dateTo}|${invFilter}|${noTracking}|${noInvoice}|${noSecondName}|${noPrice}|${invoiceStatusFilter}|${teamView}|${ownerFilter}|${cancelledOnly}|${dupOnly}|${dbaCross}|${invWaiting}|${svcBucket}|${queryState}`;
  const lastFilterRef = useRef(filterFingerprint);
  useEffect(() => {
    if (lastFilterRef.current !== filterFingerprint) {
      lastFilterRef.current = filterFingerprint;
      if (page !== 1) setPage(1);  // triggers fetch via `fetchRows` dep
    }
  }, [filterFingerprint, page]);

  const openCreate = () => { setDraft(emptyDraft()); setEditorOpen(true); };
  const openEdit = (row) => { if (isViewer || row?.view_only) { toast.info(row?.view_only && !isViewer ? "View-only service — you can note and query this parcel, but only edit your allotted services" : "View-only access — editing is disabled"); return; } setDraft({ ...row }); setAddItems([]); setEditorOpen(true); };

  const saveDraft = async (guardPwd = "", editGuard = "") => {
    // onClick passes the click event here — only accept a real string.
    if (typeof guardPwd !== "string") guardPwd = "";
    if (typeof editGuard !== "string") editGuard = "";
    if (!draft.customer_name && !draft.tracking && !draft.medicine) {
      toast.error("Fill at least one of: Customer, Tracking, Medicine"); return;
    }
    // Iter 212 — tracking changes and NEW invoice assignments made via
    // the pencil Edit dialog must go through the login-password lock
    // dialog first (same rule as the inline cell edits from Iter 210).
    const orig = draft.id ? rows.find(r => r.id === draft.id) : null;
    const trackingChanged = orig
      && (draft.tracking || "") !== (orig.tracking || "");
    // Iter 280 — mode (service) edits are now password-guarded like tracking.
    const modeChanged = orig
      && !!(draft.mode || "") && !!(orig.mode || "")
      && (draft.mode || "").toUpperCase() !== (orig.mode || "").toUpperCase();
    // Iter 311 — assigning a NEW invoice # needs no password (audited).
    const effEditGuard = editGuard || draftEditGuard || guardPassword();
    const newItems = draft.id ? addItems.filter(i => (i.medicine || "").trim()) : [];
    if (newItems.some(i => !String(i.quantity || "").trim())) { toast.error("Every added product needs a quantity"); return; }
    if (!effEditGuard && (trackingChanged || modeChanged || newItems.length)) {
      setLockErr(""); setLockPwd("");
      setLockTarget({ id: draft.id, field: 'draft' });
      setLockOpen(true);
      return;
    }
    setSaving(true);
    try {
      if (draft.id) {
        const payload = { ...draft };
        const KEY = { customer_name: "Customer", address: "Address", medicine: "Product", quantity: "Qty", tracking: "Tracking", mode: "Service" };
        const norm = (v) => String(v ?? "").replace(/\s+/g, " ").trim().toUpperCase().replace(/\.0+$/, "");
        const diff = orig ? Object.keys(KEY).filter(k => norm(orig[k]) !== norm(draft[k])) : [];
        if (diff.length) {
          const why = await askChangeReason({ title: `Why change ${diff.map(k => KEY[k]).join(", ")}?`,
            detail: diff.map(k => `${KEY[k]}: ${orig[k] || "—"} → ${draft[k] || "—"}`).join("\n") });
          if (!why) { setSaving(false); return; }
          payload.change_reason = why;
        }
        if (guardPwd) payload.invoice_guard = guardPwd;
        if (effEditGuard) payload.edit_guard = effEditGuard;
        const resp = await axios.put(`${API}/order-register/${draft.id}`,
          payload,
          { headers: getAuthHeader() });
        toast.success(resp.data?.parcel_cascade
          ? `Order updated · applied to ${resp.data.parcel_cascade} more product row${resp.data.parcel_cascade > 1 ? "s" : ""} of this parcel`
          : "Order updated");
        if (resp.data?.parcel_warning) toast.warning(resp.data.parcel_warning);
        if (newItems.length) {
          const why = await askChangeReason({ title: `Why add ${newItems.length} product${newItems.length > 1 ? "s" : ""} to this parcel?`,
            detail: newItems.map(i => `+ ${i.medicine} × ${i.quantity}`).join("\n") });
          if (!why) { setSaving(false); return; }
          const ar = await axios.post(`${API}/order-register/${draft.id}/add-products`,
            { items: newItems, edit_guard: effEditGuard, change_reason: why, reason: why }, { headers: getAuthHeader() });
          toast.success(`${ar.data.added} product${ar.data.added > 1 ? "s" : ""} added to the parcel`);
          setAddItems([]);
        }
      } else {
        const resp = await axios.post(`${API}/order-register`, draft, { headers: getAuthHeader() });
        toast.success("Order created");
        if (resp.data?.parcel_warning) toast.warning(resp.data.parcel_warning);
      }
      setEditorOpen(false);
      setInvGuardOpen(false);
      setInvGuardPwd("");
      setDraftEditGuard("");
      fetchRows();
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      // Iter 199 — invoice-number change needs the supervisor password:
      // open the guard dialog instead of failing hard.
      if (e.response?.status === 403 && /invoice number/i.test(detail)) {
        setInvGuardOpen(true);
      } else if (e.response?.status === 403 && /login password/i.test(detail)) {
        // Iter 212 — tracking/invoice guard tripped server-side (e.g.
        // supervisor step consumed the flow): reopen the lock dialog.
        setLockErr(""); setLockPwd("");
        setLockTarget({ id: draft.id, field: 'draft' });
        setLockOpen(true);
      } else {
        toast.error(detail || "Save failed");
      }
    } finally { setSaving(false); }
  };

  const bulkDelete = async (idsArg) => {
    const ids = Array.isArray(idsArg) ? idsArg : Array.from(selected);
    if (ids.length === 0) { toast.info("Select rows to delete"); return; }
    const one = Array.isArray(idsArg) && ids.length === 1 ? rows.find(r => r.id === ids[0]) : null;
    const pwd = one ? window.prompt(
      `Delete this product line?\n\n${one.medicine || "—"} ×${one.quantity || "—"} · ${one.customer_name || "—"}\n\n` +
      `Enter the supervisor password to confirm. It can be restored from "Recover deleted" for 24 hours.`) : window.prompt(
      `Delete ${ids.length} selected order${ids.length === 1 ? '' : 's'}?\n\n` +
      `Admin-only. Enter the supervisor password to confirm.\n` +
      `Deleted rows can be restored from "Recover deleted" for 24 hours.`);
    if (pwd === null) return; // cancelled
    if (!pwd.trim()) { toast.error("Password required"); return; }
    const why = await askChangeReason({ title: `Why delete ${ids.length} row${ids.length === 1 ? "" : "s"}?`, confirmLabel: "Delete with reason",
      detail: rows.filter(r => ids.includes(r.id)).slice(0, 6).map(r => `${r.customer_name || "—"} · ${r.medicine || "—"} ×${r.quantity || "—"} · ${r.tracking || "no tracking"}`).join("\n") });
    if (!why) return;
    try {
      const r = await axios.post(`${API}/order-register/bulk-delete`,
        { ids, password: pwd.trim(), reason: why }, { headers: getAuthHeader() });
      toast.success(`Deleted ${r.data.deleted} order${r.data.deleted === 1 ? '' : 's'} · restorable for 24h`);
      setSelected(new Set());
      fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk delete failed"); }
  };

  // Iter 205 - scoped delete handlers (preview count -> password -> delete)
  const openScopeDelete = (scope) => {
    setDelMenuOpen(false);
    setDelScope(scope);
    setDelPreview(null);
    setDelPreviewParcels(null);
    setDelPassword("");
    setDelBusy(false);
    setDelDateFrom(""); setDelDateTo(""); setDelClient(""); setDelCountry("");
    setDelScopeOpen(true);
  };
  const buildScopeFilter = () => {
    if (delScope === 'filtered') {
      const f = {};
      if (debouncedQ) f.q = debouncedQ;
      if (modeFilter) f.mode = modeFilter;
      if (debouncedClient) f.client_code = debouncedClient;
      if (debouncedReason) f.reason = debouncedReason;
      if (debouncedReason && reasonExact) f.reason_exact = true;
      if (dateFrom) f.date_from = dateFrom;
      if (dateTo) f.date_to = dateTo;
      if (invFilter) f.invoice_no = invFilter;
      if (noTracking) f.no_tracking = true;
      if (noInvoice) f.no_invoice = true;
      if (noSecondName) f.no_second_name = true;
      return f;
    }
    if (delScope === 'date') {
      const f = {};
      if (delDateFrom) f.date_from = delDateFrom;
      if (delDateTo) f.date_to = delDateTo;
      return f;
    }
    if (delScope === 'client') return { client_code: delClient.trim().toUpperCase() };
    if (delScope === 'country') return { country_group: delCountry };
    return { all: true };
  };
  const scopeFilterValid = () => {
    if (delScope === 'filtered') return Object.keys(buildScopeFilter()).length > 0;
    if (delScope === 'date') return !!(delDateFrom || delDateTo);
    if (delScope === 'client') return !!delClient.trim();
    if (delScope === 'country') return !!delCountry;
    return true;
  };
  const previewScopeDelete = async () => {
    if (!scopeFilterValid()) { toast.error("Set the scope first"); return; }
    setDelBusy(true);
    try {
      const r = await axios.post(`${API}/order-register/bulk-delete`,
        { filter: buildScopeFilter(), preview: true }, { headers: getAuthHeader() });
      setDelPreview(r.data.matched);
      setDelPreviewParcels(r.data.parcels ?? null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setDelBusy(false); }
  };
  const confirmScopeDelete = async () => {
    if (!delPassword.trim()) { toast.error("Supervisor password required"); return; }
    if (delPreview === null) { toast.error("Preview the match count first"); return; }
    const why = await askChangeReason({ title: `Why delete ${delPreview} row${delPreview === 1 ? "" : "s"}?`, confirmLabel: "Delete with reason",
      detail: `Scope: ${delScope}` });
    if (!why) return;
    setDelBusy(true);
    try {
      const r = await axios.post(`${API}/order-register/bulk-delete`,
        { filter: buildScopeFilter(), password: delPassword.trim(), reason: why },
        { headers: getAuthHeader() });
      toast.success(`Deleted ${r.data.deleted} order${r.data.deleted === 1 ? '' : 's'} · restorable for 24h`);
      setDelScopeOpen(false);
      setSelected(new Set());
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scoped delete failed");
    } finally { setDelBusy(false); }
  };

  // Iter 182 — 24h delete restore
  const [recoverOpen, setRecoverOpen] = useState(false);
  const [deletedBatches, setDeletedBatches] = useState([]);
  const [recoverPassword, setRecoverPassword] = useState("");
  const [restoringBatch, setRestoringBatch] = useState("");
  const openRecover = async () => {
    setRecoverOpen(true); setRecoverPassword("");
    try {
      const r = await axios.get(`${API}/order-register/deleted-batches`,
        { headers: getAuthHeader() });
      setDeletedBatches(r.data.batches || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load deleted batches");
    }
  };
  const restoreBatch = async (bid) => {
    if (!recoverPassword.trim()) { toast.error("Enter the supervisor password"); return; }
    setRestoringBatch(bid);
    try {
      const r = await axios.post(`${API}/order-register/restore-deleted`,
        { delete_batch_id: bid, password: recoverPassword.trim() },
        { headers: getAuthHeader() });
      toast.success(`Restored ${r.data.restored} row${r.data.restored === 1 ? '' : 's'}${r.data.skipped ? ` · ${r.data.skipped} skipped` : ''}`);
      setDeletedBatches(prev => prev.filter(b => b.delete_batch_id !== bid));
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    } finally { setRestoringBatch(""); }
  };

  // ---------- Iter 183 — Multi-Parcel Invoice Management (handlers) ----------
  const openInvoiceManager = async (invoiceNo, fallbackClient = "") => {
    if (!invoiceNo) return;
    setInvMgrBusy(true);
    try {
      const r = await axios.get(`${API}/order-register/invoice/${encodeURIComponent(invoiceNo)}`,
        { headers: getAuthHeader() });
      const d = r.data;
      setInvMgr({ ...d, client_name: d.client_name || fallbackClient });
      setInvMgrStatus(d.status || "in_progress");
      // Iter 220 — pre-fill handover state; load the team list lazily
      // for admins (used for the "transfer ownership" dropdown).
      setHandoverTo("");
      if (isAdmin && teamMembers.length === 0) {
        axios.get(`${API}/users`, { headers: getAuthHeader() })
          .then(res => setTeamMembers(
            (res.data || []).filter(u => u.approved !== false)))
          .catch(() => setTeamMembers([]));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load invoice");
    } finally { setInvMgrBusy(false); }
  };

  // Iter 220 — Admin-only handover: transfer an active invoice to another
  // team member. Audited server-side.
  // Iter 286 — "No one" option releases ownership entirely (free for anyone;
  // the next puncher auto-claims it server-side).
  const runHandover = async () => {
    if (!invMgr || !handoverTo) return;
    const isRelease = handoverTo === "__release__";
    setHandoverBusy(true);
    try {
      const r = await axios.put(
        `${API}/order-register/invoice/${encodeURIComponent(invMgr.invoice_no)}/${isRelease ? "release" : "handover"}`,
        isRelease ? {} : { new_owner_id: handoverTo }, { headers: getAuthHeader() });
      const d = r.data || {};
      if (isRelease) {
        toast.success(`Invoice ${invMgr.invoice_no} released — free for anyone to pick up`);
        setInvMgr(prev => prev ? { ...prev, owner_id: "",
          owner_email: "", owner_name: "" } : prev);
        setInvOwners(prev => {
          const next = { ...prev };
          delete next[invMgr.invoice_no.toUpperCase()];
          return next;
        });
      } else {
        toast.success(`Invoice ${invMgr.invoice_no} handed over to ${d.owner_name}`);
        setInvMgr(prev => prev ? { ...prev, owner_id: d.owner_id,
          owner_email: d.owner_email, owner_name: d.owner_name } : prev);
        setInvOwners(prev => ({
          ...prev,
          [invMgr.invoice_no.toUpperCase()]: {
            owner_id: d.owner_id, owner_email: d.owner_email,
            owner_name: d.owner_name, status: invMgrStatus,
          },
        }));
      }
      setHandoverTo("");
    } catch (e) {
      toast.error(e.response?.data?.detail ||
        (isRelease ? "Release failed" : "Handover failed"));
    } finally { setHandoverBusy(false); }
  };

  // Iter 184 — only the manual status is saved; parcels are auto-derived
  // from the register rows sharing the invoice number.
  const saveInvoiceManager = async () => {
    if (!invMgr) return;
    if (invMgrStatus === "completed") {
      try {
        const oq = await axios.get(`${API}/order-register/queries/open-for-invoice/${encodeURIComponent(invMgr.invoice_no)}`, { headers: getAuthHeader() });
        if (oq.data.count && !window.confirm(`${oq.data.count} parcel(s) in this invoice still have an OPEN customer query:\n`
          + oq.data.items.map(i => `• ${i.customer_name} — ${i.type}`).join("\n") + "\n\nMark the invoice Completed anyway?")) return;
      } catch { /* query check is advisory */ }
    }
    setInvMgrBusy(true);
    try {
      await axios.put(`${API}/order-register/invoice/${encodeURIComponent(invMgr.invoice_no)}`,
        { status: invMgrStatus, client_name: invMgr.client_name },
        { headers: getAuthHeader() });
      toast.success(invMgrStatus === "completed" ? "Invoice marked Completed" : "Invoice saved");
      if (invMgrStatus === "completed") loadSlipPending(invMgr.invoice_no);
      // Iter 195 — notify listeners (CompletedInvoicesBar on each doc tool)
      // so the "Use Invoice" chip strip refreshes without a page reload.
      window.dispatchEvent(new CustomEvent("order-register:invoice-changed",
        { detail: { invoice_no: invMgr.invoice_no, status: invMgrStatus } }));
      setInvMgr(null);
      fetchRows();
      fetchInvoiceStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setInvMgrBusy(false); }
  };

  // Iter 199 — supervisor-guard dialog for invoice-number changes and
  // the Invoice Manager "Change Invoice #" (admin) inputs.
  const [invGuardOpen, setInvGuardOpen] = useState(false);
  const [invGuardPwd, setInvGuardPwd] = useState("");
  // Iter 210 — locked-edit gate (tracking + new invoice #) using the
  // team member's own login password. Once verified, `unlockedEdit`
  // stores {id, field, password} for a single edit; cleared on save/cancel.
  const [lockOpen, setLockOpen] = useState(false);
  const [lockTarget, setLockTarget] = useState(null);  // {id, field}
  const [lockPwd, setLockPwd] = useState("");
  const [lockErr, setLockErr] = useState("");
  const [unlockedEdit, setUnlockedEdit] = useState(null);
  // Iter 212 — Edit-dialog guard: password verified once per draft save.
  const [draftEditGuard, setDraftEditGuard] = useState("");
  // Iter 219 — cache a successful unlock for 10 minutes (was 5) so batch
  // tracking/invoice edits don't prompt on every row. Scoped to the
  // logged-in user: another person's login never inherits the unlock.
  const [guardCache, setGuardCache] = useState(null);  // {password, until, user}
  const guardPassword = () => (
    guardCache && guardCache.user === (user?.email || "")
    && Date.now() < guardCache.until ? guardCache.password : null);
  const [invRename, setInvRename] = useState("");
  const [invRenamePwd, setInvRenamePwd] = useState("");
  const renameInvoice = async () => {
    const newInv = invRename.trim().toUpperCase();
    if (!newInv || !invMgr) return;
    setInvMgrBusy(true);
    try {
      await axios.put(`${API}/order-register/invoice/${encodeURIComponent(invMgr.invoice_no)}`,
        { status: invMgrStatus, client_name: invMgr.client_name,
          new_invoice_no: newInv, invoice_guard: invRenamePwd },
        { headers: getAuthHeader() });
      toast.success(`Invoice changed to ${newInv}`);
      setInvRename(""); setInvRenamePwd("");
      setInvMgr(null);
      fetchRows();
      fetchInvoiceStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rename failed");
    } finally { setInvMgrBusy(false); }
  };

  // Iter 185 — invoice dates display like the Date column: dd/mm/yyyy.
  // Storage accepts both; the picker works in ISO, we convert at the edges.
  const fmtDmy = (v) => {
    const s = String(v ?? "").trim();
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
  };
  // Iter 198 — flow-stage hover tooltip: "Packing List · generated
  // 21/09/2026 18:45" (earliest generation, like the Invoice Flow report)
  const STAGE_LABELS = { packing_list: "Packing List", bulk_data: "Bulk Data",
                          pack_team: "Pack Team", bulk_label: "Bulk Label" };
  const fmtStageTooltip = (stage, inv) => {
    const done = (inv.stages || {})[stage];
    const at = (inv.stage_at || {})[stage];
    if (!done) return `${STAGE_LABELS[stage]} — not generated yet`;
    if (!at) return `${STAGE_LABELS[stage]} — generated`;
    const d = new Date(at);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${STAGE_LABELS[stage]} — generated ${dd}/${mm}/${d.getFullYear()} ${hh}:${mi}`;
  };
  const dmyToIso = (v) => {
    const s = String(v ?? "").trim();
    const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : s;
  };
  const todayDmy = () => {
    const d = new Date();
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  };

  // Iter 186 — Complete Shipping Address viewer (dbl-click Country cell)
  const [addrView, setAddrView] = useState(null); // {customer, address, phone}

  // Iter 184 — Inv Date: double-click an EMPTY cell stamps today's date
  // instantly (system date); double-click a FILLED cell opens the picker.
  const quickSetInvoiceDateToday = async (row) => {
    if (isViewer || row?.view_only) { toast.info(row?.view_only && !isViewer ? "View-only service — you can note and query this parcel, but only edit your allotted services" : "View-only access — editing is disabled"); return; }
    const today = todayDmy();
    const key = parcelKey(row);
    const targets = rows.filter(r => parcelKey(r) === key);
    const targetIds = new Set(targets.map(t => t.id));
    setRows(prev => prev.map(r => targetIds.has(r.id) ? { ...r, invoice_date: today } : r));
    try {
      await Promise.all(targets.map(t =>
        axios.put(`${API}/order-register/${t.id}`, { ...t, invoice_date: today },
          { headers: getAuthHeader() })));
      toast.success(`Invoice date set to ${today}${targets.length > 1 ? ` (${targets.length} rows in parcel)` : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      fetchRows();
    }
  };

  // Iter 254 — preview-first upload with verification.
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadPendingFile, setUploadPendingFile] = useState(null);
  const [lastUploadResult, setLastUploadResult] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [owhOpen, setOwhOpen] = useState(false);
  // Iter 254 — Upload Analytics (Test 8)
  const [uaOpen, setUaOpen] = useState(false);
  const [uaLoading, setUaLoading] = useState(false);
  const [uaData, setUaData] = useState({ uploads: [], can_see_all: false, uploaders: [] });
  const [uaFrom, setUaFrom] = useState("");
  const [uaTo, setUaTo] = useState("");
  const [uaUploader, setUaUploader] = useState("");
  const [uaWithSnapshot, setUaWithSnapshot] = useState(false);
  const downloadUploadCompare = async (u) => {
    try {
      const r = await axios.get(`${API}/order-register/uploads/${u.id}/compare`,
        { headers: getAuthHeader(), responseType: "blob" });
      const m = (r.headers["content-disposition"] || "").match(/filename="?([^";]+)"?/);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = m ? m[1] : `compare_${u.filename || "upload"}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Compare sheet not available for this upload");
    }
  };
  const downloadScorecard = async () => {
    try {
      const r = await axios.get(`${API}/order-register/scorecard/latest`,
        { headers: getAuthHeader(), responseType: "blob" });
      const m = (r.headers["content-disposition"] || "").match(/filename="?([^";]+)"?/);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = m ? m[1] : "client_scorecard.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No scorecard generated yet");
    }
  };

  const loadUploadAnalytics = async () => {
    setUaLoading(true);
    try {
      const params = {};
      if (uaFrom) params.date_from = uaFrom;
      if (uaTo) params.date_to = uaTo;
      if (uaUploader) params.uploader_id = uaUploader;
      const r = await axios.get(`${API}/order-register/uploads`,
        { headers: getAuthHeader(), params });
      setUaData(r.data);
    } catch { toast.error("Failed to load analytics"); }
    finally { setUaLoading(false); }
  };
  const openUploadAnalytics = () => { setUaOpen(true); loadUploadAnalytics(); };
  // Iter 255 — one-click Excel export of the uploads analytics table;
  // honours the same From/To/Uploader filters currently applied.
  const exportUploads = async () => {
    try {
      const params = {};
      if (uaFrom) params.date_from = uaFrom;
      if (uaTo) params.date_to = uaTo;
      if (uaUploader) params.uploader_id = uaUploader;
      if (uaWithSnapshot) params.include_snapshot = "true";
      const r = await axios.get(`${API}/order-register/uploads/export`,
        { headers: getAuthHeader(), params, responseType: "blob" });
      const dispo = r.headers["content-disposition"] || "";
      const m = dispo.match(/filename="?([^";]+)"?/);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = m ? m[1] : "OrderRegister_Uploads.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  const previewExcel = async (file) => {
    if (!file) return;
    setUploading(true); setUploadPreview(null); setLastUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("preview", "true");
      const r = await axios.post(`${API}/order-register/upload`, fd,
        { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" }, timeout: 60000 });
      setUploadPreview(r.data); setUploadPendingFile(file);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setUploading(false); }
  };
  const commitUpload = async () => {
    if (!uploadPendingFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadPendingFile);
      const r = await axios.post(`${API}/order-register/upload`, fd,
        { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" }, timeout: 60000 });
      setLastUploadResult(r.data);
      if (r.data.verified) {
        toast.success(`✓ Verified · ${r.data.rows_inserted ?? r.data.inserted} rows inserted`);
      } else {
        toast.error(`⚠ Mismatch · detected ${r.data.rows_detected}, inserted ${r.data.inserted}`);
      }
      setUploadPreview(null); setUploadPendingFile(null);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const cancelPreview = () => {
    setUploadPreview(null); setUploadPendingFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };
  const uploadExcel = (file) => previewExcel(file);

  // Iter 208 - shared download helper (scoped export + legacy button).
  const SCOPE_TITLES = { filtered: 'All filtered results (current view)',
    date: 'Date-wise', client: 'Client-wise', country: 'Country-wise',
    all_clients: 'All client codes (client-wise, one sheet)',
    single_track: 'Single Track Data', all: 'All data (every date)' };
  // Screen view — HTML table of the same export, opened in a new tab.
  const openScreenView = async (params, label) => {
    const win = window.open("", "_blank");
    if (win) win.document.write("<p style='font-family:sans-serif;padding:16px'>Loading…</p>");
    try {
      const res = await axios.get(`${API}/order-register/export`,
        { headers: getAuthHeader(), params: { ...params, fmt: "html", title: `Order Register — ${label}` },
          responseType: "text" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
      if (win) win.location.href = url; else window.open(url, "_blank");
    } catch { if (win) win.close(); toast.error("Screen view failed"); }
  };
  const downloadExport = async (params, label) => {
    try {
      const res = await axios.get(`${API}/order-register/export`,
        { headers: getAuthHeader(), params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `OrderRegister_${params.layout === 'single_track' ? 'SingleTrack_' : ''}${label}_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported (${label})`);
    } catch { toast.error("Export failed"); }
  };
  const buildFilteredParams = () => {
    const params = {};
    if (debouncedQ) params.q = debouncedQ;
    if (modeFilter) params.mode = modeFilter;
    if (debouncedClient) params.client_code = debouncedClient;
    if (debouncedReason) params.reason = debouncedReason;
    if (debouncedReason && reasonExact) params.reason_exact = true;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (invFilter) params.invoice_no = invFilter;
    if (noTracking) params.no_tracking = true;
    if (noInvoice) params.no_invoice = true;
    if (noSecondName) params.no_second_name = true;
    if (noPrice) params.no_price = true;
    // Iter 208 fix - export exactly what the team view shows.
    if (teamView === "pending" || teamView === "completed") {
      params.invoice_status = teamView;
    }
    if (invoiceStatusFilter === "in_progress" || invoiceStatusFilter === "completed") {
      params.invoice_status = invoiceStatusFilter;
    }
    return params;
  };
  const exportExcel = () => downloadExport(buildFilteredParams(), "filtered");

  // Iter 208 - scoped export handlers
  const openScopeExport = (scope) => {
    setExpMenuOpen(false);
    setExpScope(scope);
    setExpPreview(null);
    setExpBusy(false);
    setExpDateFrom(""); setExpDateTo(""); setExpClient(""); setExpCountry("");
    setExpSingle(scope === 'single_track');
    // Iter 271 — default the export to the SAME team view as the table so
    // export and screen always agree (user can switch to "All parcels").
    setExpView(teamView === "pending" || teamView === "completed" ? teamView : "");
    setExpScopeOpen(true);
  };
  const buildExportParams = () => {
    const p = expScope === 'filtered' ? buildFilteredParams() : {};
    if (expScope !== 'filtered') {
      if (expDateFrom) p.date_from = expDateFrom;
      if (expDateTo) p.date_to = expDateTo;
    }
    if ((expScope === 'client' || expScope === 'single_track') && expClient.trim())
      p.client_code = expClient.trim().toUpperCase();
    if (expScope === 'country') p.country_group = expCountry;
    if (expScope === 'all_clients') p.sort = 'code';
    if (expSingle) p.layout = 'single_track';
    if (expScope !== 'filtered' && expView) p.invoice_status = expView;
    return p;
  };
  const expScopeValid = () => {
    if (expScope === 'date') return !!(expDateFrom || expDateTo);
    if (expScope === 'client') return !!expClient.trim();
    if (expScope === 'country') return !!expCountry;
    return true;
  };
  const previewScopeExport = async () => {
    if (!expScopeValid()) { toast.error("Set the scope first"); return; }
    setExpBusy(true);
    try {
      const r = await axios.get(`${API}/order-register/export`,
        { headers: getAuthHeader(),
          params: { ...buildExportParams(), preview: true } });
      setExpPreview(r.data.matched);
    } catch { toast.error("Preview failed"); }
    finally { setExpBusy(false); }
  };
  const confirmScopeExport = async () => {
    setExpBusy(true);
    try {
      await downloadExport(buildExportParams(),
        expScope === 'client' && expClient ? expClient.trim().toUpperCase() : expScope);
      setExpScopeOpen(false);
    } finally { setExpBusy(false); }
  };

  // Iter 176 — Fetch reason autocomplete list on mount.
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/order-register/reasons`,
          { headers: getAuthHeader() });
        if (Array.isArray(r.data?.reasons) && r.data.reasons.length) {
          setReasonSuggestions(r.data.reasons);
        }
        if (Array.isArray(r.data?.country_groups) && r.data.country_groups.length) {
          setCountryGroups(r.data.country_groups);
        }
        if (Array.isArray(r.data?.modes) && r.data.modes.length) {
          setModeOptions(["", ...r.data.modes]);
        }
      } catch { /* keep client-side defaults */ }
    })();
  }, []);

  // Iter 176 — Generate Pending Parcel List (Full / Service-wise /
  // Client-wise) in Excel or PDF.
  const runPendingReport = async () => {
    setPendingRunning(true);
    try {
      // Iter 188 — Client-code-wise + Excel → per-client ZIP endpoint
      // (one workbook per client, no shared workbook / no multi-sheet
      // printing headaches).
      const useZip = pendingGrouping === "client" && pendingFmt === "excel";
      const r = await axios.get(
        useZip
          ? `${API}/order-register/pending-report-by-client`
          : `${API}/order-register/pending-report`,
        { headers: getAuthHeader(),
          params: useZip ? {} : { grouping: pendingGrouping, fmt: pendingFmt },
          responseType: "blob" });
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const fname = m ? m[1]
        : (useZip
            ? `PENDING-BY-CLIENT-${new Date().toISOString().slice(0,10)}.zip`
            : `PENDING-${pendingGrouping}-${new Date().toISOString().slice(0,10)}.${pendingFmt === 'pdf' ? 'pdf' : 'xlsx'}`);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      window.URL.revokeObjectURL(url);
      setPendingOpen(false);
      toast.success(useZip ? "Per-client ZIP generated" : "Pending report generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pending report failed");
    } finally {
      setPendingRunning(false);
    }
  };

  // Iter 176 — Emirates Tracking Fill. Upload an Excel with columns
  // Client Code / Customer Name / Tracking No; matched rows get their
  // (blank) tracking cells filled.
  const runEmiratesFill = async () => {
    if (!emiratesFile) { toast.error("Choose an Excel first"); return; }
    setEmiratesRunning(true);
    setEmiratesResult(null);
    try {
      const fd = new FormData();
      fd.append("file", emiratesFile);
      const r = await axios.post(
        `${API}/order-register/emirates-tracking-fill`, fd,
        { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" } });
      setEmiratesResult(r.data);
      const u = r.data?.updated || 0;
      const um = (r.data?.unmatched || []).length;
      const rm = r.data?.rows_modified || 0;
      toast.success(`Filled ${rm} row${rm === 1 ? '' : 's'} across ${u} customer${u === 1 ? '' : 's'}; ${um} unmatched`);
      fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Emirates fill failed");
    } finally {
      setEmiratesRunning(false);
    }
  };

  // Iter 153 — Bulk fill Second Name from Product Aliases for rows on
  // the current page where Second Name is empty and the medicine
  // matches an alias mapping.
  const fillFromAliases = async () => {
    const candidates = rows.filter(r => (r.medicine || '').trim()
      && !(r.second_name || '').trim());
    if (candidates.length === 0) {
      toast.info("Nothing to fill — every visible row already has a Second Name");
      return;
    }
    setFillingAliases(true);
    try {
      const meds = Array.from(new Set(candidates.map(r => r.medicine.trim())));
      const r = await axios.post(`${API}/product-aliases/bulk-lookup`,
        { medicines: meds }, { headers: getAuthHeader() });
      const results = r.data?.results || {};
      let filled = 0, skipped = 0;
      const updates = [];
      for (const row of candidates) {
        const m = (row.medicine || '').trim();
        const hit = results[m];
        if (hit && hit.second_name) {
          updates.push(axios.put(`${API}/order-register/${row.id}`,
            { ...row, second_name: hit.second_name },
            { headers: getAuthHeader() }));
          filled++;
        } else {
          skipped++;
        }
      }
      if (filled > 0) {
        // Optimistic UI update
        setRows(prev => prev.map(row => {
          const hit = results[(row.medicine || '').trim()];
          if (hit && hit.second_name && !(row.second_name || '').trim()) {
            return { ...row, second_name: hit.second_name };
          }
          return row;
        }));
        await Promise.allSettled(updates);
        toast.success(`Filled ${filled} row${filled === 1 ? '' : 's'}` +
          (skipped ? ` • ${skipped} had no alias match` : ''));
      } else {
        toast.info(`No alias matches found for ${skipped} medicine${skipped === 1 ? '' : 's'}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fill from Aliases failed");
    } finally {
      setFillingAliases(false);
    }
  };

  // Iter 153 — save a (medicine → second_name) pair as a new alias mapping.
  const saveAsAlias = async (medicine, secondName) => {
    const med = (medicine || '').trim();
    const sn = (secondName || '').trim();
    if (!med || !sn) return;
    try {
      // Fetch any existing mapping so we merge instead of overwriting aliases.
      const list = await axios.get(`${API}/product-aliases`,
        { headers: getAuthHeader(), params: { q: sn } });
      // Match by strong normalisation (strip case / spaces / punctuation)
      // so variants like "Paracetamol 500 mg" and "PARACETAMOL500MG" land
      // on the same alias row instead of creating a near-duplicate.
      const _norm = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
      const snKey = _norm(sn);
      const existing = (list.data?.rows || []).find(r => _norm(r.second_name) === snKey);
      const aliases = existing ? Array.from(new Set([...(existing.aliases || []), med])) : [med];
      await axios.post(`${API}/product-aliases`,
        { second_name: sn, aliases }, { headers: getAuthHeader() });
      toast.success(`Alias saved: "${med}" → ${sn}`);
      // Re-run bulk lookup so chip flips to green.
      const meds = Array.from(new Set(rows.map(r => (r.medicine || '').trim()).filter(Boolean)));
      if (meds.length) {
        const r = await axios.post(`${API}/product-aliases/bulk-lookup`,
          { medicines: meds }, { headers: getAuthHeader() });
        setAliasMatches(r.data?.results || {});
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save alias");
    }
  };

  // Iter 155 — build tray candidates: unique medicines on the current
  // page that have NO alias match yet. Each entry includes the count
  // of rows using that medicine so ops can prioritise heavy ones.
  // Iter 155/281 — build tray candidates from the current page.
  // Two kinds:
  //   ready  — row already has a Second Name but the medicine→name
  //            mapping isn't saved yet (the per-row "Save alias" chip
  //            case). Pre-filled; Save All stores them in one click.
  //   manual — medicine has no alias match AND no Second Name typed on
  //            the page; needs typing (auto-suggestion pre-fills when a
  //            close alias exists).
  const buildUnmatched = () => {
    const counts = {};
    const names = {};
    for (const r of rows) {
      const m = (r.medicine || '').trim();
      if (!m) continue;
      counts[m] = (counts[m] || 0) + 1;
      const sn = (r.second_name || '').trim();
      if (sn && !names[m]) names[m] = sn;   // first non-empty wins
    }
    const out = [];
    for (const m of Object.keys(counts)) {
      const hit = aliasMatches[m];
      const sn = names[m] || '';
      if (sn) {
        const mapped = hit && hit.second_name
          && hit.second_name.trim().toLowerCase() === sn.trim().toLowerCase();
        if (!mapped) out.push({ medicine: m, count: counts[m], kind: 'ready', sn });
      } else if (!hit || !hit.second_name) {
        out.push({ medicine: m, count: counts[m], kind: 'manual', sn: '' });
      }
    }
    // Ready-to-save first (one-click value), then heaviest medicines.
    out.sort((a, b) => (a.kind === b.kind
      ? (b.count - a.count || a.medicine.localeCompare(b.medicine))
      : a.kind === 'ready' ? -1 : 1));
    return out;
  };
  const unmatchedList = buildUnmatched();

  // Iter 281 — when the tray opens: pre-fill "ready" rows with the row's
  // own Second Name, and auto-suggest names for "manual" rows from
  // Product Aliases. Only fills rows the user hasn't already typed into.
  useEffect(() => {
    if (!trayOpen) return;
    const items = buildUnmatched();
    const readyFills = {};
    for (const it of items) {
      if (it.kind === 'ready' && !(trayDrafts[it.medicine] || '').trim())
        readyFills[it.medicine] = it.sn.toUpperCase();
    }
    if (Object.keys(readyFills).length) {
      setTrayDrafts(d => ({ ...readyFills, ...d }));
      setTraySuggested(s => {
        const next = { ...s };
        for (const m of Object.keys(readyFills)) next[m] = 'ready';
        return next;
      });
    }
    const meds = items
      .filter(it => it.kind === 'manual')
      .map(it => it.medicine)
      .filter(m => !(trayDrafts[m] || '').trim())
      .slice(0, 50);
    if (!meds.length) { setTraySuggesting(false); return; }
    let cancelled = false;
    setTraySuggesting(true);
    (async () => {
      const fills = {};
      await Promise.all(meds.map(async (m) => {
        try {
          const r = await axios.get(`${API}/product-aliases/suggest`,
            { headers: getAuthHeader(), params: { q: m, limit: 1 } });
          const best = (r.data?.rows || [])[0];
          if (best && String(best).trim()
              && String(best).trim().toUpperCase() !== m.trim().toUpperCase()) {
            fills[m] = String(best).trim().toUpperCase();
          }
        } catch { /* leave blank — user can still type */ }
      }));
      if (cancelled) return;
      if (Object.keys(fills).length) {
        setTrayDrafts(d => {
          const next = { ...d };
          for (const [m, v] of Object.entries(fills))
            if (!(next[m] || '').trim()) next[m] = v;
          return next;
        });
        setTraySuggested(s => {
          const next = { ...s };
          for (const m of Object.keys(fills)) next[m] = 'suggested';
          return next;
        });
      }
      setTraySuggesting(false);
    })();
    return () => { cancelled = true; };
  }, [trayOpen]);

  const setTrayDraft = (medicine, val) => {
    setTrayDrafts(d => ({ ...d, [medicine]: val }));
    // A manual edit is no longer a machine suggestion.
    setTraySuggested(s => {
      if (!s[medicine]) return s;
      const next = { ...s }; delete next[medicine]; return next;
    });
  };

  const saveTrayOne = async (medicine) => {
    const sn = (trayDrafts[medicine] || '').trim();
    if (!sn) { toast.error("Enter a canonical Second Name first"); return; }
    await saveAsAlias(medicine, sn);
    setTrayDrafts(d => { const { [medicine]: _drop, ...rest } = d; return rest; });
    setTraySuggested(s => { const { [medicine]: _drop, ...rest } = s; return rest; });
  };

  // Iter 281 — count of tray rows that have a value (typed or suggested);
  // drives the "Save All (n)" label + disabled state.
  const trayFilledCount = unmatchedList
    .filter(u => (trayDrafts[u.medicine] || '').trim()).length;

  const saveTrayAll = async () => {
    const pairs = unmatchedList
      .map(({ medicine }) => ({ medicine, sn: (trayDrafts[medicine] || '').trim() }))
      .filter(p => p.sn);
    if (pairs.length === 0) {
      toast.info("Type a Second Name against at least one medicine first");
      return;
    }
    setTraySaving(true);
    try {
      // Serial so alias merging (fetch-then-post) is safe.
      let ok = 0;
      for (const { medicine, sn } of pairs) {
        try { await saveAsAlias(medicine, sn); ok++; } catch { /* toast already fired */ }
      }
      // Clear only the ones that saved.
      setTrayDrafts(d => {
        const next = { ...d };
        for (const { medicine } of pairs) delete next[medicine];
        return next;
      });
      setTraySuggested(s => {
        const next = { ...s };
        for (const { medicine } of pairs) delete next[medicine];
        return next;
      });
      toast.success(`Saved ${ok} alias mapping${ok === 1 ? '' : 's'}`);
      return { ok, pairs };
    } finally { setTraySaving(false); }
  };

  // Iter 282 — "Save All & Fill": run Save All, then back-fill every
  // visible row whose Second Name is blank, in a single click.
  const saveTrayAllAndFill = async () => {
    const result = await saveTrayAll();
    if (!result || result.ok === 0) return;
    // Build an immediate medicine→sn map from the pairs that just saved,
    // so Fill works even if the aliasMatches cache hasn't refreshed yet.
    const quickMap = {};
    for (const { medicine, sn } of result.pairs) {
      quickMap[medicine.trim().toUpperCase()] = sn;
    }
    const targets = rows.filter(r => (r.medicine || '').trim()
      && !(r.second_name || '').trim()
      && quickMap[r.medicine.trim().toUpperCase()]);
    if (targets.length === 0) {
      // Fall back to the full Fill (covers aliases we didn't just save).
      await fillFromAliases();
      return;
    }
    // Optimistic UI
    setRows(prev => prev.map(r => {
      if (!(r.medicine || '').trim() || (r.second_name || '').trim()) return r;
      const sn = quickMap[r.medicine.trim().toUpperCase()];
      return sn ? { ...r, second_name: sn } : r;
    }));
    try {
      await Promise.allSettled(targets.map(t => {
        const sn = quickMap[t.medicine.trim().toUpperCase()];
        return axios.put(`${API}/order-register/${t.id}`,
          { ...t, second_name: sn }, { headers: getAuthHeader() });
      }));
      toast.success(`Filled ${targets.length} empty row${targets.length === 1 ? '' : 's'} with the new aliases`);
    } catch (e) {
      toast.error("Alias saved, but back-fill failed");
    }
  };

  // Iter 157 — Missing-Invoice Bulk-Fill: stamps the given invoice #
  // and date across every row that matches the current filter AND has
  // an empty invoice_no. Mirrors the /list filter shape so the server
  // sees the same rows the user is looking at.
  const runBulkFillInvoice = async () => {
    if (!bulkInvNo.trim()) { toast.error("Invoice # is required"); return; }
    setBulkInvSaving(true);
    try {
      const filter = {};
      if (debouncedQ)      filter.q = debouncedQ;
      if (modeFilter)      filter.mode = modeFilter;
      if (debouncedClient) filter.client_code = debouncedClient;
      if (debouncedReason) filter.reason = debouncedReason;
      if (dateFrom)        filter.date_from = dateFrom;
      if (dateTo)          filter.date_to = dateTo;
      if (noTracking)      filter.no_tracking = true;
      if (noSecondName)    filter.no_second_name = true;
      // Note: we DON'T need to send no_invoice — the endpoint always
      // restricts to blank invoice_no so the operation is idempotent.
      const r = await axios.post(`${API}/order-register/bulk-fill-invoice`,
        { invoice_no: bulkInvNo, invoice_date: bulkInvDate, filter },
        { headers: getAuthHeader() });
      const { matched = 0, updated = 0 } = r.data || {};
      if (updated === 0) {
        toast.info("No rows without an invoice in the current filter");
      } else {
        toast.success(`Stamped ${updated} row${updated === 1 ? '' : 's'} • Invoice ${bulkInvNo}`);
      }
      setBulkInvOpen(false);
      setBulkInvNo("");
      setBulkInvDate("");
      await fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk fill failed");
    } finally {
      setBulkInvSaving(false);
    }
  };

  // Print-friendly view of the currently-loaded rows (client-side, no server).
  const screenPrint = async () => {
    if (rows.length === 0) { toast.info("No rows to print"); return; }
    // open synchronously (pop-up blockers), then fetch the export trace
    const w = window.open('', '_blank', 'width=1200,height=800');
    if (!w) { toast.error("Pop-up blocked — please allow pop-ups"); return; }
    let trace = { trace_id: '', stamp: new Date().toLocaleString() };
    try {
      const tr = await axios.post(`${API}/order-register/screen-print-trace`,
        { rows: rows.length, filters: { q, mode: modeFilter, client_code: clientFilter, date_from: dateFrom, date_to: dateTo } }, { headers: getAuthHeader() });
      trace = tr.data;
    } catch { /* print still works without a trace */ }
    const esc = (s) => String(s ?? '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const stamp = trace.stamp;
    const wmSvg = btoa(`<svg xmlns='http://www.w3.org/2000/svg' width='460' height='220'><text x='20' y='130' transform='rotate(-24 230 110)' font-family='Arial' font-size='15' fill='rgba(100,116,139,0.16)'>${esc(stamp)}</text></svg>`);
    const wm = trace.trace_id
      ? `<div data-testid="export-watermark" style="position:fixed;inset:0;pointer-events:none;z-index:50;background-image:url('data:image/svg+xml;base64,${wmSvg}');-webkit-print-color-adjust:exact;print-color-adjust:exact"></div>`
      : '';
    const trs = rows.map(r => {
      const usd = (typeof r.usd_price === "number") ? r.usd_price
                  : (r.second_name ? usdOf(r.second_name) : null);
      const usdCell = usd !== null
        ? `${r.usd_price_frozen ? '🔒 ' : ''}$${usd.toFixed(2)}`
        : '';
      return `
      <tr>
        <td>${esc(r.date)}</td><td>${esc(r.code)}</td><td>${esc(r.customer_name)}</td>
        <td>${esc(r.medicine)}</td><td style="text-align:right">${esc(r.quantity)}</td>
        <td style="text-align:right${r.qty1 ? '' : ';background:#fbcfe8'}">${esc(r.qty1)}</td>
        <td>${esc(r.tracking)}</td><td>${esc(r.mode)}</td>
        <td>${esc(r.second_name)}</td>
        <td style="text-align:right">${esc(usdCell)}</td>
        <td>${esc(r.reason)}</td>
        <td>${esc(r.invoice_no)}</td><td>${esc(fmtDmy(r.invoice_date))}</td>
        <td>${esc(r.notes)}</td>
      </tr>`;
    }).join('');
    const html = `<!doctype html><html><head><title>Order Register — ${stamp}</title>
      <style>
        body{font:12px/1.35 Arial,Helvetica,sans-serif;padding:14px}
        h1{font-size:14px;margin:0 0 6px}
        .meta{color:#666;font-size:10px;margin-bottom:10px}
        table{width:100%;border-collapse:collapse;font-size:10px}
        th,td{border:1px solid #999;padding:4px 6px;text-align:left;vertical-align:top}
        th{background:#eee}
        @media print { body{padding:8px} }
      </style></head><body>
      <h1>Order Register</h1>
      <div class="meta">${rows.length} rows • ${esc(stamp)}</div>
      <table><thead><tr>
        <th>Date</th><th>Code</th><th>Customer</th><th>Medicine</th><th>Qty</th>
        <th>Qty1</th><th>Tracking</th><th>Mode</th><th>Second Name</th><th>$ Price</th><th>Reason</th>
        <th>Invoice #</th><th>Inv Date</th><th>Notes</th>
      </tr></thead><tbody>${trs}</tbody></table>
      ${wm}
      <script>window.onload=()=>{setTimeout(()=>window.print(),200)};</script>
      </body></html>`;
    w.document.write(html);
    w.document.close();
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };
  const toggleSelectAllOnPage = () => {
    if (rows.every(r => selected.has(r.id))) {
      setSelected(prev => { const s = new Set(prev); rows.forEach(r => s.delete(r.id)); return s; });
    } else {
      setSelected(prev => { const s = new Set(prev); rows.forEach(r => s.add(r.id)); return s; });
    }
  };

  // Inline edit — double-click a Tracking / Second Name cell.
  const [inlineEdit, setInlineEdit] = useState(null);      // { id, field }
  const [inlineValue, setInlineValue] = useState("");
  const beginInline = (row, field) => {
    if (isViewer || row?.view_only) { toast.info(row?.view_only && !isViewer ? "View-only service — you can note and query this parcel, but only edit your allotted services" : "View-only access — editing is disabled"); return; }
    setInlineEdit({ id: row.id, field });
    // Iter 185 — invoice_date picker needs ISO; stored/displayed as dd/mm/yyyy
    setInlineValue(field === 'invoice_date' ? dmyToIso(row[field] || "") : (row[field] || ""));
    setSnSug([]);
  };
  // Iter 215/216 — second_name auto-suggestions from Product Aliases.
  // Non-sticky: nothing found → team keeps typing a new name freely.
  // Uses /product-aliases/suggest which is gated by order_register access
  // (the wider /product-aliases list requires the aliases sub-panel and
  // 403s for most teams — that hid all suggestions before this iter).
  const [snSug, setSnSug] = useState([]);
  const [snSugIdx, setSnSugIdx] = useState(-1);
  const snSugRef = useRef(null);
  const onSecondNameType = (raw) => {
    const v = raw.toUpperCase();   // saved in capitals by default
    setInlineValue(v);
    setSnSugIdx(-1);
    if (snSugRef.current) clearTimeout(snSugRef.current);
    if (v.trim().length < 3) { setSnSug([]); return; }
    snSugRef.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/product-aliases/suggest`,
          { headers: getAuthHeader(), params: { q: v.trim(), limit: 30 } });
        const q = v.trim();
        const names = (r.data.rows || [])
          .map(n => String(n || "").toUpperCase())
          .filter(n => n && n !== q)
          .sort((a, b) => { const r = (n) => (n.startsWith(q) ? 0 : n.split(/\s+/).some(w => w.startsWith(q)) ? 1 : 2); return r(a) - r(b) || a.localeCompare(b); })
          .slice(0, 6);
        setSnSug(names);
      } catch { setSnSug([]); }
    }, 250);
  };
  const onSecondNameKey = (e) => {
    if (snSug.length === 0) {
      if (e.key === 'Enter') commitInline();
      else if (e.key === 'Escape') { setSnSug([]); setInlineEdit(null); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSnSugIdx(i => (i + 1) % snSug.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSnSugIdx(i => (i <= 0 ? snSug.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (snSugIdx >= 0) commitInline(snSug[snSugIdx]);
      else commitInline();
    } else if (e.key === 'Escape') {
      setSnSug([]); setInlineEdit(null);
    }
  };
  const commitInline = async (overrideVal) => {
    if (!inlineEdit) return;
    setSnSug([]);
    const { id, field } = inlineEdit;
    const row = rows.find(r => r.id === id);
    if (!row) { setInlineEdit(null); return; }
    const upper = ['invoice_no', 'second_name'];
    const rawVal = (typeof overrideVal === 'string') ? overrideVal : inlineValue;
    const val = field === 'invoice_date'
      ? fmtDmy(rawVal)   // picker gives ISO → store dd/mm/yyyy
      : upper.includes(field) ? rawVal.toUpperCase().trim() : rawVal.trim();
    if ((row[field] || "") === val) { setInlineEdit(null); return; }
    if (field === 'qty1' && Number(val) > 0 && (row.second_name || '').trim()) {
      setInlineEdit(null);
      let info = {};
      try { info = (await axios.get(`${API}/order-register/${row.id}/packing`, { headers: getAuthHeader() })).data; } catch { /* prompt still works */ }
      const q = parseFloat(String(row.quantity || '').replace(/[^\d.]/g, ''));
      const ans = await askPacking({ secondName: row.second_name, current: info.packing || '', qty: row.quantity, qty1: val,
        suggested: q > 0 ? String(Math.round(q / Number(val))) : '' });
      if (!ans) return;
      if (ans.mode === 'save') {
        try {
          const r = await axios.post(`${API}/order-register/${row.id}/packing`, { packing: ans.packing }, { headers: getAuthHeader() });
          setRows(prev => prev.map(x => x.id === row.id ? { ...x, qty1: r.data.qty1, qty1_manual: false } : x));
          toast.success(`Packing ${r.data.packing} saved for ${r.data.second_name} · Qty1 ${r.data.qty1 || '—'}`);
        } catch (e) { toast.error(e.response?.data?.detail || 'Could not save packing'); }
        return;
      }
    }
    let changeReason = "";
    if (field === 'tracking') {
      changeReason = await askChangeReason({ title: "Why change the tracking?", detail: `${row.customer_name || ""}\n${row.tracking || "—"} → ${val || "—"}` });
      if (!changeReason) { setInlineEdit(null); return; }
    }
    // Iter 154: invoice_no + invoice_date are parcel-wide — apply
    // the edit to every row that shares this parcel's key.
    const parcelWide = field === 'invoice_no' || field === 'invoice_date';
    const key = parcelKey(row);
    const targets = parcelWide ? rows.filter(r => parcelKey(r) === key) : [row];
    // Optimistic UI
    const targetIds = new Set(targets.map(t => t.id));
    // Parcel-level fields: backend cascades to the whole parcel; mirror it optimistically.
    const parcelLevel = ['date', 'code', 'customer_name', 'address', 'tracking'].includes(field);
    setRows(prev => prev.map(r => (targetIds.has(r.id) || (parcelLevel && parcelKey(r) === key)) ? { ...r, [field]: val } : r));
    // A reason on any product row covers the whole parcel — clear sibling no-reason flags.
    if (field === 'reason') {
      setRows(prev => prev.map(r => parcelKey(r) !== key ? r
        : { ...r, parcel_has_reason: !!val || r.parcel_has_reason,
            no_reason_age_days: val ? null : r.no_reason_age_days }));
    }
    setInlineEdit(null);
    try {
      const results = await Promise.all(targets.map(t => {
        const payload = { ...t, [field]: val };
        if (changeReason) payload.change_reason = changeReason;
        // Iter 203 - a Qty1 cell edit is a MANUAL override; the backend
        // only treats it as such when this flag is present (the row
        // payload always carries the current qty1 value).
        if (field === 'qty1') payload.qty1_override = val;
        // Iter 210 — pass the verified login password for locked fields.
        if (unlockedEdit && unlockedEdit.id === t.id
              && unlockedEdit.field === field) {
          payload.edit_guard = unlockedEdit.password;
        }
        return axios.put(`${API}/order-register/${t.id}`, payload,
          { headers: getAuthHeader() });
      }));
      const nice = { tracking: 'Tracking', second_name: 'Second name',
                     reason: 'Reason', invoice_no: 'Invoice #',
                     invoice_date: 'Invoice date', qty1: 'Qty1' };
      const suffix = parcelWide && targets.length > 1
        ? ` (${targets.length} rows in parcel)` : '';
      toast.success(`${nice[field] || field} saved${suffix}`);
      // Iter 202 — soft amber toast when the invoice is one parcel short
      // of its cap (11/12, or 29/30 for Emirates).
      const warn = results.map(r => r.data?.parcel_warning).find(Boolean);
      if (warn) toast.warning(warn);
      // Iter 203 - the server may have recomputed Qty1 (medicine / qty /
      // second_name edits); apply the returned value to the edited row(s).
      results.forEach((res, ix) => {
        const d = res?.data || {};
        if (d.qty1 !== undefined && targets[ix]) {
          const tid = targets[ix].id;
          setRows(prev => prev.map(r => r.id === tid
            ? { ...r, qty1: d.qty1, qty1_manual: d.qty1_manual } : r));
        }
      });
      // Iter 199 — server auto-fills today's Inv Date when an invoice
      // number is newly assigned; refetch so the date shows immediately.
      if (field === 'invoice_no' && val) fetchRows();
      // Iter 217 — alias saving is now handled purely by the inline
      // "Save alias" chip in the second_name cell; no toast prompt.
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      fetchRows();   // roll back
    } finally {
      // Iter 210 — always burn the unlock token after a locked-field
      // save attempt (success or failure).
      setUnlockedEdit(null);
    }
  };

  // Iter 311 — blank invoice cell: no password; pre-fill the user's running
  // (own, not yet completed) invoice so the team only presses Enter.
  const beginInvoiceEntry = (row) => {
    if (isViewer || row?.view_only) { toast.info(row?.view_only && !isViewer ? "View-only service — you can note and query this parcel, but only edit your allotted services" : "View-only access — editing is disabled"); return; }
    beginInline(row, 'invoice_no');
    const me = (user?.email || '').toLowerCase();
    const mine = activeOwners.find(o => (o.owner_email || '').toLowerCase() === me);
    const inv = mine?.running_invoice || '';
    if (inv && (invOwners[inv]?.status || '') !== 'completed') setInlineValue(inv);
  };

  // Iter 210 — password-gated edit opener for tracking + invoice_no.
  // Verifies the current user's login password, then unlocks a
  // single inline edit; the audit trail records who did it.
  const openLockedEdit = (row, field) => {
    if (isViewer || row?.view_only) { toast.info(row?.view_only && !isViewer ? "View-only service — you can note and query this parcel, but only edit your allotted services" : "View-only access — editing is disabled"); return; }
    // Iter 219 — reuse the cached password for 10 minutes after the first
    // successful unlock; no dialog needed while the cache is warm.
    const cached = guardPassword();
    if (cached) {
      setUnlockedEdit({ id: row.id, field, password: cached });
      beginInline(row, field);
      return;
    }
    setLockErr("");
    setLockPwd("");
    setLockTarget({ id: row.id, field });
    setLockOpen(true);
  };
  const confirmLockedEdit = async () => {
    if (!lockTarget) return;
    try {
      await axios.post(`${API}/auth/verify`, { password: lockPwd },
        { headers: getAuthHeader() });
      // Iter 219 — cache the verified password for 10 minutes so batch
      // edits (tracking, invoice) don't re-prompt on every row.
      setGuardCache({ password: lockPwd, until: Date.now() + 10 * 60 * 1000,
                      user: user?.email || "" });
      // Iter 212 — Edit-dialog mode: verified password unlocks the draft
      // save (covers tracking + new-invoice changes made in the editor).
      if (lockTarget.field === 'draft') {
        const pwd = lockPwd;
        setDraftEditGuard(pwd);
        setLockOpen(false);
        setLockTarget(null);
        setLockPwd("");
        await saveDraft("", pwd);
        return;
      }
      const row = rows.find(r => r.id === lockTarget.id);
      if (row) {
        setUnlockedEdit({ id: lockTarget.id, field: lockTarget.field,
                          password: lockPwd });
        beginInline(row, lockTarget.field);
        toast.info("Edit unlocked - the change will appear in the Audit Log");
      }
      setLockOpen(false);
      setLockTarget(null);
      setLockPwd("");
    } catch (e) {
      setLockErr(e.response?.data?.detail || "Incorrect password");
    }
  };
  const copyTracking = (t) => {
    if (!t) return;
    const onOk = () => toast.success("Tracking copied");
    // Iter 214 fix — clipboard.writeText REJECTS (async) when the
    // browser denies permission; a plain try/catch can't catch that and
    // the uncaught rejection broke the page (dev overlay). Use promise
    // handlers + an execCommand fallback.
    const onFail = () => {
      try {
        const ta = document.createElement('textarea');
        ta.value = String(t);
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        onOk();
      } catch { toast.error("Copy failed"); }
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(String(t)).then(onOk).catch(onFail);
    } else {
      onFail();
    }
  };

  // Row-level parcel grouping so multi-parcel customers (Lanny Akridge,
  // Marwan Saad, Angel…) render with alternating tints + identity
  // fields only on the first row, mirroring Pending Parcels' Iter-125
  // design.
  const parcelKey = (r) => [
    (r.date || '').trim(),
    (r.code || '').toUpperCase().trim(),
    (r.customer_name || '').trim().toLowerCase(),
    // Iter 290 — no-tracking (Emirates) parcels stay separate via the
    // wizard's permanent parcel_ref; they merge only if tracking is the same.
    (r.tracking || '').toUpperCase().trim() || (r.parcel_ref || '').trim(),
    // Iter 231 — same name + different address = DIFFERENT parcels
    // (e.g. three Kevin Hall orders to three different Denver addresses).
    (r.address || '').replace(/\s+/g, ' ').trim().toLowerCase(),
  ].join('|');

  const totalPages = Math.max(1, pageInfo.pageCount || Math.ceil(total / PAGE_SIZE));
  const searchActive = !!(debouncedQ || clientFilter || reasonFilter || dateFrom || dateTo || modeFilter
    || invFilter || noTracking || noInvoice || noSecondName || noPrice || ownerFilter || invoiceStatusFilter || cancelledOnly || overdueFilter || pendingOnly || age7Filter || rawOnly);

  // Iter 288 — Merge Guardrail: same customer + address + invoice with
  // ≥2 distinct trackings = multi-parcel customer; ops must double-check
  // before generating docs. Page-scoped; map parcelKey → sibling count.
  const multiParcelMap = useMemo(() => {
    const custTracks = {};   // custAddrKey|invoice -> Set(trackings)
    const parcelMeta = {};   // parcelKey -> {ck, trk, cancelled}
    for (const r of rows) {
      const trk = (r.tracking || '').trim().toUpperCase();
      if (!trk) continue;
      const ck = [
        (r.code || '').toUpperCase().trim(),
        (r.customer_name || '').trim().toLowerCase(),
        (r.address || '').replace(/\s+/g, ' ').trim().toLowerCase(),
        (r.invoice_no || '').trim().toUpperCase(),
      ].join('|');
      (custTracks[ck] = custTracks[ck] || new Set()).add(trk);
      parcelMeta[parcelKey(r)] = { ck };
    }
    const out = {};  // parcelKey -> siblingCount (only when > 1)
    for (const [pk, meta] of Object.entries(parcelMeta)) {
      const n = custTracks[meta.ck]?.size || 0;
      if (n > 1) out[pk] = n;
    }
    return out;
  }, [rows]);
  const multiParcelParcelCount = Object.keys(multiParcelMap).length;

  return (
    <div className="p-1 sm:p-4 space-y-3" data-testid="order-register-panel">
      <PpUserBar selected={selected} onChanged={() => { setSelected(new Set()); fetchRows(); }} />
      <ParcelQueryDialog onChanged={() => fetchRows()} />
      <Card>
        <CardContent className="pt-4 space-y-3 px-3 sm:px-6">
          {/* Iter 211 — toolbar stays pinned at the top while the page scrolls */}
          <div className={`sticky top-0 z-30 bg-white/95 backdrop-blur -mx-3 px-3 sm:-mx-6 sm:px-6 -mt-4 pt-4 pb-1 border-b border-slate-100 ${hiddenTools.size > 0 ? "or-tb-reflow" : "space-y-3"}`}
               data-testid="or-toolbar-sticky">
          <div className="or-tb-row flex flex-wrap items-center gap-2">
            {/* Iter 261 — menu slate toggle. Collapsing hides every
                other control row; only this button + global search +
                Today's Invoices stay visible to save screen space. */}
            <Button size="sm" variant="outline"
                    onClick={() => setSlateOpen(v => !v)}
                    title={slateOpen
                      ? "Collapse the menu slate — keep only search + Today's Invoices"
                      : "Expand the full menu slate"}
                    className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 max-md:order-[-4] max-md:px-2.5"
                    data-testid="or-menu-slate-toggle">
              {slateOpen
                ? <PanelTopClose className="w-4 h-4 mr-1 max-md:mr-0" />
                : <PanelTopOpen className="w-4 h-4 mr-1 max-md:mr-0" />}
              <span className="max-md:hidden">Menu</span>
            </Button>
            <div className="relative flex-1 min-w-[220px] max-w-md max-md:min-w-0 max-md:order-[-5] max-md:basis-[55%] max-md:max-w-none">
              <Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
              <OrSearchInput value={q} onCommit={setQ} />
            </div>
            {!slateOpen && (
              <Button size="sm" variant="outline"
                      className="border-blue-400 text-blue-700 hover:bg-blue-50 max-md:order-[-2] max-md:flex-1 max-md:basis-[40%]"
                      onClick={() => openTodayInvoices()}
                      title="Show today's invoices with parcel counts"
                      data-testid="or-today-invoices-collapsed">
                <Package className="w-4 h-4 mr-1" /> Today's Invoices
                {(todaySummary?.pending_no_invoice ?? 0) > 0 && (
                  <span className="ml-1 rounded-full bg-rose-600 text-white px-1.5 text-[10px] font-bold"
                        title="Rows without an invoice number today"
                        data-testid="or-today-invoices-pending-badge-collapsed">
                    {todaySummary.pending_no_invoice}
                  </span>
                )}
              </Button>
            )}
            {isMobile && (
              <Button size="sm" variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-50 max-md:order-[-1] max-md:flex-1 max-md:basis-[40%]"
                      onClick={() => openPage("/pending-worklist?bucket=ALL&age=total")} data-testid="or-m-pending-parcels-btn">
                <Clock className="w-4 h-4 mr-1" /> Pending
                {invoiceStats?.pending_parcels != null && (
                  <span className="ml-1 rounded-full bg-amber-500 text-white px-1.5 text-[10px] font-bold">{invoiceStats.pending_parcels}</span>)}
              </Button>
            )}
            {isMobile && (<Button size="sm" variant="outline" className="max-md:order-[-3] max-md:px-2.5" onClick={() => setOrFiltersOpen(true)} data-testid="or-mobile-filters-btn" title="Filters" aria-label="Filters"><SlidersHorizontal className="w-4 h-4" /></Button>)}
            <MobileSheetWrap isMobile={isMobile} open={slateOpen} onOpenChange={setSlateOpen} title="Menu" testid="or-mobile-menu-sheet">
            <Select value={modeFilter || "__all__"} onValueChange={(v) => setModeFilter(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-40 h-9" data-testid="or-mode-filter">
                <SelectValue placeholder="All modes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All modes</SelectItem>
                {modeOptions.filter(Boolean).map(m => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="or-add">
              <Plus className="w-4 h-4 mr-1" /> New Order
            </Button>
            <Button size="sm" variant="outline" onClick={() => setWizardOpen(true)}
                    disabled={uploading} data-testid="or-upload">
              {uploading ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              Upload Excel
            </Button>
            <OrderSheetButton />
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden"
                   onChange={(e) => e.target.files?.[0] && uploadExcel(e.target.files[0])} />
            <Button size="sm" variant="outline" onClick={exportExcel} data-testid="or-export">
              <Download className="w-4 h-4 mr-1" /> Export
            </Button>
            <div className="relative" ref={expMenuRef}>
              <Button size="sm" variant="outline"
                      className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => setExpMenuOpen(v => !v)}
                      title="Export rows by scope: this page, current filters, date range, client, country, or everything"
                      data-testid="or-export-scope-btn">
                <Download className="w-4 h-4 mr-1" /> Export by scope ▾
              </Button>
              {expMenuOpen && (
                <div className="absolute left-0 mt-1 z-20 bg-white border border-slate-200 rounded shadow-md py-1 min-w-[250px]"
                     data-testid="or-export-scope-menu">
                  {[
                    { k: '__page',   label: `This page (${rows.length} rows)` },
                    { k: 'filtered', label: 'All filtered results (current view)' },
                    { k: 'date',     label: 'Date-wise (pick range)…' },
                    { k: 'client',   label: 'Client-wise (client code)…' },
                    { k: 'country',  label: 'Country-wise (country group)…' },
                    { k: 'all_clients', label: 'All client codes (client-wise, one sheet)…' },
                    { k: 'single_track', label: 'Single Track Data (one tracking per parcel)…' },
                    { k: 'all',      label: 'All data (every date)' },
                  ].map(o => (
                    <button key={o.k} type="button"
                      className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-emerald-50 text-slate-700"
                      data-testid={`or-expscope-opt-${o.k}`}
                      onClick={() => {
                        if (o.k === '__page') {
                          setExpMenuOpen(false);
                          const ids = rows.map(r => r.id).join(',');
                          if (!ids) { toast.info('No rows on this page'); return; }
                          downloadExport({ ids }, 'this-page');
                        } else {
                          openScopeExport(o.k);
                        }
                      }}>
                      {o.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={screenPrint}
                    title="Open a printer-friendly view of the current filtered rows"
                    data-testid="or-screen-print">
              <Printer className="w-4 h-4 mr-1" /> Screen Print
            </Button>
            <Button size="sm" variant="outline"
                    onClick={fillFromAliases} disabled={fillingAliases || loading}
                    title="Auto-fill Second Name for visible rows using Product Aliases"
                    data-testid="or-fill-aliases">
              {fillingAliases ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />}
              Fill from Aliases
            </Button>
            <Button size="sm" variant="outline"
                    onClick={() => setTrayOpen(true)}
                    title="Review & save alias mappings for this page — ready rows pre-filled, Save All stores them in one click"
                    data-testid="or-suggest-aliases">
              <Sparkles className="w-4 h-4 mr-1" /> Suggest Aliases
              {unmatchedList.length > 0 && (
                <span className="ml-1 rounded-full bg-amber-100 text-amber-800 px-1.5 text-[10px] font-bold"
                      data-testid="or-suggest-count">
                  {unmatchedList.length}
                </span>
              )}
            </Button>
            <Button size="sm" variant="outline"
                    onClick={() => window.open('/order-register/audit', '_blank')}
                    title="Order Register audit trail (kept for 365 days) - opens in a new tab"
                    data-testid="or-audit-log-btn">
              <ScrollText className="w-4 h-4 mr-1" /> Audit Log
            </Button>
            <button type="button" onClick={openPricing}
                    className="inline-flex items-center gap-1 h-8 px-2 rounded-md border border-slate-300 text-[11px] text-slate-600 hover:bg-slate-50 shadow-sm"
                    title="Monthly INR→USD rate + margin for the $ Price column — admin edits directly, team needs the supervisor password"
                    data-testid="or-pricing-chip">
              <DollarSign className="w-3.5 h-3.5 text-emerald-600" />
              {pricing.inr_per_usd
                ? <span>₹{pricing.inr_per_usd}/$1 · +{pricing.margin_pct ?? 5}%</span>
                : <span>Set ₹/$ rate</span>}
              {pricing.month && pricing.month !== new Date().toISOString().slice(0, 7) && (
                <span className="text-amber-600 font-semibold"
                      title={`Rate last set for ${pricing.month} — refresh for the new month`}
                      data-testid="or-pricing-stale">stale</span>
              )}
            </button>
            <div className="inline-flex rounded-md border border-slate-300 overflow-hidden shadow-sm"
                 data-testid="or-teamview-toggle" role="group">
              {[
                { k: "pending",   label: "Pending",
                  hint: "Parcels that still need work - excludes completed invoices" },
                { k: "completed", label: "Completed",
                  hint: "Only parcels tied to a completed invoice" },
                { k: "all",       label: "All",
                  hint: "Every parcel regardless of invoice status" },
              ].map(o => (
                <button key={o.k} type="button" title={o.hint}
                  data-testid={`or-teamview-${o.k}`}
                  onClick={() => { setTeamView(o.k); setInvoiceStatusFilter(""); }}
                  className={`px-3 h-8 text-[12px] font-semibold transition inline-flex items-center ${
                    teamView === o.k && !invoiceStatusFilter && !cancelledOnly
                      ? (o.k === "completed"
                          ? "bg-emerald-600 text-white"
                          : o.k === "pending"
                            ? "bg-amber-500 text-white"
                            : "bg-slate-700 text-white")
                      : "bg-white text-slate-600 hover:bg-slate-50"
                  }`}>
                  {o.label}
                  {invoiceStats?.[`${o.k}_parcels`] != null && (
                    <span className={`ml-1.5 px-1.5 py-0 rounded-full text-[10px] font-bold ${
                      teamView === o.k
                        ? "bg-white/30 text-white"
                        : o.k === "pending" ? "bg-amber-100 text-amber-800"
                        : o.k === "completed" ? "bg-emerald-100 text-emerald-800"
                        : "bg-slate-100 text-slate-700"
                    }`} data-testid={o.k === "pending" ? "or-pending-badge" : `or-${o.k}-badge`}
                      title="Parcels (a multi-product parcel counts once)">
                      {invoiceStats[`${o.k}_parcels`]}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {selected.size > 0 && (
              <>
                <Button size="sm" variant="outline" className="border-rose-500 text-rose-700 hover:bg-rose-50"
                        onClick={() => { setBulkCancelOpen(true); setBulkCancelPassword(""); setBulkCancelReason(""); }}
                        title="Cancel every selected parcel with one password (ideal for scrapped batches)"
                        data-testid="or-bulk-cancel">
                  <Ban className="w-4 h-4 mr-1" /> Cancel ({selected.size})
                </Button>
                <Button size="sm" variant="outline" className="border-amber-500 text-amber-700 hover:bg-amber-50"
                        onClick={() => setBulkReasonOpen(true)}
                        title="Set one reason on every selected parcel that has no reason yet"
                        data-testid="or-bulk-reason">
                  <MessageSquarePlus className="w-4 h-4 mr-1" /> Set reason ({selected.size})
                </Button>
                <Button size="sm" variant="outline" className="border-red-500 text-red-700 hover:bg-red-50"
                        onClick={() => bulkDelete()} data-testid="or-bulk-delete">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete ({selected.size})
                </Button>
              </>
            )}
            <div className="relative" ref={delMenuRef}>
              <Button size="sm" variant="outline"
                      className="border-red-400 text-red-700 hover:bg-red-50"
                      onClick={() => setDelMenuOpen(v => !v)}
                      title="Delete rows by scope: this page, current filters, date range, client, country, or everything"
                      data-testid="or-delete-scope-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Delete by scope ▾
              </Button>
              {delMenuOpen && (
                <div className="absolute left-0 mt-1 z-20 bg-white border border-slate-200 rounded shadow-md py-1 min-w-[250px]"
                     data-testid="or-delete-scope-menu">
                  {[
                    { k: '__page',    label: `This page - select all ${rows.length} rows` },
                    { k: 'filtered',  label: 'All filtered results (current view)' },
                    { k: 'date',      label: 'Date-wise (pick range)…' },
                    { k: 'client',    label: 'Client-wise (client code)…' },
                    { k: 'country',   label: 'Country-wise (country group)…' },
                    { k: 'all',       label: 'All data - every date (danger)' },
                  ].map(o => (
                    <button key={o.k} type="button"
                      className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-red-50 text-slate-700"
                      data-testid={`or-delscope-opt-${o.k}`}
                      onClick={() => {
                        if (o.k === '__page') {
                          setDelMenuOpen(false);
                          setSelected(prev => {
                            const s = new Set(prev);
                            rows.forEach(r => s.add(r.id));
                            return s;
                          });
                          toast.info(`Selected all ${rows.length} rows on this page - click Delete (${rows.length}) to proceed`);
                        } else {
                          openScopeDelete(o.k);
                        }
                      }}>
                      {o.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button size="sm" variant="outline"
                    className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                    onClick={openRecover}
                    title="Restore rows deleted in the last 24h"
                    data-testid="or-recover">
              <RefreshCw className="w-4 h-4 mr-1" /> Recover deleted
            </Button>
            <button type="button"
              onClick={() => openTodayInvoices()}
              className="h-8 px-2 rounded border border-slate-300 bg-slate-50 text-[11px] font-semibold text-slate-700 hover:bg-slate-100 transition"
              title="Actual parcel count (multi-product rows collapsed) — click to open Today's Invoices"
              data-testid="or-today-parcels-chip">
              Today's Parcels ({todaySummary?.total_parcels ?? 0})
            </button>
            <button type="button"
              onClick={openUploadAnalytics}
              className="h-8 px-2 rounded border border-indigo-300 bg-white text-[11px] font-semibold text-indigo-700 hover:bg-indigo-50 transition"
              title="Who uploaded what and when — rows/invoices/parcels detected vs inserted"
              data-testid="or-uploads-btn">
              Uploads
            </button>
            {canManageOverwrites && (
              <button type="button"
                onClick={() => setOwhOpen(true)}
                className="h-8 px-2 rounded border border-amber-300 bg-white text-[11px] font-semibold text-amber-700 hover:bg-amber-50 transition"
                title="Orders replaced by Overwrite during uploads — restore old rows"
                data-testid="or-overwrite-history-btn">
                Overwrite History
              </button>
            )}
            <div className="ml-auto text-xs text-slate-500 flex items-center gap-3">
              <span data-testid="or-count">
                {(() => {
                  // Iter 182 — clearer indicator: "X shown · Y total" when
                  // a filter/search is active so it's obvious the search
                  // spans every page.
                  const filterOn = !!(debouncedQ || modeFilter || debouncedClient ||
                                       debouncedReason || dateFrom || dateTo ||
                                       noTracking || noInvoice || noSecondName ||
                                       ownerFilter);
                  if (filterOn) {
                    const shown = rows.length;
                    return (
                      <span className="text-indigo-700 font-semibold">
                        {shown.toLocaleString()} shown · {total.toLocaleString()} match{total === 1 ? '' : 'es'} (all pages)
                      </span>
                    );
                  }
                  return `${total.toLocaleString()} order${total === 1 ? "" : "s"}`;
                })()}
              </span>
              <Button size="sm" variant="ghost" onClick={fetchRows} className="h-7 w-7 p-0"
                      title="Refresh" data-testid="or-refresh">
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
            </MobileSheetWrap>
          </div>

          <MobileSheetWrap isMobile={isMobile} open={isMobile ? orFiltersOpen : slateOpen} onOpenChange={setOrFiltersOpen} title="Filters & views" testid="or-mobile-filters-sheet">
          {/* Iter 193 — Invoice row: Today's Invoices + stat toggles +
              No Invoice / Pending presets together in one row */}
          <div className="or-tb-row flex flex-wrap items-center gap-2 text-xs" data-testid="or-invoice-row">
            <Button size="sm" variant="outline"
                    className="border-blue-400 text-blue-700 hover:bg-blue-50"
                    onClick={() => openTodayInvoices()}
                    title="Show today's invoices with parcel counts, completed/in-progress and pending non-invoice rows"
                    data-testid="or-today-invoices">
              <Package className="w-4 h-4 mr-1" /> Today's Invoices
              {(todaySummary?.pending_no_invoice ?? 0) > 0 && (
                <span className="ml-1 rounded-full bg-rose-600 text-white px-1.5 text-[10px] font-bold"
                      title="Rows without an invoice number today"
                      data-testid="or-today-invoices-pending-badge">
                  {todaySummary.pending_no_invoice}
                </span>
              )}
            </Button>
            {/* Iter 191 — invoice stat cards converted to compact
                segmented buttons (Invoice-Flow tab style). Same
                click-to-filter behaviour as the old cards. */}
            <div className="flex gap-1 bg-slate-100 rounded-lg p-1" data-testid="or-invoice-cards">
              <button type="button"
                onClick={() => setInvoiceStatusFilter(f => f === "in_progress" ? "" : "in_progress")}
                className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-all ${
                  invoiceStatusFilter === "in_progress"
                    ? "bg-white text-amber-700 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"}`}
                data-testid="or-card-uncompleted"
                title="Click to filter the table to In Progress invoices">
                Uncompleted Invoices
                <span className="ml-1 font-bold text-amber-700" data-testid="or-card-uncompleted-count">
                  ({invoiceStats?.uncompleted ?? "—"})
                </span>
              </button>
              <button type="button"
                onClick={() => setInvoiceStatusFilter(f => f === "completed_today" ? "" : "completed_today")}
                className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-all ${
                  invoiceStatusFilter === "completed_today"
                    ? "bg-white text-emerald-700 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"}`}
                data-testid="or-card-completed-today"
                title="Click to filter the table to invoices completed today">
                Completed Today
                <span className="ml-1 font-bold text-emerald-700" data-testid="or-card-completed-today-count">
                  ({invoiceStats?.completed_today ?? "—"})
                </span>
              </button>
              <button type="button"
                onClick={() => setInvoiceStatusFilter(f => f === "has_invoice" ? "" : "has_invoice")}
                className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-all ${
                  invoiceStatusFilter === "has_invoice"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"}`}
                data-testid="or-card-total-active"
                title="Click to filter the table to rows that carry an invoice number">
                Total Active
                <span className="ml-1 font-bold text-slate-700" data-testid="or-card-total-active-count">
                  ({invoiceStats?.total_active ?? "—"})
                </span>
              </button>
              <button type="button"
                onClick={() => setCancelledOnly(v => !v)}
                className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-all ${
                  cancelledOnly
                    ? "bg-white text-rose-700 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"}`}
                data-testid="or-card-cancelled"
                title="Show only cancelled parcels — they leave Pending but search still finds them">
                Cancelled
                <span className="ml-1 font-bold text-rose-700" data-testid="or-card-cancelled-count">
                  ({invoiceStats?.cancelled_rows ?? "—"})
                </span>
              </button>
            </div>
            <DupToolbar dupOnly={dupOnly} setDupOnly={(v) => { setDupOnly(v); setPage(1); }} dbaCross={dbaCross}
              setDbaCross={(v) => { setDbaCross(v); setPage(1); }} refreshKey={dupRefresh} />
            <OrderHealthBoard onFix={(k) => {
              setDupOnly(k === "dup"); setDbaCross(false); setInvWaiting(k === "inv_waiting");
              setNoTracking(k === "no_tracking"); setNoSecondName(k === "no_second_name");
              if (k === "no_tracking" || k === "no_second_name") setTeamView("pending");
              setPage(1);
            }} />
            <OrReadingMode rows={rows} />
            <QueryToolbar value={queryState} onChange={(v) => { setQueryState(v); setPage(1); }} />
            {!isMobile && <PendingServiceReport onOpenInRegister={(b) => { setSvcBucket(b); setTeamView("pending"); setPage(1); }} />}
            {svcBucket && (
              <button type="button" onClick={() => { setSvcBucket(""); setPage(1); }} data-testid="or-svc-bucket-chip"
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-indigo-600 px-2.5 py-1 text-[11px] font-semibold text-white">
                Pending: {svcBucket} ✕
              </button>
            )}
            {invWaiting && (
              <button type="button" onClick={() => { setInvWaiting(false); setPage(1); }} data-testid="or-inv-waiting-chip"
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-sky-600 px-2.5 py-1 text-[11px] font-semibold text-white">
                Invoices waiting 2+ days ✕
              </button>
            )}
            {(invoiceStats?.ageing?.overdue ?? 0) > 0 && (
              <button type="button"
                onClick={() => setOverdueFilter(v => v ? false : "amber")}
                className={`h-8 px-2 rounded border transition text-[11px] font-semibold ${
                  overdueFilter ? 'bg-red-600 text-white border-red-600'
                  : 'bg-white text-red-600 border-red-300 hover:bg-red-50'}`}
                title={`Parcels whose reason is waiting 2+ days (${invoiceStats.ageing.critical} waiting 4+ days) — click to filter`}
                aria-pressed={!!overdueFilter} aria-label="Filter parcels ageing 2 days or more"
                data-testid="or-ageing-chip">
                <AlertTriangle className="w-3 h-3 mr-1 inline" />
                Ageing {invoiceStats.ageing.overdue}
                {invoiceStats.ageing.critical > 0 && (
                  <span role="button" tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); setOverdueFilter(v => v === "red" ? false : "red"); }}
                        title="Show only parcels waiting 4+ days"
                        aria-pressed={overdueFilter === "red"} aria-label="Filter parcels ageing 4 days or more"
                        className={`ml-1 rounded-full px-1.5 text-[10px] font-bold ${overdueFilter === "red" ? 'bg-white text-red-700 ring-1 ring-white' : overdueFilter ? 'bg-white/30 text-white' : 'bg-red-100 text-red-700'}`}
                        data-testid="or-ageing-critical">
                    {invoiceStats.ageing.critical} × 4d+
                  </span>
                )}
              </button>
            )}
            <button type="button"
              onClick={() => setNoInvoice(v => !v)}
              className={`h-8 px-2 rounded border transition text-[11px] font-semibold ${
                noInvoice ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'
              }`}
              data-testid="or-preset-no-invoice">
              No Invoice
            </button>
            <Button size="sm" variant="outline"
              onClick={() => setPcOpen(true)}
              className={`h-8 text-[11px] font-semibold ${pendingOnly ? "bg-amber-600 text-white border-amber-600 hover:bg-amber-700" : "border-amber-400 text-amber-800 hover:bg-amber-50"}`}
              data-testid="or-pending-btn"
              title="Pending Center — filters, service/country lists, client ZIP, procure list">
              <Clock className="w-3 h-3 mr-1" /> Pending
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => { setRawOnly(v => !v); setPage(1); }}
              className={`h-8 text-[11px] font-semibold ${rawOnly ? "bg-rose-600 text-white border-rose-600 hover:bg-rose-700" : "border-rose-400 text-rose-700 hover:bg-rose-50"}`}
              data-testid="or-raw-api-btn"
              title="Raw / API / powder processing — open (not invoiced) orders whose product has raw, API, powder, kg or gram in the name">
              <FlaskConical className="w-3 h-3 mr-1" /> API
              {rawCount != null && (
                <span className={`ml-1 rounded-full px-1.5 text-[10px] leading-4 ${rawOnly ? "bg-white text-rose-700" : "bg-rose-600 text-white"}`}
                      data-testid="or-raw-api-count">{rawCount}</span>)}
            </Button>
            {!isViewer && <PendingSlipsButton onOpen={openSlipFromList} refreshKey={slipRefresh} isAdmin={isAdmin} />}
            <PendingByUserButton onShow={(f) => {
              setPendingOnly(true); setPendingScope("all"); setAge7Filter(false);
              setDateFrom(f.date_from || ""); setDateTo(f.date_to || "");
              setReasonFilter(f.urgent ? "urgent" : ""); setReasonExact(false);
              setScopeUser(f.user_id ? { id: f.user_id, name: f.user_name } : null);
              setTeamView("all"); setInvoiceStatusFilter(""); setSortMode("priority"); setPage(1);
              if (f.bucket) setSvcBucket(f.bucket);
            }} />
            <button type="button" data-testid="or-sort-toggle"
              onClick={() => { const v = sortMode === "priority" ? "latest" : "priority"; localStorage.setItem("orSortMode", v); setSortMode(v); setPage(1); }}
              title={sortMode === "priority" ? "Urgent-ready first, then pending oldest first — click for latest uploads first" : "Latest uploads first — click for urgent / pending-first order"}
              className={`h-8 px-2.5 rounded-md border text-xs font-semibold transition-colors ${sortMode === "priority" ? "bg-red-50 border-red-300 text-red-700" : "bg-white border-slate-300 text-slate-600"}`}>
              {sortMode === "priority" ? "Order: Urgent → Pending oldest" : "Order: Latest first"}
            </button>
            {scopeUser && (
              <span className="inline-flex items-center gap-1 h-8 px-2 rounded-md bg-sky-50 border border-sky-300 text-xs text-sky-800" data-testid="or-scope-user-chip">
                Scope: <b>{scopeUser.name}</b>
                <button type="button" onClick={() => setScopeUser(null)} className="ml-1 font-bold hover:text-sky-950" data-testid="or-scope-user-clear">×</button>
              </span>)}
            {!isViewer && <EmiratesTrackingUpdate testId="or" onApplied={() => fetchRows()} />}
            {!isViewer && <MergedParcelCheck />}
            <PartnerOrdersBadge onOpened={() => fetchRows()} onFind={(name) => setQ(name)} />
            {isAdmin && <PartnerAdmin />}
          </div>

          {/* Secondary filter row — mirrors Pending Parcels toolbar */}
          <div className="or-tb-row flex flex-wrap items-center gap-2 text-xs">
            <ClientCodeInput value={clientFilter} onChange={setClientFilter}
              className="h-8 w-40" testid="or-client-filter" />
            <ReasonFilter value={reasonFilter} exact={reasonExact}
              onChange={(v, ex) => { setReasonFilter(v); setReasonExact(!!ex && !!v); setPage(1); }}
              onReasonsChanged={async () => {
                try {
                  const r = await axios.get(`${API}/order-register/reasons`, { headers: getAuthHeader() });
                  if (Array.isArray(r.data?.reasons)) setReasonSuggestions(r.data.reasons);
                } catch { /* ignore */ }
              }} />
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="h-8 w-40" data-testid="or-date-from" title="Order date from" />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="h-8 w-40" data-testid="or-date-to" title="Order date to" />
            {/* Iter 220 — Owner filter: everything a team member still has
                open (rows whose ACTIVE invoice they own) */}
            <select
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              className={`h-8 px-1.5 rounded border text-[11px] font-semibold bg-white ${
                ownerFilter
                  ? 'border-indigo-400 text-indigo-700'
                  : 'border-slate-300 text-slate-500'
              }`}
              title="Filter to rows whose active invoice belongs to this team member"
              data-testid="or-owner-filter">
              <option value="">All owners</option>
              {activeOwners.map(o => (
                <option key={o.owner_email} value={o.owner_email}>
                  {o.owner_name} ({o.parcels ?? o.invoices} {o.parcels != null ? 'parcels' : 'inv'})
                </option>
              ))}
            </select>
            {/* Blank-field preset toggles (Iter 149) */}
            <button type="button"
              onClick={() => setNoTracking(v => !v)}
              className={`h-8 px-2 rounded border transition text-[11px] font-semibold ${
                noTracking ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-rose-700 border-rose-300 hover:bg-rose-50'
              }`}
              data-testid="or-preset-no-tracking">
              No Tracking
            </button>
            {/* Iter 176 - "Fill Missing Invoice" button removed per
                operator request. The Pending report + Emirates
                tracking fill (see below) replace this flow. */}
            <Button size="sm" variant="outline"
              onClick={() => emiratesInputRef.current?.click()}
              className="h-8 border-purple-400 text-purple-800 hover:bg-purple-50 text-[11px] font-semibold"
              data-testid="or-emirates-fill-btn"
              title="Emirates Tracking Fill — upload Excel to fill trackings by Client Code + Customer Name">
              <Truck className="w-3 h-3 mr-1" /> Emirates Tracking Fill
            </Button>
            <input ref={emiratesInputRef} type="file"
              accept=".xlsx,.xls" hidden
              data-testid="or-emirates-file-input"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                if (f) { setEmiratesFile(f); setEmiratesResult(null); }
              }} />
            <button type="button"
              onClick={() => setNoSecondName(v => !v)}
              className={`h-8 px-2 rounded border transition text-[11px] font-semibold ${
                noSecondName ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-violet-700 border-violet-300 hover:bg-violet-50'
              }`}
              data-testid="or-preset-no-secondname">
              No Second Name
            </button>
            <button type="button"
              onClick={() => setNoPrice(v => !v)}
              className={`h-8 px-2 rounded border transition text-[11px] font-semibold ${
                noPrice ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'
              }`}
              title="Show only rows whose Second Name has no purchase price in Product Aliases"
              data-testid="or-preset-no-price">
              No $ Price
            </button>
            {isAdmin && noPrice && (
              <button type="button"
                onClick={openBulkPrice}
                className="h-8 px-2 rounded border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition text-[11px] font-semibold"
                title="Type purchase prices for all visible products — writes back to Product Aliases"
                data-testid="or-bulk-price-btn">
                Update Prices ({unpricedNames.length})
              </button>
            )}
            {/* Column hide toggle (Iter 149) */}
            <div className="relative" ref={colsMenuRef}>
              <Button size="sm" variant="outline" className="h-8"
                onClick={() => setColsMenuOpen(v => !v)}
                data-testid="or-cols-btn">
                <EyeOff className="w-3 h-3 mr-1" /> Hide Columns
                {hiddenCols.size > 0 && (
                  <span className="ml-1 rounded-full bg-indigo-100 text-indigo-700 px-1.5">
                    {hiddenCols.size}
                  </span>
                )}
              </Button>
              {colsMenuOpen && (
                <div className="absolute right-0 mt-1 z-20 bg-white border border-slate-200 rounded shadow-md p-2 min-w-[200px]"
                     data-testid="or-cols-menu">
                  <label className="flex items-center gap-2 py-1 cursor-pointer text-[11px] font-semibold border-b border-slate-100 pb-2 mb-1"
                         data-testid="or-col-toggle-all">
                    <input type="checkbox" checked={allColsHidden} onChange={toggleAllCols} />
                    Hide all columns
                  </label>
                  {[
                    { k: 'select',  label: 'Select checkbox' },
                    { k: 'edit',    label: 'Edit column' },
                    { k: 'address', label: 'Country column' },
                    { k: 'qty',     label: 'Qty column' },
                    { k: 'parcelId', label: 'Parcel ID column' },
                    { k: 'invDate', label: 'Invoice Date column' },
                    { k: 'notes',   label: 'Notes column' },
                  ].map(({k, label}) => (
                    <label key={k} className="flex items-center gap-2 py-1 cursor-pointer text-[11px]"
                           data-testid={`or-col-toggle-${k}`}>
                      <input type="checkbox" checked={isHidden(k)}
                        onChange={() => toggleCol(k)} />
                      Hide {label}
                    </label>
                  ))}
                  <div className="pt-1 border-t border-slate-100 mt-1 flex justify-between">
                    <button className="text-[10px] text-slate-500 hover:text-slate-700"
                            title="Back to defaults: Select + Edit hidden"
                            onClick={() => setHiddenCols(new Set(DEFAULT_HIDDEN_COLS))}>
                      Reset
                    </button>
                    <button className="text-[10px] text-indigo-600 hover:text-indigo-800 font-medium"
                            onClick={() => setColsMenuOpen(false)}>
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
            <OrToolbarMore />
            {/* Iter 286 — Hide Buttons (per-user toolbar customization) */}
            {tbHideCss && <style>{tbHideCss}</style>}
            <div className={isMobile ? "flex w-full flex-wrap" : "relative inline-flex"} ref={tbMenuRef}>
              <Button size="sm" variant="outline" className={`h-8 ${(tbPresets.length || teamPresets.length) ? "rounded-r-none" : ""}`}
                onClick={() => { setTbQuickOpen(false); setTbMenuOpen(v => !v); }}
                data-testid="or-tools-btn">
                <SlidersHorizontal className="w-3 h-3 mr-1" /> Hide Buttons
                {tbActive && (
                  <span className="ml-1 text-indigo-700 font-semibold max-w-[120px] truncate"
                        data-testid="or-tools-btn-preset">· {activeLabel}{isTeamActive ? " (Team)" : ""}</span>
                )}
                {hiddenTools.size > 0 && (
                  <span className="ml-1 rounded-full bg-indigo-100 text-indigo-700 px-1.5"
                        data-testid="or-tools-hidden-count">{hiddenTools.size}</span>
                )}
              </Button>
              {(tbPresets.length > 0 || teamPresets.length > 0) && (
                <Button size="sm" variant="outline" className="h-8 px-1.5 rounded-l-none border-l-0"
                  onClick={() => { setTbMenuOpen(false); setTbQuickOpen(v => !v); }}
                  title="Switch toolbar preset"
                  data-testid="or-tools-quick-btn">
                  <ChevronDown className="w-3 h-3" />
                </Button>
              )}
              {tbQuickOpen && (
                <div className={`${isMobile ? "mt-1 w-full" : "absolute left-0 mt-1 top-full z-20 w-[200px]"} bg-white border border-slate-200 rounded shadow-md py-1`}
                     data-testid="or-tools-quick-menu">
                  {tbPresets.map(p => (
                    <button key={p.name}
                      className={`w-full text-left px-3 py-1.5 text-[11px] hover:bg-slate-50 flex items-center justify-between ${p.name === tbActive ? "font-semibold text-indigo-700" : "text-slate-700"}`}
                      onClick={() => { applyPreset(p.name); setTbQuickOpen(false); }}
                      data-testid={`or-tools-quick-${p.name}`}>
                      <span className="truncate">{p.name}</span>
                      <span className="text-[10px] text-slate-400">{(p.hidden || []).length} hidden</span>
                    </button>
                  ))}
                  {teamPresets.length > 0 && (<>
                    <div className="px-3 pt-1.5 pb-0.5 text-[9px] uppercase tracking-wider text-emerald-600 font-semibold border-t border-slate-100"
                         data-testid="or-tools-quick-team-heading">Team</div>
                    {teamPresets.map(p => (
                      <button key={`t-${p.name}`}
                        className={`w-full text-left px-3 py-1.5 text-[11px] hover:bg-emerald-50 flex items-center justify-between ${tbActive === `team:${p.name}` ? "font-semibold text-emerald-700" : "text-slate-700"}`}
                        onClick={() => { applyPreset(`team:${p.name}`); setTbQuickOpen(false); }}
                        data-testid={`or-tools-quick-team-${p.name}`}>
                        <span className="truncate">{p.name}</span>
                        <span className="text-[10px] text-slate-400">{(p.hidden || []).length} hidden</span>
                      </button>
                    ))}
                  </>)}
                  <button className="w-full text-left px-3 py-1.5 text-[11px] text-slate-500 hover:bg-slate-50 border-t border-slate-100"
                    onClick={() => { showAllTools(); setTbQuickOpen(false); }}
                    data-testid="or-tools-quick-showall">Show all (no preset)</button>
                </div>
              )}
              <TeamPresetPublishDialog open={publishOpen} onClose={() => setPublishOpen(false)}
                defaultName={activeLabel} existing={teamPresets} onPublish={doPublishTeamPreset} />
              {tbMenuOpen && (
                <div className={`${isMobile ? "mt-1 w-full" : "absolute left-0 top-full mt-1 z-20 w-[270px] max-h-[420px] overflow-y-auto"} bg-white border border-slate-200 rounded shadow-md p-2`}
                     data-testid="or-tools-menu">
                  <div className="text-[10px] text-slate-500 pb-1.5 border-b border-slate-100 mb-1">
                    Untick to hide a tool — saved to your account.
                  </div>
                  {/* Iter 287 — named presets */}
                  <div className="pb-1.5 mb-1 border-b border-slate-100 space-y-1" data-testid="or-tools-presets">
                    <div className="flex items-center gap-1">
                      <select value={tbActive} onChange={e => e.target.value && applyPreset(e.target.value)}
                              className="flex-1 h-7 border border-slate-200 rounded text-[11px] px-1 bg-white"
                              data-testid="or-tools-preset-select">
                        <option value="">{(tbPresets.length || teamPresets.length) ? "Custom (no preset)" : "No presets yet"}</option>
                        {tbPresets.length > 0 && (
                          <optgroup label="My presets">
                            {tbPresets.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
                          </optgroup>
                        )}
                        {teamPresets.length > 0 && (
                          <optgroup label="Team">
                            {teamPresets.map(p => {
                              const n = (p.target_teams?.length || 0) + (p.target_user_ids?.length || 0);
                              const aud = !n ? "" : isAdmin ? ` (${[...(p.target_teams || []), ...(p.target_user_ids?.length ? [`${p.target_user_ids.length} user(s)`] : [])].join(", ")})` : "";
                              return <option key={`t-${p.name}`} value={`team:${p.name}`}>{p.name}{aud}</option>;
                            })}
                          </optgroup>
                        )}
                      </select>
                      <button className="h-7 px-2 rounded bg-indigo-600 text-white text-[10px] font-medium hover:bg-indigo-700"
                              onClick={savePresetAs} data-testid="or-tools-preset-save">Save as…</button>
                    </div>
                    {tbActive && (
                      <div className="flex gap-3 text-[10px]">
                        <span className="text-slate-500">Active: <b data-testid="or-tools-preset-active">{activeLabel}</b>
                          {isTeamActive && <span className="ml-1 px-1 rounded bg-emerald-100 text-emerald-700" data-testid="or-tools-preset-team-tag">Team</span>}
                        </span>
                        {!isTeamActive && (<>
                          <button className="text-slate-600 hover:text-slate-900 ml-auto" onClick={renamePreset}
                                  data-testid="or-tools-preset-rename">Rename</button>
                          <button className="text-red-600 hover:text-red-800" onClick={deletePreset}
                                  data-testid="or-tools-preset-delete">Delete</button>
                        </>)}
                        {isTeamActive && isAdmin && (
                          <button className="text-red-600 hover:text-red-800 ml-auto" onClick={unpublishTeamPreset}
                                  data-testid="or-tools-team-unpublish">Unpublish</button>
                        )}
                      </div>
                    )}
                    {isAdmin && (
                      <button className="text-[10px] text-emerald-700 hover:text-emerald-900 font-medium"
                              onClick={publishTeamPreset} data-testid="or-tools-team-publish">
                        + Publish current layout as Team preset
                      </button>
                    )}
                  </div>
                  {TB_TOOLS.map(g => ({ ...g, items: g.items.filter(it => !forbiddenTools.has(it.id)) }))
                    .filter(g => g.items.length).map(g => (
                    <div key={g.group} className="mb-1.5">
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold py-0.5">{g.group}</div>
                      {g.items.map(it => (
                        <label key={it.id} className="flex items-center gap-2 py-1 cursor-pointer text-[11px]"
                               data-testid={`or-tool-toggle-${it.id}`}>
                          <input type="checkbox" checked={hiddenTools.has(it.id)}
                                 onChange={() => toggleTool(it.id)} />
                          {it.label}
                        </label>
                      ))}
                    </div>
                  ))}
                  <div className="pt-1 border-t border-slate-100 mt-1 flex justify-between">
                    <button className="text-[10px] text-slate-500 hover:text-slate-700"
                            onClick={showAllTools} data-testid="or-tools-reset">
                      Reset to default
                    </button>
                    <button className="text-[10px] text-indigo-600 hover:text-indigo-800 font-medium"
                            onClick={showAllTools} data-testid="or-tools-show-all">
                      Show all
                    </button>
                  </div>
                </div>
              )}
            </div>
            {invFilter && (
              <span className="h-8 px-2 inline-flex items-center gap-1 rounded bg-indigo-50 border border-indigo-300 text-indigo-700 text-[11px] font-semibold"
                    data-testid="or-inv-filter-chip">
                Invoice: {invFilter}
                <button type="button" onClick={() => setInvFilter("")}
                        className="text-indigo-400 hover:text-indigo-700 font-bold"
                        title="Clear invoice filter"
                        data-testid="or-inv-filter-clear">×</button>
              </span>
            )}
            {(clientFilter || reasonFilter || dateFrom || dateTo || modeFilter || q || invFilter ||
              noTracking || noInvoice || noSecondName || invoiceStatusFilter || ownerFilter) && (
              <Button size="sm" variant="ghost" className="h-8 text-slate-500"
                onClick={() => { setQ(""); setModeFilter(""); setClientFilter("");
                                  setReasonFilter(""); setReasonExact(false); setPendingOnly(false); setAge7Filter(false); setRawOnly(false); setDateFrom(""); setDateTo(""); setScopeUser(null);
                                  setInvFilter("");
                                  setNoTracking(false); setNoInvoice(false); setNoSecondName(false);
                                  setInvoiceStatusFilter(""); setOwnerFilter("");
                                  setCancelledOnly(false); }}
                data-testid="or-clear-filters">
                Clear filters
              </Button>
            )}
          </div>
          {/* Iter 272 — search result counter */}
          {searchActive && !loading && (
            <div className="or-tb-full flex items-center gap-2 px-1 text-xs text-slate-600" data-testid="or-search-counter">
              <Search className="w-3.5 h-3.5 text-slate-400" />
              <span><b data-testid="or-search-parcels">{pageInfo.parcels}</b> parcel{pageInfo.parcels === 1 ? '' : 's'}</span>
              <span className="text-slate-300">·</span>
              <span><b data-testid="or-search-rows">{total}</b> row{total === 1 ? '' : 's'}</span>
              <span className="text-slate-400">match this search</span>
            </div>
          )}
          {/* Iter 271 — tell the user when the team view is hiding matches */}
          {hiddenByView > 0 && !cancelledOnly &&
            (q || clientFilter || reasonFilter || dateFrom || dateTo || modeFilter || invFilter ||
             noTracking || noInvoice || noSecondName || invoiceStatusFilter || ownerFilter) && (
            <div className="or-tb-full flex flex-wrap items-center gap-2 px-3 py-1.5 rounded-md border border-amber-300 bg-amber-50 text-xs text-amber-800"
                 data-testid="or-hidden-view-note">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span><b>{hiddenByView}</b> row{hiddenByView === 1 ? '' : 's'} matching this search {hiddenByView === 1 ? 'is' : 'are'} hidden by the <b>{teamView === 'completed' ? 'Completed' : teamView === 'pending' ? 'Pending' : 'current'}</b> view (they belong to other invoice states).</span>
              <Button size="sm" variant="outline" className="h-6 px-2 text-[11px] bg-white"
                      onClick={() => setTeamView("all")} data-testid="or-hidden-show-all">
                Show all
              </Button>
            </div>
          )}
          {/* Iter 246 — tiny colour legend for the row tints */}
          <div className="or-tb-full flex items-center gap-3 px-1 pt-1 text-[10px] text-slate-400"
               data-testid="or-color-legend">
            <span className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-pink-200 border border-pink-300" /> Reason pending
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-sky-200 border border-sky-300" /> Invoice assigned
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-indigo-200 border border-indigo-300" /> Selected
            </span>
          </div>
          </MobileSheetWrap>
          </div>{/* /or-toolbar-sticky */}

          <TargetCard refreshKey={workList} isAdmin={isAdmin} />
          <div className="flex flex-wrap items-center gap-2">{!isViewer && <ColourDot user={user} working={workList} onSaved={loadWorking} />}<LeaderboardButton /><JumpToWorkingButton user={user} working={workList} onFocus={(w) => focusInvoice(w.invoice_no, w.invoice_no ? "" : (w.customer_name || ""))} /></div>
          <RunningInvoiceBadge owners={activeOwners} user={user} isAdmin={isAdmin} activeInvoice={invFilter}
            onPick={(inv) => { if (inv) focusInvoice(inv, ""); else { setInvFilter(""); if (prevTeamView.current) setTeamView(prevTeamView.current); prevTeamView.current = null; } }} />
          {/* Iter 222 — Owner Workload Card row + Idle Invoice Nudge.
              Both appear only when there is something to surface, so the
              register stays clean when workloads are balanced. */}
          {(activeOwners.length > 0 || (invoiceStats?.idle_count || 0) > 0) && (
            <div className="px-3 py-2 mt-1 mb-2 rounded-lg border border-slate-200 bg-white flex flex-wrap items-center gap-3"
                 data-testid="or-workload-row">
              {activeOwners.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap text-[11px]"
                     data-testid="or-workload-chips">
                  <span className="font-semibold text-slate-500 mr-1">Open by owner:</span>
                  {activeOwners.slice(0, 8).map(o => {
                    const on = ownerFilter === o.owner_email;
                    return (
                      <button key={o.owner_email} type="button"
                        onClick={() => setOwnerFilter(v => v === o.owner_email ? "" : o.owner_email)}
                        title={(() => {
                          const parts = (o.invoice_breakdown || [])
                            .map(b => `${b.invoice}: ${b.parcels}`)
                            .join(" · ");
                          return `${o.parcels ?? o.invoices} open parcels`
                            + (parts ? ` (${parts})` : "")
                            + (o.running_invoice
                                ? ` · click INV to open ${o.running_invoice}`
                                : "")
                            + " · click name to filter the register";
                        })()}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-medium transition-colors ${
                          on
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'
                        }`}
                        data-testid={`or-workload-${(o.owner_email || '').split('@')[0]}`}>
                        {o.owner_name}
                        {o.running_invoice && (
                          <span className={`font-mono text-[10px] underline decoration-dotted cursor-pointer ${
                            on ? 'text-indigo-100 hover:text-white' : 'text-indigo-500 hover:text-indigo-800'}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openInvoiceManager(o.running_invoice);
                                }}
                                title={`Open invoice ${o.running_invoice} manager`}
                                data-testid={`or-workload-inv-${(o.owner_email || '').split('@')[0]}`}>
                            · {o.running_invoice}
                          </span>
                        )}
                        <span className={`ml-1 rounded-full px-1.5 text-[10px] font-bold ${
                          on ? 'bg-white/30 text-white' : 'bg-white text-indigo-700 border border-indigo-200'
                        }`}>
                          {o.parcels ?? o.invoices}
                        </span>
                      </button>
                    );
                  })}
                  {activeOwners.length > 8 && (
                    <span className="text-slate-400 text-[10px]">
                      +{activeOwners.length - 8} more
                    </span>
                  )}
                </div>
              )}
              {(invoiceStats?.idle_count || 0) > 0 && (
                <button type="button"
                  onClick={() => setInvoiceStatusFilter(f => f === "idle" ? "" : "idle")}
                  className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold transition-colors ${
                    invoiceStatusFilter === "idle"
                      ? 'bg-orange-600 text-white border-orange-600'
                      : 'bg-orange-50 text-orange-800 border-orange-300 hover:bg-orange-100'
                  }`}
                  title={
                    (invoiceStats.idle_invoices || []).slice(0, 6).map(i =>
                      `${i.invoice_no}${i.owner_name ? ' · ' + i.owner_name : ''}`
                    ).join('\n') || 'Active invoices with no update for 2+ days'
                  }
                  data-testid="or-idle-nudge">
                  <AlertTriangle className="w-3 h-3" />
                  {invoiceStats.idle_count} idle 2+ days
                </button>
              )}
            </div>
          )}

          {/* Iter 254 — Upload Preview card (ABOVE the table) + verification result */}
          {uploadPreview && (
            <div className="rounded-md border border-indigo-200 bg-indigo-50/70 p-3 mb-2"
                 data-testid="or-upload-preview">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="text-sm font-semibold text-indigo-900">Upload Preview</div>
                <div className="text-xs text-slate-700">File: <b>{uploadPendingFile?.name}</b></div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="px-2 py-0.5 rounded bg-white border border-indigo-200">Rows Detected: <b>{uploadPreview.rows_detected}</b></span>
                  <span className="px-2 py-0.5 rounded bg-white border border-indigo-200">Invoices Detected: <b>{uploadPreview.invoices_detected}</b></span>
                  <span className="px-2 py-0.5 rounded bg-white border border-indigo-200">Parcels Detected: <b>{uploadPreview.parcels_detected}</b></span>
                </div>
                <div className="ml-auto flex gap-2">
                  <Button size="sm" variant="outline" onClick={cancelPreview} disabled={uploading}
                          data-testid="or-upload-cancel">Cancel</Button>
                  <Button size="sm" onClick={commitUpload} disabled={uploading || uploadPreview.rows_detected === 0}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="or-upload-commit">
                    {uploading ? "Uploading…" : `Upload Data (${uploadPreview.rows_detected} rows)`}
                  </Button>
                </div>
              </div>
              {uploadPreview.warning && (
                <div className="text-xs text-amber-700 mt-1">{uploadPreview.warning}</div>
              )}
            </div>
          )}
          {lastUploadResult && (
            <div className={`rounded-md border p-2 mb-2 text-xs flex items-center gap-3 ${
                lastUploadResult.verified
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                  : 'border-amber-400 bg-amber-50 text-amber-900'}`}
                 data-testid="or-upload-verify">
              {lastUploadResult.verified
                ? <span className="font-semibold">✓ Upload Count Verified</span>
                : <span className="font-semibold">⚠ Upload Count Mismatch</span>}
              <span>detected <b>{lastUploadResult.rows_detected}</b></span>
              <span>· inserted <b>{lastUploadResult.inserted ?? lastUploadResult.rows_inserted}</b></span>
              <span>· invoices <b>{lastUploadResult.invoices_detected}</b></span>
              <span>· parcels <b>{lastUploadResult.parcels_detected}</b></span>
              <button className="ml-auto text-slate-500 hover:text-slate-700"
                      onClick={() => setLastUploadResult(null)} data-testid="or-upload-verify-close">✕</button>
            </div>
          )}

          {multiParcelParcelCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-amber-50 border border-amber-300 text-amber-800 text-[11px] font-medium"
                 data-testid="or-multiparcel-banner">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
              <span>
                Merge guardrail: <b>{multiParcelParcelCount}</b> parcel{multiParcelParcelCount === 1 ? '' : 's'} on
                this page belong to customers with <b>multiple parcels in the same invoice</b>.
                Double-check before generating Pack Team Print / Packing List / Bulk Data —
                every tracking must produce its own label &amp; row.
              </span>
            </div>
          )}

          {isMobile && (
            <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="or-m-pending-strip">
              <PendingServiceReport onOpenInRegister={(b) => { setSvcBucket(b); setTeamView("pending"); setPage(1); }} />
            </div>
          )}
          <div className="md:hidden grid grid-cols-4 gap-1 rounded-lg border border-slate-200 bg-white p-1 mb-2" data-testid="or-m-teamview">
            {[["pending", "Pending", "bg-amber-500"], ["completed", "Done", "bg-emerald-600"], ["all", "All", "bg-slate-700"]].map(([k, l, on]) => (
              <button key={k} type="button" onClick={() => { setTeamView(k); setInvoiceStatusFilter(""); setCancelledOnly(false); setPage(1); }}
                className={`h-8 rounded-md text-[11px] font-semibold transition-colors ${teamView === k && !invoiceStatusFilter && !cancelledOnly ? `${on} text-white` : "text-slate-600 hover:bg-slate-50"}`}
                data-testid={`or-m-teamview-${k}`}>
                {l}{invoiceStats?.[`${k}_parcels`] != null && <span className="ml-0.5 opacity-80">({invoiceStats[`${k}_parcels`]})</span>}
              </button>
            ))}
            <button type="button" onClick={() => { setCancelledOnly(true); setPage(1); }}
              className={`h-8 rounded-md text-[11px] font-semibold transition-colors ${cancelledOnly ? "bg-rose-600 text-white" : "text-rose-700 hover:bg-rose-50"}`}
              data-testid="or-m-teamview-cancelled">
              Cancelled{invoiceStats?.cancelled_rows != null && <span className="ml-0.5 opacity-80">({invoiceStats.cancelled_rows})</span>}
            </button>
          </div>
          <OrMobileCards rows={rows} parcelKey={parcelKey} invOwners={invOwners} onOpen={openEdit} workMap={workMap}
            noteProps={{ readOnly: isViewer, isAdmin, me: user?.id, onChanged: fetchRows }}
            actions={isViewer ? null : {
              cancel: (r) => { setCancelTarget(r); setCancelPassword(""); },
              restore: (r) => { setRestoreTarget(r); setRestorePassword(""); },
              remove: (x) => bulkDelete([x.id]) }} />
          <div className="border rounded-md overflow-auto max-h-[calc(100vh-260px)] hidden md:block" data-testid="or-table-scroll">
            <table className="w-full text-xs border-collapse" data-testid="or-table">
              <thead className="bg-slate-100 text-slate-600 text-[10px] uppercase tracking-wide sticky top-0 z-20 shadow-sm">
                <tr>
                  {!isHidden('select') && (
                    <th className="p-2 w-8 text-center border border-slate-200">
                      <input type="checkbox"
                        checked={rows.length > 0 && rows.every(r => selected.has(r.id))}
                        onChange={toggleSelectAllOnPage}
                        data-testid="or-select-all" />
                    </th>
                  )}
                  {(() => {
                    const SortHead = ({ label, k, align = "left", cls = "" }) => {
                      const active = sortKey === k;
                      const arrow = !active ? "" : sortDir === "asc" ? " ▲" : " ▼";
                      return (
                        <th className={`text-${align} px-2 py-2 border border-slate-200 cursor-pointer select-none hover:bg-slate-200/60 ${active ? 'bg-slate-200/50 text-slate-800' : ''} ${cls}`}
                            onClick={() => cycleSort(k)}
                            data-testid={`or-sort-${k}`}
                            title="Click to sort at the parcel-group level">
                          {label}{arrow}
                        </th>
                      );
                    };
                    return (
                      <>
                        <SortHead label="Date"     k="date" />
                        <SortHead label="Code"     k="code" />
                        <SortHead label="Customer" k="customer_name" />
                        {!isHidden('address') && <th className="p-2 text-left border border-slate-200 w-20">Country</th>}
                        <th className="p-2 text-left border border-slate-200">Medicine</th>
                        {!isHidden('qty') && <th className="p-2 text-right border border-slate-200">Qty</th>}
                        <th className="p-2 text-right border border-slate-200 w-16"
                            title="Strips = ceil(Qty / Packing). Pink = no packing found - double-click to enter manually.">Qty1</th>
                        <SortHead label="Tracking" k="tracking" />
                        {!isHidden('parcelId') && (
                          <th className="p-2 text-left border border-slate-200 w-16"
                              title="Permanent parcel id from the upload — survives late tracking fill">PID</th>
                        )}
                        <SortHead label="Mode"     k="mode" />
                        <th className="p-2 text-left border border-slate-200">Second Name</th>
                        <th className="p-2 text-right border border-slate-200 w-16"
                            title="(Alias ₹ price + margin) ÷ monthly ₹/$ rate">$ Price</th>
                        <th className="p-2 text-left border border-slate-200 w-56">Reason</th>
                        <th className="p-2 text-left border border-slate-200 w-24">Invoice #</th>
                        <th className="p-2 text-center border border-slate-200 w-16">Parcels</th>
                        <th className="p-2 text-center border border-slate-200 w-20">Inv Status</th>
                        {!isHidden('invDate') && <th className="p-2 text-left border border-slate-200 w-24">Inv Date</th>}
                        {!isHidden('notes') && <th className="p-2 text-left border border-slate-200">Notes</th>}
                        {!isHidden('edit') && <th className="p-2 text-right border border-slate-200 w-14">Edit</th>}
                      </>
                    );
                  })()}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Column count adjusts when hide toggles are on so
                  // Loading / Empty rows still span the whole table.
                  const totalCols = 20 - (isHidden('select') ? 1 : 0)
                                      - (isHidden('address') ? 1 : 0)
                                      - (isHidden('edit') ? 1 : 0)
                                      - (isHidden('parcelId') ? 1 : 0)
                                      - (isHidden('qty') ? 1 : 0)
                                      - (isHidden('invDate') ? 1 : 0)
                                      - (isHidden('notes') ? 1 : 0);
                  if (loading && rows.length === 0) return (
                    <tr><td colSpan={totalCols} className="text-center text-slate-500 py-6">
                      <RefreshCw className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                    </td></tr>
                  );
                  if (rows.length === 0) return (
                    <tr><td colSpan={totalCols} className="text-center text-slate-500 py-6" data-testid="or-empty">
                      {isViewer || forbiddenTools.has('or-add')
                        ? <>No orders in your scope yet.</>
                        : <>No orders yet. Click <b>Upload Excel</b> to import or <b>New Order</b> to create manually.</>}
                    </td></tr>
                  );
                  return null;
                })()}
                {rows.length > 0 && (() => {
                  // Bucket rows into parcel groups (date+code+customer+tracking).
                  const groups = [];
                  const gIx = {};
                  for (const r of rows) {
                    const k = parcelKey(r);
                    if (!(k in gIx)) { gIx[k] = groups.length; groups.push({ key: k, rows: [] }); }
                    groups[gIx[k]].rows.push(r);
                  }
                  // Iter 288 — merge-guardrail flags come from the
                  // memoised multiParcelMap (computed above from rows).
                  const multiParcelCount = multiParcelParcelCount;
                  // Group-level sort — every medicine in a group stays glued
                  // together; the group order is what the user re-ranks.
                  if (sortKey) {
                    // Date-aware comparator — rows may carry dd/mm/yyyy,
                    // yyyy-mm-dd, or a legacy "yyyy-mm-dd 00:00:00" stamp;
                    // plain string compare mixes those formats wrongly.
                    const parseOrDate = (v) => {
                      const s = String(v ?? '').trim().split(/[T ]/)[0];
                      let m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
                      if (m) return new Date(+m[3], +m[2] - 1, +m[1]).getTime();
                      m = s.match(/^(\d{4})[\/\-.](\d{1,2})[\/\-.](\d{1,2})/);
                      if (m) return new Date(+m[1], +m[2] - 1, +m[3]).getTime();
                      m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2})$/);
                      if (m) return new Date(2000 + +m[3], +m[2] - 1, +m[1]).getTime();
                      return NaN;
                    };
                    const cmp = (a, b) => {
                      if (a == null || a === '') return 1;
                      if (b == null || b === '') return -1;
                      if (sortKey === 'date') {
                        const ta = parseOrDate(a), tb = parseOrDate(b);
                        if (!isNaN(ta) && !isNaN(tb)) return ta - tb;
                        if (!isNaN(ta)) return -1;
                        if (!isNaN(tb)) return 1;
                      }
                      return String(a).localeCompare(String(b), undefined,
                        { numeric: true, sensitivity: 'base' });
                    };
                    const dir = sortDir === 'desc' ? -1 : 1;
                    groups.sort((g1, g2) => dir * cmp(g1.rows[0][sortKey], g2.rows[0][sortKey]));
                  } else {
                    // Default view: cluster each invoice's parcels at its first position, ordered 01/12, 02/12…
                    const invOf = (g) => (g.rows[0].invoice_no || '').trim().toUpperCase();
                    const byInv = {};
                    groups.forEach(g => { const k = invOf(g); if (k) (byInv[k] = byInv[k] || []).push(g); });
                    const seq = (g) => g.rows.find(x => x.parcel_seq != null)?.parcel_seq ?? Infinity;
                    const ordered = [];
                    const done = new Set();
                    groups.forEach(g => {
                      const k = invOf(g);
                      if (!k) { ordered.push(g); return; }
                      if (done.has(k)) return;
                      done.add(k);
                      ordered.push(...byInv[k].slice().sort((a, b) => seq(a) - seq(b)));
                    });
                    groups.splice(0, groups.length, ...ordered);
                  }
                  const out = [];
                  groups.forEach((g, gIdx) => {
                    const groupTint = gIdx % 2 === 0 ? '' : 'bg-amber-50/40';
                    const working = g.rows.map(x => workMap[x.id]).find(Boolean);
                    g.rows.forEach((r, rIdx) => {
                      const firstInGroup = rIdx === 0;
                      const topBorder = firstInGroup ? 'border-t-2 border-t-slate-400' : '';
                      // Iter 212 - a row with a pending REASON and no
                      // invoice yet is highlighted light pink so the team
                      // spots pending parcels at a glance; the highlight
                      // clears as soon as an invoice number is assigned.
                      const reasonPending = !!((r.reason || '').trim())
                        && !((r.invoice_no || '').trim());
                      // Iter 245 — a row with an invoice number is tinted
                      // light blue so the team can spot assigned orders at
                      // a glance (takes over from the pink pending tint).
                      const invoiced = !!((r.invoice_no || '').trim());
                      const noReasonAged = r.no_reason_age_days != null;
                      const rowCls = `text-slate-800 ${
                        selected.has(r.id) ? 'bg-indigo-50/60'
                        : working ? 'font-semibold text-slate-900 or-working-row'
                        : invoiced ? 'bg-sky-100/70 hover:bg-sky-100'
                        : reasonPending ? 'bg-pink-100/70 hover:bg-pink-100'
                        : noReasonAged ? 'bg-amber-100/80 hover:bg-amber-100'
                        : groupTint}`;
                      const editingTrk = inlineEdit && inlineEdit.id === r.id && inlineEdit.field === 'tracking';
                      const editingSN  = inlineEdit && inlineEdit.id === r.id && inlineEdit.field === 'second_name';
                      const editingReason = inlineEdit && inlineEdit.id === r.id && inlineEdit.field === 'reason';
                      const editingInv    = inlineEdit && inlineEdit.id === r.id && inlineEdit.field === 'invoice_no';
                      const editingInvDt  = inlineEdit && inlineEdit.id === r.id && inlineEdit.field === 'invoice_date';
                      const editingQty1 = inlineEdit && inlineEdit.id === r.id && inlineEdit.field === 'qty1';
                      out.push(
                        <tr key={r.id} className={`${rowCls} ${topBorder}`}
                            style={rowHlStyle(r) || (working ? workLook(working).rowStyle : undefined)}
                            data-working={working ? working.email : undefined}
                            data-testid={`or-row-${r.id}`} data-parcel-key={g.key}
                            data-parcel-first={firstInGroup ? 'true' : 'false'}>
                          {!isHidden('select') && (
                            <td className="p-1.5 text-center border border-slate-200">
                              <input type="checkbox" checked={selected.has(r.id)}
                                onChange={() => toggleSelect(r.id)}
                                data-testid={`or-select-${r.id}`} />
                            </td>
                          )}
                          <td className={`p-1.5 whitespace-nowrap border border-slate-200 ${firstInGroup ? 'font-semibold' : 'text-slate-300'} ${hc(r, firstInGroup, 'date')}`}>
                            {firstInGroup ? (r.date || '—') : ''}
                            {firstInGroup && working && (
                              <div className="mt-0.5"><WorkingTag w={working} testid={`or-working-tag-${r.id}`} /></div>
                            )}
                          </td>
                          <td className={`p-1.5 font-mono text-[10px] font-bold border border-slate-200 ${firstInGroup ? '' : 'text-slate-300'} ${hc(r, firstInGroup, 'code')}`}>
                            {firstInGroup ? (r.code || '—') : ''}
                          </td>
                          <td className={`p-1.5 border border-slate-200 ${firstInGroup ? 'font-medium' : 'text-slate-300'} ${hc(r, firstInGroup, 'customer_name')}`}>
                            {firstInGroup ? (r.customer_name || '—') : ''}
                            {firstInGroup && r.order_of_day && (
                              <span className={`ml-1 inline-block rounded text-[9px] font-bold px-1 py-px align-middle ${r.order_of_day > 1 ? 'bg-fuchsia-600 text-white' : 'bg-fuchsia-100 text-fuchsia-700 border border-fuchsia-300'}`}
                                    title={`This customer has ${r.orders_of_day} separate orders on ${r.date} — keep them as separate parcels`}
                                    data-testid={`or-order-of-day-${r.id}`}>
                                {r.order_of_day === 1 ? '1st' : r.order_of_day === 2 ? '2nd' : r.order_of_day === 3 ? '3rd' : `${r.order_of_day}th`} order
                              </span>
                            )}
                            {firstInGroup && r.view_only && <span title="View-only service — search, note and query only"
                              className="ml-1 rounded bg-sky-100 px-1 text-[9px] font-bold text-sky-700 align-middle" data-testid={`or-view-only-${r.id}`}>VIEW</span>}
                            {firstInGroup && <ParcelNoteBadge row={r} rows={g.rows} readOnly={isViewer} isAdmin={isAdmin} me={user?.id} onChanged={fetchRows} />}
                            {firstInGroup && (r.pquery ? <ParcelQueryBadge q={r.pquery} rowId={r.id} /> : (
                              <button type="button" onClick={() => openParcelQuery(r.id)} title="Open a customer query for this parcel"
                                      className="ml-1 align-middle text-orange-500 opacity-40 hover:opacity-100" data-testid={`or-query-cust-${r.id}`}>
                                <MessageSquareWarning className="inline w-3 h-3" />
                              </button>
                            ))}
                            {firstInGroup && multiParcelMap[g.key] && !r.cancelled && (
                              <span className="ml-1 inline-block rounded bg-amber-500 text-white text-[9px] font-bold px-1 py-px align-middle cursor-help animate-pulse"
                                    title={`Merge guardrail: this customer has ${multiParcelMap[g.key]} parcels (distinct trackings) in invoice ${r.invoice_no}. Double-check before generating Pack Team Print / Packing List / Bulk Data — each parcel must get its own label & row.`}
                                    data-testid={`or-multiparcel-badge-${r.id}`}>
                                ⚠ {multiParcelMap[g.key]} PARCELS
                              </span>
                            )}
                            {firstInGroup && r.cancelled && (
                              <>
                                <span className="ml-1 inline-block rounded bg-rose-600 text-white text-[9px] font-bold px-1 py-px align-middle"
                                      title={`Cancelled by ${r.cancelled_by_name || r.cancelled_by || 'team'}`}
                                      data-testid={`or-cancelled-badge-${r.id}`}>
                                  CANCELLED
                                </span>
                                <button type="button"
                                        className="ml-1 inline-block align-middle text-slate-400 hover:text-emerald-600 transition-colors"
                                        onClick={() => { setRestoreTarget(r); setRestorePassword(""); }}
                                        title="Undo cancel — restore this parcel (asks for your login password)"
                                        data-testid={`or-restore-${r.id}`}>
                                  <Undo2 className="w-3 h-3 inline" />
                                </button>
                              </>
                            )}
                            {firstInGroup && !r.cancelled && !isViewer && (
                              <button type="button"
                                      className="ml-1 inline-block align-middle text-slate-300 hover:text-rose-600 transition-colors"
                                      onClick={() => { setCancelTarget(r); setCancelPassword(""); }}
                                      title="Cancel this parcel (asks for your login password)"
                                      data-testid={`or-cancel-${r.id}`}>
                                <Ban className="w-3 h-3 inline" />
                              </button>
                            )}
                          </td>
                          {!isHidden('address') && (
                            <td className={`p-1.5 w-20 border border-slate-200 ${firstInGroup && r.address ? 'cursor-pointer hover:bg-teal-50/60' : 'text-slate-300'} ${hc(r, firstInGroup, 'country')}`}
                                title={firstInGroup && r.address ? "Double-click to view the complete shipping address" : ""}
                                onDoubleClick={firstInGroup && r.address
                                  ? () => setAddrView({ customer: r.customer_name, address: r.address, phone: r.phone })
                                  : undefined}
                                data-testid={`or-country-${r.id}`}>
                              {firstInGroup ? (
                                r.country_display ? (
                                  <span className="inline-block px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 font-semibold text-[10px]"
                                        data-testid={`or-country-badge-${r.id}`}>
                                    {r.country_display}
                                  </span>
                                ) : <span className="text-slate-400">—</span>
                              ) : ''}
                            </td>
                          )}
                          <td className={`p-1.5 font-medium border border-slate-200 ${hc(r, firstInGroup, 'medicine')}`}>{r.medicine || '—'}</td>
                          {!isHidden('qty') && (
                            <td className={`p-1.5 text-right border border-slate-200 ${hc(r, firstInGroup, 'quantity')}`}>{r.quantity || '—'}</td>
                          )}
                          <td className={`p-1.5 text-right border border-slate-200 cursor-text ${r.qty1 ? 'hover:bg-amber-50/40' : 'bg-pink-200/70 hover:bg-pink-200'} ${hc(r, firstInGroup, 'qty1')}`}
                              title={r.qty1
                                ? (r.qty1_manual
                                  ? 'Qty1 manually entered - double-click to edit'
                                  : 'Qty1 auto = ceil(Qty / Packing) - double-click to override')
                                : 'No packing found for this second name - double-click to enter Qty1 manually'}
                              onDoubleClick={() => beginInline(r, 'qty1')}
                              data-testid={`or-qty1-${r.id}`}>
                            {editingQty1 ? (
                              <Input autoFocus value={inlineValue}
                                onChange={(e) => setInlineValue(e.target.value)}
                                onBlur={commitInline}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') commitInline();
                                  else if (e.key === 'Escape') setInlineEdit(null);
                                }}
                                className="h-6 text-[10px] px-1.5 text-right"
                                data-testid={`or-qty1-edit-${r.id}`} />
                            ) : r.qty1 ? (
                              <span className={`font-bold ${r.qty1_manual ? 'text-pink-700' : 'text-slate-700'}`}
                                    data-testid={`or-qty1-val-${r.id}`}>{r.qty1}</span>
                            ) : (
                              <span className="text-pink-500 italic text-[10px]">-</span>
                            )}
                          </td>
                          <td className={`p-1.5 font-mono text-[10px] border border-slate-200 cursor-text hover:bg-amber-50/40 ${hc(r, firstInGroup, 'tracking')}`}
                              title={firstInGroup ? "Double-click to unlock edit (login password) - click badge to copy" : ""}
                              onDoubleClick={firstInGroup ? () => openLockedEdit(r, 'tracking') : undefined}
                              data-testid={`or-tracking-${r.id}`}>
                            {editingTrk ? (
                              <Input autoFocus value={inlineValue}
                                onChange={(e) => setInlineValue(e.target.value)}
                                onBlur={commitInline}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') commitInline();
                                  else if (e.key === 'Escape') setInlineEdit(null);
                                }}
                                className="h-6 text-[10px] font-mono px-1.5"
                                data-testid={`or-tracking-edit-${r.id}`} />
                            ) : firstInGroup && r.tracking ? (
                              <span className="inline-flex items-center gap-1">
                                <span className="inline-block px-1.5 py-0.5 rounded bg-slate-200 text-slate-500 font-semibold text-[10px] cursor-copy hover:bg-slate-300"
                                      title="Click to copy the tracking number"
                                      onClick={(e) => { e.stopPropagation(); copyTracking(r.tracking); }}
                                      data-testid={`or-tracking-copy-${r.id}`}>
                                  {r.tracking}
                                </span>
                                {trackingConflicts.has(String(r.tracking).toUpperCase()) && (
                                  <span title="Already dispatched — this tracking # exists in Database A"
                                        className="text-emerald-600 font-bold text-[11px]"
                                        data-testid={`or-track-conflict-${r.id}`}>
                                    ✓
                                  </span>
                                )}
                              </span>
                            ) : firstInGroup ? (
                              (isViewer || r.view_only) ? "—" : <span className="text-slate-400 italic">— dbl-click —</span>
                            ) : ''}
                            {firstInGroup && r.dup_flag?.status === 'pending' && (
                              <div className="mt-0.5"><DupFlagBadge row={r} readOnly={isViewer} onDone={() => { setDupRefresh(x => x + 1); fetchRows(); }} /></div>
                            )}
                            {firstInGroup && <HubTrackNote r={r} />}
                          </td>
                          {!isHidden('parcelId') && (
                            <td className="p-1.5 border border-slate-200 text-[9px] font-mono text-slate-400 whitespace-nowrap"
                                data-testid={`or-pid-${r.id}`}>
                              {firstInGroup && r.parcel_ref
                                ? <span title="Permanent parcel id">{r.parcel_ref}</span>
                                : firstInGroup ? <span className="italic">—</span> : ''}
                            </td>
                          )}
                          <td className={`p-1.5 border border-slate-200 ${hc(r, firstInGroup, 'mode')}`}>
                            {firstInGroup && r.mode ? (
                              <span className="inline-block px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-semibold text-[10px]">
                                {r.mode}
                              </span>
                            ) : firstInGroup ? <span className="text-slate-400">—</span> : ''}
                            {firstInGroup && <OutsourcedTag mode={r.mode} testid={`or-outsourced-${r.id}`} />}
                            {firstInGroup && <HandbackTag r={r} />}
                            {firstInGroup && <PhotoMissingTag r={r} onDone={fetchRows} />}
                            {firstInGroup && <HubMoneyTag r={r} />}
                          </td>
                          <td className={`p-1.5 border border-slate-200 cursor-text hover:bg-amber-50/40 relative ${hc(r, firstInGroup, 'second_name')}`}
                              title="Double-click to edit second name — type 3+ letters for alias suggestions"
                              onDoubleClick={() => beginInline(r, 'second_name')}
                              data-testid={`or-secondname-${r.id}`}>
                            {editingSN ? (
                              <div className="relative">
                                <Input autoFocus value={inlineValue}
                                  onChange={(e) => onSecondNameType(e.target.value)}
                                  onBlur={() => commitInline()}
                                  onKeyDown={onSecondNameKey}
                                  className="h-6 text-[10px] px-1.5 uppercase"
                                  data-testid={`or-secondname-edit-${r.id}`} />
                                {snSug.length > 0 && (
                                  <div className="absolute left-0 top-full mt-0.5 z-30 bg-white border border-slate-200 rounded shadow-lg w-64 max-h-44 overflow-auto"
                                       data-testid="or-sn-suggest-list">
                                    {snSug.map((s, i) => (
                                      <button key={s} type="button"
                                        className={`w-full text-left px-2 py-1.5 text-[11px] font-semibold border-b border-slate-100 last:border-0 ${
                                          i === snSugIdx ? 'bg-indigo-100 text-indigo-800'
                                          : 'text-slate-700 hover:bg-indigo-50'
                                        }`}
                                        onMouseEnter={() => setSnSugIdx(i)}
                                        onMouseDown={(e) => { e.preventDefault(); commitInline(s); }}
                                        data-testid={`or-sn-suggest-item-${i}`}>
                                        {s}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : r.second_name ? (() => {
                              const med = (r.medicine || '').trim();
                              const hit = med ? aliasMatches[med] : null;
                              const matches = hit && hit.second_name
                                && hit.second_name.trim().toLowerCase() === r.second_name.trim().toLowerCase();
                              let dotCls = null, dotTitle = '';
                              if (matches && hit.rank === 0) {
                                dotCls = 'bg-emerald-500'; dotTitle = 'Exact alias match';
                              } else if (matches && hit.rank >= 1 && hit.rank <= 3) {
                                dotCls = 'bg-amber-500'; dotTitle = 'Fuzzy alias match';
                              }
                              const showSaveChip = med && (!hit || !hit.second_name
                                || hit.second_name.trim().toLowerCase() !== r.second_name.trim().toLowerCase());
                              return (
                                <span className="inline-flex items-center gap-1.5">
                                  {dotCls && (
                                    <span className={`w-2 h-2 rounded-full ${dotCls}`}
                                          title={dotTitle}
                                          data-testid={`or-alias-chip-${r.id}`} />
                                  )}
                                  <span className="font-semibold text-slate-700">{r.second_name}</span>
                                  {showSaveChip && !isViewer && (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); saveAsAlias(med, r.second_name); }}
                                      className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                                      title={`Save "${med}" → ${r.second_name} as an alias mapping`}
                                      data-testid={`or-save-alias-${r.id}`}>
                                      <BookmarkPlus className="w-2.5 h-2.5" /> Save alias
                                    </button>
                                  )}
                                </span>
                              );
                            })() : (
                              r.alias_missing
                                ? <span className="rounded bg-rose-100 px-1 py-0.5 text-[9px] font-bold text-rose-700" title="Hub order: this name is not in Product Alias — double-click to set the second name, then Save alias so it matches next time" data-testid={`or-alias-missing-${r.id}`}>Not in product list</span>
                                : (isViewer || r.view_only) ? "—" : <span className="text-slate-400 italic">— dbl-click —</span>
                            )}
                          </td>
                          {/* Iter 236 — $ price: (alias ₹ price + margin) ÷ monthly rate */}
                          <td className={`p-1.5 border border-slate-200 text-right ${hc(r, firstInGroup, 'price')}`}
                              data-testid={`or-usdprice-${r.id}`}>
                            {(() => {
                              const server = (typeof r.usd_price === "number") ? r.usd_price : null;
                              const usd = server !== null ? server : (r.second_name ? usdOf(r.second_name) : null);
                              if (usd === null) {
                                return r.second_name
                                  ? <span className="text-slate-300 italic" title="No purchase price in Product Aliases — please update">—</span>
                                  : <span className="text-slate-300">—</span>;
                              }
                              const inr = parseFloat(String(priceMap[normName(r.second_name)] ?? ""));
                              const margin = parseFloat(pricing.margin_pct ?? 5) || 0;
                              const frozen = r.usd_price_frozen === true;
                              return (
                                <span className={`inline-flex items-center gap-1 font-semibold text-[11px] ${
                                        frozen ? "text-slate-700" : "text-emerald-700"}`}
                                      title={frozen
                                        ? "Frozen at invoice completion — future rate changes don't affect this"
                                        : `₹${inr} + ${margin}% = ₹${(inr * (1 + margin / 100)).toFixed(2)} ÷ ₹${pricing.inr_per_usd}/$1`}>
                                  {frozen && <Lock className="w-2.5 h-2.5" />}${usd.toFixed(2)}
                                </span>
                              );
                            })()}
                          </td>
                          {/* Iter 148: Reason. Iter 176: while editing,
                              show a datalist with the 6 seeded reasons
                              PLUS support free text (typed value is
                              saved as-is on blur / Enter). */}
                          <td className={`p-1.5 border border-slate-200 cursor-text hover:bg-amber-50/40 max-w-[240px] ${hc(r, firstInGroup, 'reason')}`}
                              title="Double-click to edit reason"
                              onDoubleClick={() => beginInline(r, 'reason')}
                              data-testid={`or-reason-${r.id}`}>
                            {editingReason ? (
                              <>
                                <Input autoFocus value={inlineValue}
                                  list={`or-reason-list-${r.id}`}
                                  placeholder="Pick or type…"
                                  onChange={(e) => setInlineValue(e.target.value)}
                                  onBlur={commitInline}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') commitInline();
                                    else if (e.key === 'Escape') setInlineEdit(null);
                                  }}
                                  className="h-6 text-[10px] px-1.5"
                                  data-testid={`or-reason-edit-${r.id}`} />
                                <datalist id={`or-reason-list-${r.id}`}>
                                  {reasonSuggestions.map(s => (
                                    <option key={s} value={s} />
                                  ))}
                                </datalist>
                              </>
                            ) : r.reason ? (
                              <span className="inline-flex items-start gap-1">
                                <span className={/urgent/i.test(r.reason) ? "font-bold text-red-600 whitespace-pre-wrap break-words" : "text-slate-700 whitespace-pre-wrap break-words"}
                                      data-testid={/urgent/i.test(r.reason) ? `or-reason-urgent-${r.id}` : undefined}>{r.reason}</span>
                                {r.reason_age_days != null && (
                                  <span className={`mt-0.5 shrink-0 rounded-full px-1.5 text-[9px] font-bold leading-4 ${
                                      r.reason_age_level === "red" ? "bg-red-600 text-white"
                                      : r.reason_age_level === "amber" ? "bg-amber-400 text-amber-900"
                                      : "bg-slate-100 text-slate-500"}`}
                                    title={`Reason set ${r.reason_age_days} day${r.reason_age_days === 1 ? '' : 's'} ago`}
                                    data-testid={`or-reason-age-${r.id}`}>
                                    {r.reason_age_days}d
                                  </span>
                                )}
                              </span>
                            ) : r.no_reason_age_days != null ? (
                              <span className="rounded-full bg-amber-400 text-amber-900 px-1.5 text-[9px] font-bold leading-4"
                                    title={`No reason for ${r.no_reason_age_days} days — double-click to add one`}
                                    data-testid={`or-no-reason-age-${r.id}`}>
                                {r.no_reason_age_days}d no reason
                              </span>
                            ) : (
                              (isViewer || r.view_only) ? "—" : <span className="text-slate-400 italic">— dbl-click —</span>
                            )}
                          </td>
                          <td className={`p-1.5 font-mono text-[10px] border border-slate-200 ${firstInGroup ? 'cursor-pointer hover:bg-indigo-50/60 uppercase' : 'text-slate-300'} ${hc(r, firstInGroup, 'invoice_no')}`}
                              title={firstInGroup
                                ? (r.invoice_no
                                    ? "Double-click to open Invoice Parcel Manager (up to 12 parcels, 30 for Emirates)"
                                    : "Double-click to set invoice # — your running invoice is filled in automatically; press Enter to save (applies to all rows in this parcel)")
                                : ""}
                              onDoubleClick={firstInGroup ? () => {
                                if (r.invoice_no) openInvoiceManager(r.invoice_no, r.customer_name);
                                else beginInvoiceEntry(r);
                              } : undefined}
                              data-testid={`or-invoice-${r.id}`}>
                            {!firstInGroup ? '' : editingInv ? (
                              <Input autoFocus value={inlineValue}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => setInlineValue(e.target.value)}
                                onBlur={commitInline}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') commitInline();
                                  else if (e.key === 'Escape') setInlineEdit(null);
                                }}
                                className="h-6 text-[10px] font-mono px-1.5 uppercase"
                                data-testid={`or-invoice-edit-${r.id}`} />
                            ) : r.invoice_no ? (
                              <span className="inline-flex items-center gap-1">
                                <span className={`inline-block px-1.5 py-0.5 rounded font-semibold ${
                                  r.invoice_status === 'completed'
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {r.invoice_no}
                                </span>
                                {(() => {
                                  const own = invOwners[r.invoice_no.toUpperCase()];
                                  const me = (user?.email || '').toLowerCase();
                                  if (!own || own.status === 'completed') return null;
                                  const mine = (own.owner_email || '').toLowerCase() === me;
                                  return (
                                    <span
                                      className={`text-[9px] px-1 py-[1px] rounded ${
                                        mine
                                          ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                                          : 'bg-rose-50 text-rose-700 border border-rose-200'
                                      }`}
                                      title={`Active invoice owner: ${own.owner_name}`}
                                      data-testid={`or-inv-owner-chip-${r.id}`}>
                                      {mine ? 'You' : `In use by ${own.owner_name}`}
                                    </span>
                                  );
                                })()}
                              </span>
                            ) : (
                              (isViewer || r.view_only) ? "—" : <span className="text-slate-400 italic normal-case">— dbl-click —</span>
                            )}
                          </td>
                          <td className="p-1.5 text-center border border-slate-200"
                              data-testid={`or-parcel-count-${r.id}`}>
                            {firstInGroup && r.parcel_seq != null && (
                              <span className={`inline-block px-1.5 py-0.5 rounded font-semibold text-[10px] ${
                                r.invoice_status === 'completed'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-300'
                              }`}
                                title={`Parcel ${r.parcel_seq} of ${r.parcel_count ?? '—'} used · cap ${r.parcel_total || 12}`}>
                                {String(r.parcel_seq).padStart(2, '0')}/{r.parcel_total || 12}
                              </span>
                            )}
                            {firstInGroup && r.parcel_seq == null && (
                              <span className="inline-block px-1.5 py-0.5 rounded font-semibold text-[10px] bg-slate-100 text-slate-500 ring-1 ring-slate-200"
                                    title="Queue position on this page (un-invoiced parcel — the official number appears once an invoice is assigned)"
                                    data-testid={`or-queue-pos-${r.id}`}>
                                #{gIdx + 1}
                              </span>
                            )}
                          </td>
                          <td className="p-1.5 text-center border border-slate-200"
                              data-testid={`or-inv-status-${r.id}`}>
                            {firstInGroup && r.invoice_status && (
                              <span className={`inline-block whitespace-nowrap px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${
                                r.invoice_status === 'completed'
                                  ? 'bg-emerald-600 text-white'
                                  : 'bg-yellow-400 text-yellow-900'
                              }`}>
                                {r.invoice_status === 'completed' ? 'Completed' : 'In Progress'}
                              </span>
                            )}
                            {firstInGroup && r.invoice_status === 'completed' && r.invoice_no && (
                              <div className="mt-0.5"><ReadyBadge invoiceNo={r.invoice_no} testId={`or-${r.id}`} /></div>
                            )}
                          </td>
                          {!isHidden('invDate') && <td className={`p-1.5 text-[10px] border border-slate-200 ${firstInGroup ? 'cursor-text hover:bg-amber-50/40' : 'text-slate-300'}`}
                              title={firstInGroup
                                ? (r.invoice_date
                                    ? "Double-click to change the invoice date (applies to all rows in this parcel)"
                                    : "Double-click to stamp TODAY's date automatically")
                                : ""}
                              onDoubleClick={firstInGroup ? () => {
                                if (!r.invoice_date) quickSetInvoiceDateToday(r);
                                else beginInline(r, 'invoice_date');
                              } : undefined}
                              data-testid={`or-invdate-${r.id}`}>
                            {!firstInGroup ? '' : editingInvDt ? (
                              <Input autoFocus type="date" value={inlineValue}
                                onChange={(e) => setInlineValue(e.target.value)}
                                onBlur={commitInline}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') commitInline();
                                  else if (e.key === 'Escape') setInlineEdit(null);
                                }}
                                className="h-6 text-[10px] px-1.5"
                                data-testid={`or-invdate-edit-${r.id}`} />
                            ) : r.invoice_date ? (
                              <span className="text-slate-700">{fmtDmy(r.invoice_date)}</span>
                            ) : (
                              (isViewer || r.view_only) ? "—" : <span className="text-slate-400 italic">— dbl-click —</span>
                            )}
                          </td>}
                          {!isHidden('notes') && <td className={`p-1.5 max-w-[160px] truncate border border-slate-200 ${firstInGroup ? '' : 'text-slate-300'} ${hc(r, firstInGroup, 'notes')}`}
                              title={firstInGroup ? r.notes : ''}>
                            {firstInGroup ? (r.notes || '—') : ''}
                          </td>}
                          {!isHidden('edit') && (
                            <td className="p-1.5 text-right border border-slate-200 whitespace-nowrap">
                              {firstInGroup && (
                                <QueryHover rowId={r.id}>
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-orange-600"
                                        onClick={() => openParcelQuery(r.id)}
                                        data-testid={`or-query-${r.id}`}>
                                  <MessageSquareWarning className="w-3 h-3" />
                                </Button>
                                </QueryHover>
                              )}
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0"
                                      onClick={() => openEdit(r)}
                                      title="Edit full order"
                                      data-testid={`or-edit-${r.id}`}>
                                <Pencil className="w-3 h-3" />
                              </Button>
                            </td>
                          )}
                        </tr>
                      );
                    });
                  });
                  return out;
                })()}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-end gap-2 text-xs">
              <span className="text-slate-400" data-testid="or-page-parcels">
                {pageInfo.pageParcels} parcels · {rows.length} rows on this page · parcels never split across pages
              </span>
              <span className="text-slate-500">Page {page} of {totalPages}</span>
              <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={page <= 1}
                      onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="or-prev">
                <ChevronLeft className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={page >= totalPages}
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid="or-next">
                <ChevronRight className="w-3 h-3" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <ReasonPromptHost />
      <QtyPackingPromptHost />
      {/* Iter 188/189/191 — Today's Invoices popup */}
      <Dialog open={todayInvOpen} onOpenChange={setTodayInvOpen}>
        <DialogContent className="max-w-[96vw] w-[96vw] max-h-[92vh] overflow-hidden flex flex-col" data-testid="or-today-invoices-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-4 h-4 text-blue-600" /> Today's Invoices
              <a href={`/today-invoices?from=${encodeURIComponent(todayInvFrom || "")}&to=${encodeURIComponent(todayInvTo || "")}`}
                 target="_blank" rel="noopener noreferrer"
                 className="ml-auto text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 underline underline-offset-2"
                 data-testid="or-today-inv-open-new-tab">
                Open in new tab ↗
              </a>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm flex-1 overflow-y-auto">
            <div className="flex items-center gap-2 flex-wrap">
              <Label className="text-xs">From</Label>
              <Input type="date" value={todayInvFrom}
                onChange={(e) => { setTodayInvFrom(e.target.value); openTodayInvoices(e.target.value, todayInvTo); }}
                className="h-8 w-36" data-testid="or-today-inv-from" />
              <Label className="text-xs">To</Label>
              <Input type="date" value={todayInvTo}
                onChange={(e) => { setTodayInvTo(e.target.value); openTodayInvoices(todayInvFrom, e.target.value); }}
                className="h-8 w-36" data-testid="or-today-inv-to" />
              <Button size="sm" variant="outline" onClick={() => openTodayInvoices()}
                disabled={todayInvBusy} data-testid="or-today-inv-refresh">
                {todayInvBusy
                  ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Loading…</>
                  : <>Refresh</>}
              </Button>
              <Button size="sm" variant="ghost" className="text-slate-500"
                onClick={() => { const y=_isoYesterday(), t=_isoToday(); setTodayInvFrom(y); setTodayInvTo(t); openTodayInvoices(y, t); }}
                data-testid="or-today-inv-today">Y+Today</Button>
              {isAdmin && <MemberPicker data={todayInvData} value={todayInvMember}
                onChange={(m) => { setTodayInvMember(m); openTodayInvoices(undefined, undefined, m); }} />}
              {!isAdmin && <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700" data-testid="or-today-inv-mine">Your invoices only</span>}
              {todayInvData && (
                <span className="ml-auto text-[11px] text-slate-500" data-testid="or-today-inv-date-label">
                  Showing <b>{todayInvData.date_from === todayInvData.date_to
                    ? todayInvData.date_from
                    : `${todayInvData.date_from} → ${todayInvData.date_to}`}</b> (IST) ·
                  <b className="text-slate-700"> {todayInvData.total_parcels ?? 0} parcels</b>
                  {' '}({todayInvData.total_rows ?? 0} rows)
                </span>
              )}
            </div>
            {todayInvData && (
              <>
                {todayInvData.invoices.length === 0 && todayInvData.pending_no_invoice === 0 ? (
                  <div className="rounded border border-slate-200 bg-slate-50 p-4 text-xs text-slate-500 text-center"
                       data-testid="or-today-inv-empty">
                    No invoices in this date range.
                  </div>
                ) : (
                  <>
                  {!isViewer && <DocPrintBar keys={todayInvData.invoices.map(i => i.invoice_no)} sel={todaySel.sel} setSel={todaySel.setSel} testid="or-today-docprint" />}
                  <div className="max-h-[58vh] overflow-y-auto border rounded" data-testid="or-today-inv-list">
                    <table className="w-full text-[12px]">
                      <thead className="bg-slate-100 sticky top-0 z-10">
                        <tr>
                          <th className="px-2 py-1 w-8"></th>
                          <th className="px-2 py-1 text-left">Invoice #</th>
                          <th className="px-2 py-1 text-left w-24">Invoice Date</th>
                          <th className="px-2 py-1 text-left">Client</th>
                          <th className="px-2 py-1 text-left">Company</th>
                          <th className="px-2 py-1 text-left w-20">Service</th>
                          <th className="px-1 py-1 text-center w-8" title="Packing List generated">PL</th>
                          <th className="px-1 py-1 text-center w-8" title="Bulk Data generated">BD</th>
                          <th className="px-1 py-1 text-center w-8" title="Pack Team print generated">PT</th>
                          <th className="px-1 py-1 text-center w-8" title="Bulk Label generated">BL</th>
                          <th className="px-2 py-1 text-center w-20">Completed</th>
                          <th className="px-2 py-1 text-center w-20" title="Parcels of this invoice with no weight in Database A — click to open a sheet">No weight</th>
                          <th className="px-2 py-1 text-center w-20">In Progress</th>
                          <th className="px-2 py-1 text-center w-16">Pending</th>
                        </tr>
                      </thead>
                      <tbody>
                        {todayInvData.invoices.map(i => {
                          const done = i.status === 'completed';
                          return (
                            <tr key={i.invoice_key} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                                onClick={() => { setTodayInvOpen(false); setInvFilter(i.invoice_no); }}
                                title="Filter Order Register to this invoice"
                                data-testid={`or-today-inv-row-${i.invoice_key}`}>
                              <td className="px-2 py-1 text-center" onClick={e => e.stopPropagation()}>
                                {!isViewer && <SelectBox k={i.invoice_no} sel={todaySel.sel} toggle={todaySel.toggle} testid={`or-today-inv-select-${i.invoice_key}`} />}
                              </td>
                              <td className="px-2 py-1 font-mono font-semibold">{i.invoice_no}
                                <ReleasedTag inv={i} />
                                {i.in_flow && (
                                  <span className="ml-1 px-1 rounded bg-blue-100 text-blue-700 text-[9px] font-sans font-semibold"
                                        title={i.flow_only ? "From Invoice Flow (no order rows in range)" : "Present in Invoice Flow for this range"}>Flow</span>
                                )}
                              </td>
                              <td className="px-2 py-1 text-slate-600">{fmtDmy(i.invoice_date) || '—'}</td>
                              <td className="px-2 py-1">{i.customer_name || '—'}</td>
                              <td className="px-2 py-1 text-[11px] text-violet-700 font-medium" data-testid={`or-today-inv-company-${i.invoice_key}`}>{i.company || '—'}</td>
                              <td className="px-2 py-1">{i.mode || '—'}</td>
                              {['packing_list', 'bulk_data', 'pack_team', 'bulk_label'].map(st => (
                                <td key={st} className="px-1 py-1 text-center"
                                    title={fmtStageTooltip(st, i)}
                                    data-testid={`or-today-inv-stage-${st}-${i.invoice_key}`}>
                                  {(i.stages || {})[st]
                                    ? <span className="text-emerald-600 font-bold">✓</span>
                                    : <span className="text-slate-300">—</span>}
                                </td>
                              ))}
                              <td className="px-2 py-1 text-center" data-testid={`or-today-inv-completed-${i.invoice_key}`}>
                                {done
                                  ? <span className="inline-block px-1.5 py-0.5 rounded font-semibold text-[10px] bg-emerald-100 text-emerald-700">{i.parcel_count}</span>
                                  : <span className="text-slate-300">—</span>}
                              </td>
                              <td className="px-2 py-1 text-center"><NoWeightBadge inv={i} /><EmiTrackBadge inv={i} /></td>
                              <td className="px-2 py-1 text-center" data-testid={`or-today-inv-inprogress-${i.invoice_key}`}>
                                {!done
                                  ? <span className="inline-flex items-center gap-1"><span className="inline-block px-1.5 py-0.5 rounded font-semibold text-[10px] bg-yellow-100 text-yellow-800 ring-1 ring-yellow-300">{i.parcel_count}</span>
                                      {isAdmin && <ReleaseInvoiceButton inv={i} onDone={() => openTodayInvoices()} />}</span>
                                  : <span className="text-slate-300">—</span>}
                              </td>
                              <td className="px-2 py-1 text-center text-slate-300">—</td>
                            </tr>
                          );
                        })}
                        {todayInvData.pending_no_invoice > 0 && (
                          <tr className="border-t border-rose-200 bg-rose-50 hover:bg-rose-100 cursor-pointer"
                              onClick={() => { setTodayInvOpen(false); setNoInvoice(true); }}
                              title="Show rows without an invoice number"
                              data-testid="or-today-inv-pending-row">
                            <td className="px-2 py-1 font-semibold text-rose-700" colSpan={10}>No Invoice (pending)</td>
                            <td className="px-2 py-1 text-center text-slate-300">—</td>
                            <td className="px-2 py-1 text-center text-slate-300">—</td>
                            <td className="px-2 py-1 text-center text-slate-300">—</td>
                            <td className="px-2 py-1 text-center" data-testid="or-today-inv-pending-count">
                              <span className="inline-block px-1.5 py-0.5 rounded font-semibold text-[10px] bg-rose-600 text-white">
                                {todayInvData.pending_no_invoice}
                              </span>
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot className="bg-slate-50 border-t border-slate-200 sticky bottom-0" data-testid="or-today-inv-totals">
                        <tr className="text-[10px] font-bold text-slate-600 uppercase">
                          <td className="px-2 py-1" colSpan={10}>
                            Total · {todayInvData.total_invoices} invoice{todayInvData.total_invoices === 1 ? '' : 's'}
                            {' '}· {todayInvData.total_parcels ?? 0} parcel{(todayInvData.total_parcels ?? 0) === 1 ? '' : 's'}
                          </td>
                          <td className="px-2 py-1 text-center text-emerald-700">{todayInvData.completed}</td>
                          <td className="px-2 py-1 text-center text-rose-700" data-testid="or-today-inv-nowt-total">{todayInvData.invoices.filter(i => i.weight?.missing).length || ""}</td>
                          <td className="px-2 py-1 text-center text-yellow-800">{todayInvData.in_progress}</td>
                          <td className="px-2 py-1 text-center text-rose-700">{todayInvData.pending_no_invoice}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  </>
                )}
                <CorrectionsReport member={isAdmin ? todayInvMember : ""} />
                {(todayInvData.pending_hourly_slots || []).length > 0 && (
                  <div className="border rounded" data-testid="or-today-inv-slots">
                    <div className="px-2 py-1 bg-amber-50 border-b border-amber-200 text-[11px] font-bold text-amber-800 flex items-center justify-between flex-wrap gap-2">
                      <span>Pending Hourly Slots (IST) — {todayInvData.pending_hourly_slots.length} slot{todayInvData.pending_hourly_slots.length === 1 ? '' : 's'}</span>
                      <div className="flex items-center gap-1 font-normal">
                        <button
                          onClick={() => setPendingSlotMode(m => m === 'detailed' ? 'summary' : 'detailed')}
                          className="px-2 py-0.5 rounded border border-amber-300 bg-white text-amber-800 text-[10px] hover:bg-amber-50"
                          data-testid="or-slot-mode-toggle">
                          {pendingSlotMode === 'detailed' ? '📋 Detailed' : '📊 Summary'}
                        </button>
                      </div>
                    </div>
                    <div className="max-h-[30vh] overflow-y-auto">
                      <table className="w-full text-[11px]">
                        <thead className="bg-amber-100/60 sticky top-0">
                          <tr>
                            <th className="px-2 py-1 text-left w-24">Date</th>
                            <th className="px-2 py-1 text-left w-24">Slot (IST)</th>
                            <th className="px-2 py-1 text-center w-16">Parcels</th>
                            {pendingSlotMode === 'detailed' && (
                              <th className="px-2 py-1 text-left">Trackings / Customer</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {todayInvData.pending_hourly_slots.map((s, idx) => (
                            <tr key={idx} className="border-t border-amber-100 hover:bg-amber-50"
                                data-testid={`or-slot-${idx}`}>
                              <td className="px-2 py-1 font-mono">{s.date}</td>
                              <td className="px-2 py-1 font-semibold">{s.label}</td>
                              <td className="px-2 py-1 text-center">
                                <span className="inline-block px-1.5 py-0.5 rounded font-semibold text-[10px] bg-amber-600 text-white">
                                  {s.count}
                                </span>
                              </td>
                              {pendingSlotMode === 'detailed' && (
                                <td className="px-2 py-1 text-[10px] font-mono">
                                  {(s.parcels || []).slice(0, 6).map((p, i) => (
                                    <div key={i} className="truncate">
                                      {(p.trackings || []).length
                                        ? p.trackings.join(', ')
                                        : `${p.code || '?'} · ${p.customer_name || '?'} · ${p.country || '?'}`}
                                    </div>
                                  ))}
                                  {(s.parcels || []).length > 6 && (
                                    <div className="text-slate-400">+{s.parcels.length - 6} more…</div>
                                  )}
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                <ReleaseHistory data={todayInvData} />
                {(todayInvData.pending_items || []).length > 0 && (
                  <div className="border rounded" data-testid="or-today-inv-pending-items">
                    <div className="px-2 py-1 bg-rose-50 border-b border-rose-200 text-[11px] font-bold text-rose-700 flex items-center justify-between">
                      <span>Pending Parcels ({todayInvData.pending_no_invoice}) — no invoice yet</span>
                      <span className="font-normal text-rose-500">
                        {todayInvData.pending_rows_total ?? 0} product row{(todayInvData.pending_rows_total ?? 0) === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="max-h-[30vh] overflow-y-auto">
                      <table className="w-full text-[11px]">
                        <thead className="bg-rose-100/60 sticky top-0">
                          <tr>
                            <th className="px-2 py-1 text-left w-24">Code</th>
                            <th className="px-2 py-1 text-left">Customer</th>
                            <th className="px-2 py-1 text-left w-28">Country</th>
                            <th className="px-2 py-1 text-left">Tracking(s)</th>
                            <th className="px-2 py-1 text-center w-16">Rows</th>
                          </tr>
                        </thead>
                        <tbody>
                          {todayInvData.pending_items.map((p, idx) => (
                            <tr key={idx} className="border-t border-rose-100 hover:bg-rose-50"
                                data-testid={`or-today-inv-pending-item-${idx}`}>
                              <td className="px-2 py-1 font-mono font-semibold">{p.code || '—'}</td>
                              <td className="px-2 py-1">{p.customer_name || '—'}</td>
                              <td className="px-2 py-1">{p.country || '—'}</td>
                              <td className="px-2 py-1 font-mono text-[10px]">
                                {(p.trackings || []).length > 0
                                  ? p.trackings.join(', ')
                                  : <span className="text-slate-400 italic">no tracking</span>}
                              </td>
                              <td className="px-2 py-1 text-center text-slate-600">{p.row_count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                <p className="text-[10px] text-slate-400">Click an invoice row to filter the Order Register · click the pending row to show no-invoice rows.</p>
              </>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button size="sm" variant="outline" onClick={() => downloadInvoicesReport("xlsx")}
                    disabled={!todayInvData || todayInvBusy}
                    title="Download this table as Excel (includes a Pending Parcels sheet)"
                    data-testid="or-today-inv-export-excel">
              <Download className="w-3.5 h-3.5 mr-1" /> Excel
            </Button>
            <Button size="sm" variant="outline" onClick={() => downloadInvoicesReport("pdf")}
                    disabled={!todayInvData || todayInvBusy}
                    title="Download this table as PDF"
                    data-testid="or-today-inv-export-pdf">
              <Download className="w-3.5 h-3.5 mr-1" /> PDF
            </Button>
            <Button variant="ghost" onClick={() => setTodayInvOpen(false)}
                    data-testid="or-today-inv-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 199 — Supervisor guard dialog for invoice-number changes */}
      <Dialog open={invGuardOpen} onOpenChange={setInvGuardOpen}>
        <DialogContent className="max-w-sm" data-testid="or-inv-guard-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Lock className="w-4 h-4 text-indigo-600" /> Password Required
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-600">
            You are changing an existing invoice number. Enter the supervisor
            password <b>or your own login password</b> to continue. The change
            is recorded in the Audit Log.
          </p>
          <Input type="password" autoComplete="new-password" value={invGuardPwd}
            onChange={(e) => setInvGuardPwd(e.target.value)}
            placeholder="Supervisor or your login password" autoFocus
            data-testid="or-inv-guard-password" />
          <DialogFooter className="gap-2">
            <Button variant="ghost"
              onClick={() => { setInvGuardOpen(false); setInvGuardPwd(""); }}
              data-testid="or-inv-guard-cancel">Cancel</Button>
            <Button onClick={() => saveDraft(invGuardPwd)}
              disabled={saving || !invGuardPwd}
              data-testid="or-inv-guard-apply">
              Save with password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 238 — Bulk price update (writes back to Product Aliases) */}
      <Dialog open={bulkPriceOpen} onOpenChange={setBulkPriceOpen}>
        <DialogContent className="max-w-md" data-testid="or-bulk-price-dialog">
          <DialogHeader>
            <DialogTitle>Update Purchase Prices (₹)</DialogTitle>
            <DialogDescription className="text-xs">
              These products have no price in Product Aliases. Enter the ₹ purchase price for
              each — saved straight back to Product Aliases so $ Price fills in everywhere.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-1.5 pr-1">
            {unpricedNames.length === 0 ? (
              <p className="text-sm text-slate-500 italic py-4 text-center"
                 data-testid="or-bulk-price-empty">
                All visible products already have a price.
              </p>
            ) : unpricedNames.map((sn, i) => (
              <div key={sn} className="grid grid-cols-[1fr_110px] items-center gap-2"
                   data-testid={`or-bulk-price-row-${i}`}>
                <span className="text-xs font-semibold text-slate-700 truncate" title={sn}>{sn}</span>
                <Input type="number" step="0.01" min="0" placeholder="₹ price"
                       className="h-7 text-xs text-right"
                       value={bulkPriceDrafts[sn] ?? ""}
                       onChange={(e) => setBulkPriceDrafts(d => ({ ...d, [sn]: e.target.value }))}
                       data-testid={`or-bulk-price-input-${i}`} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkPriceOpen(false)}>Cancel</Button>
            <Button onClick={saveBulkPrices}
                    disabled={bulkPriceSaving || unpricedNames.length === 0}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="or-bulk-price-save">
              {bulkPriceSaving && <RefreshCw className="w-3 h-3 mr-1 animate-spin" />}
              Save to Aliases
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 236 — Monthly ₹→$ rate + margin settings */}
      <Dialog open={pricingOpen} onOpenChange={setPricingOpen}>
        <DialogContent className="max-w-sm" data-testid="or-pricing-dialog">
          <DialogHeader>
            <DialogTitle>Monthly $ Price Settings</DialogTitle>
            <DialogDescription className="text-xs">
              Base ₹ price comes from Product Aliases. $ Price = (₹ + margin) ÷ monthly rate.
              Set the rate at the start of each month.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">INR per 1 USD (this month)</Label>
              <Input type="number" step="0.01" value={pricingDraft.inr_per_usd}
                     onChange={(e) => setPricingDraft(d => ({ ...d, inr_per_usd: e.target.value }))}
                     placeholder="95" data-testid="or-pricing-rate-input" />
            </div>
            <div>
              <Label className="text-xs">Margin % (default 5)</Label>
              <Input type="number" step="0.1" value={pricingDraft.margin_pct}
                     onChange={(e) => setPricingDraft(d => ({ ...d, margin_pct: e.target.value }))}
                     placeholder="5" data-testid="or-pricing-margin-input" />
            </div>
            {!isAdmin && (
              <div>
                <Label className="text-xs">Supervisor password</Label>
                <Input type="password" autoComplete="new-password" value={pricingDraft.guard_password}
                       onChange={(e) => setPricingDraft(d => ({ ...d, guard_password: e.target.value }))}
                       placeholder="Required for team members"
                       data-testid="or-pricing-password" />
              </div>
            )}
            {pricing.updated_at && (
              <p className="text-[10px] text-slate-400" data-testid="or-pricing-meta">
                Last set by {pricing.updated_by} · {String(pricing.updated_at).slice(0, 10)}
                {pricing.month ? ` · rate month ${pricing.month}` : ""}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPricingOpen(false)}>Cancel</Button>
            <Button onClick={savePricing} disabled={pricingSaving}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="or-pricing-save">
              {pricingSaving && <RefreshCw className="w-3 h-3 mr-1 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 210 — Locked edit (tracking + new invoice #) with own login password */}
      <Dialog open={lockOpen} onOpenChange={(o) => { setLockOpen(o); if (!o) { setLockPwd(""); setLockErr(""); setLockTarget(null); } }}>
        <DialogContent className="max-w-sm" data-testid="or-locked-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Lock className="w-4 h-4 text-rose-600" />
              {lockTarget?.field === 'tracking' ? 'Unlock tracking edit'
                : lockTarget?.field === 'invoice_no' ? 'Unlock invoice # assignment'
                : 'Unlock row edit'}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Enter your login password <b>or the supervisor password</b> to
              edit this field. The change will be recorded in the Audit Log
              against your account. Stays unlocked for 10 minutes.
            </DialogDescription>
          </DialogHeader>
          <Input type="password" autoComplete="new-password" value={lockPwd} autoFocus
            onChange={(e) => { setLockPwd(e.target.value); setLockErr(""); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && lockPwd) confirmLockedEdit(); }}
            placeholder="Login or supervisor password"
            data-testid="or-locked-edit-password" />
          {lockErr && (
            <div className="text-xs text-rose-600" data-testid="or-locked-edit-error">{lockErr}</div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="ghost"
              onClick={() => { setLockOpen(false); setLockPwd(""); setLockErr(""); setLockTarget(null); }}
              data-testid="or-locked-edit-cancel">Cancel</Button>
            <Button onClick={confirmLockedEdit} disabled={!lockPwd}
              data-testid="or-locked-edit-unlock">Unlock &amp; edit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 205 - Scoped bulk delete */}
      <Dialog open={delScopeOpen} onOpenChange={setDelScopeOpen}>
        <DialogContent className="max-w-md" data-testid="or-delete-scope-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              Delete rows — {{ filtered: 'All filtered results (current view)',
                                 date: 'Date-wise', client: 'Client-wise',
                                 country: 'Country-wise',
                                 all: 'All data (every date)' }[delScope]}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {delScope === 'filtered' && (
              <p className="text-xs text-slate-500">
                Uses your current toolbar filters (search, mode, client, reason,
                date range, invoice, blank-field presets). Invoice-status card
                filters are not included.
              </p>
            )}
            {delScope === 'date' && (
              <div className="flex items-center gap-2">
                <Input type="date" value={delDateFrom}
                  onChange={(e) => { setDelDateFrom(e.target.value); setDelPreview(null); }}
                  className="h-9" data-testid="or-delscope-date-from" />
                <span className="text-xs text-slate-400">to</span>
                <Input type="date" value={delDateTo}
                  onChange={(e) => { setDelDateTo(e.target.value); setDelPreview(null); }}
                  className="h-9" data-testid="or-delscope-date-to" />
              </div>
            )}
            {delScope === 'client' && (
              <Input placeholder="Client code (e.g. BHAMUK)" value={delClient}
                onChange={(e) => { setDelClient(e.target.value.toUpperCase()); setDelPreview(null); }}
                className="h-9" data-testid="or-delscope-client" />
            )}
            {delScope === 'country' && (
              <Select value={delCountry}
                onValueChange={(v) => { setDelCountry(v); setDelPreview(null); }}>
                <SelectTrigger className="h-9" data-testid="or-delscope-country">
                  <SelectValue placeholder="Pick country group" />
                </SelectTrigger>
                <SelectContent>
                  {countryGroups.map(g => (
                    <SelectItem key={g} value={g}>{g.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {delScope === 'all' && (
              <p className="text-xs text-red-600 font-semibold">
                Danger zone — this deletes EVERY row in the Order Register,
                all dates. Deleted rows stay restorable for 24 hours via
                "Recover deleted".
              </p>
            )}
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={previewScopeDelete}
                disabled={delBusy} data-testid="or-delscope-preview">
                {delBusy ? 'Working…' : 'Preview count'}
              </Button>
              {delPreview !== null && (
                <span className={`text-xs font-semibold ${delPreview ? 'text-red-600' : 'text-slate-500'}`}
                      data-testid="or-delscope-count">
                  {delPreviewParcels != null && `${delPreviewParcels} parcel${delPreviewParcels === 1 ? '' : 's'} · `}
                  {delPreview} row{delPreview === 1 ? '' : 's'} will be deleted
                </span>
              )}
            </div>
            <Button size="sm" variant="outline" className="w-full border-sky-300 text-sky-700 hover:bg-sky-50"
              disabled={!scopeFilterValid()}
              onClick={() => {
                const { all, ...p } = buildScopeFilter();
                openScreenView({ ...p, include_cancelled: true, layout: 'single_track' },
                  `DELETE PREVIEW — ${({ filtered: 'Filtered view', date: 'Date-wise', client: `Client ${delClient.trim().toUpperCase()}`, country: `Country ${delCountry}`, all: 'ALL DATA' })[delScope]}`);
              }}
              title="Opens the exact rows this delete would remove in a new tab — no password needed"
              data-testid="or-delscope-preview-tab">
              <ExternalLink className="w-4 h-4 mr-1" /> Open preview in new tab (no password)
            </Button>
            <Input type="password" autoComplete="new-password" placeholder="Supervisor password"
              value={delPassword} onChange={(e) => setDelPassword(e.target.value)}
              className="h-9" data-testid="or-delscope-password" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDelScopeOpen(false)}
              data-testid="or-delscope-cancel">Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700"
              disabled={delBusy || delPreview === null || !delPreview || !delPassword.trim()}
              onClick={confirmScopeDelete} data-testid="or-delscope-confirm">
              Delete {delPreview !== null ? `(${delPreview})` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 208 - Scoped export */}
      <Dialog open={expScopeOpen} onOpenChange={setExpScopeOpen}>
        <DialogContent className="max-w-md" data-testid="or-export-scope-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              Export rows — {SCOPE_TITLES[expScope]}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Downloads an Excel of the rows matching the chosen scope.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {expScope === 'filtered' && (
              <p className="text-xs text-slate-500">
                Uses your current toolbar filters (search, mode, client,
                reason, date range, invoice, blank-field presets).
              </p>
            )}
            {expScope !== 'filtered' && (
              <div className="flex items-center gap-2">
                <Input type="date" value={expDateFrom}
                  onChange={(e) => { setExpDateFrom(e.target.value); setExpPreview(null); }}
                  className="h-9" data-testid="or-expscope-date-from" />
                <span className="text-xs text-slate-400">to</span>
                <Input type="date" value={expDateTo}
                  onChange={(e) => { setExpDateTo(e.target.value); setExpPreview(null); }}
                  className="h-9" data-testid="or-expscope-date-to" />
              </div>
            )}
            {expScope !== 'filtered' && expScope !== 'date' && (
              <p className="text-[11px] text-slate-400 -mt-1">Date range is optional{expScope === 'date' ? '' : ' — leave blank for all dates'}.</p>
            )}
            {(expScope === 'client' || expScope === 'single_track') && (
              <ClientCodeInput value={expClient}
                placeholder={expScope === 'single_track' ? "Client code (optional)" : "Client code (e.g. BHAMUK)"}
                onChange={(v) => { setExpClient(v); setExpPreview(null); }}
                className="h-9" testid="or-expscope-client" />
            )}
            {expScope === 'all_clients' && (
              <p className="text-xs text-slate-500">Every client code in one sheet, sorted client-wise (then customer, newest date).</p>
            )}
            {expScope !== 'filtered' && (
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <span>View:</span>
                <Select value={expView || "__all__"} onValueChange={(v) => { setExpView(v === "__all__" ? "" : v); setExpPreview(null); }}>
                  <SelectTrigger className="h-8 w-44" data-testid="or-expscope-view">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All parcels</SelectItem>
                    <SelectItem value="pending">Pending only</SelectItem>
                    <SelectItem value="in_progress">In-progress invoices</SelectItem>
                    <SelectItem value="completed">Completed only</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-[11px] text-slate-400">defaults to your current table view</span>
              </div>
            )}
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={expSingle} onChange={(e) => setExpSingle(e.target.checked)}
                     data-testid="or-expscope-single-track" />
              Single Track Data layout — one code &amp; one tracking per parcel (like the screen)
            </label>
            {expScope === 'country' && (
              <Select value={expCountry}
                onValueChange={(v) => { setExpCountry(v); setExpPreview(null); }}>
                <SelectTrigger className="h-9" data-testid="or-expscope-country">
                  <SelectValue placeholder="Pick country group" />
                </SelectTrigger>
                <SelectContent>
                  {countryGroups.map(g => (
                    <SelectItem key={g} value={g}>{g.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {expScope === 'all' && (
              <p className="text-xs text-slate-500">
                Exports every row in the Order Register, all dates.
              </p>
            )}
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={previewScopeExport}
                disabled={expBusy} data-testid="or-expscope-preview">
                {expBusy ? 'Working…' : 'Check count'}
              </Button>
              {expPreview !== null && (
                <span className="text-xs font-semibold text-slate-600"
                      data-testid="or-expscope-count">
                  {expPreview} row{expPreview === 1 ? '' : 's'} will be exported
                </span>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setExpScopeOpen(false)}
              data-testid="or-expscope-cancel">Cancel</Button>
            <Button variant="outline" className="border-sky-300 text-sky-700 hover:bg-sky-50"
              disabled={expBusy || !expScopeValid()}
              onClick={() => openScreenView(buildExportParams(), SCOPE_TITLES[expScope])}
              data-testid="or-expscope-screen-view">
              <ExternalLink className="w-4 h-4 mr-1" /> Screen View
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700"
              disabled={expBusy || !expScopeValid()}
              onClick={confirmScopeExport} data-testid="or-expscope-download">
              <Download className="w-4 h-4 mr-1" /> Download Excel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 186 — Complete Shipping Address viewer */}
      <Dialog open={!!addrView} onOpenChange={(o) => { if (!o) setAddrView(null); }}>
        <DialogContent className="max-w-md" data-testid="or-address-dialog">
          <DialogHeader>
            <DialogTitle>Complete Shipping Address</DialogTitle>
          </DialogHeader>
          {addrView && (
            <div className="space-y-3 text-sm">
              <div>
                <span className="text-xs text-slate-500">Customer</span>
                <p className="font-semibold" data-testid="or-addr-customer">{addrView.customer || "—"}</p>
              </div>
              <div>
                <span className="text-xs text-slate-500">Address</span>
                <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed whitespace-pre-wrap"
                     data-testid="or-addr-full">
                  {addrView.address}
                </div>
                <div className="mt-2 space-y-0.5 text-[11px] text-slate-600" data-testid="or-addr-lines">
                  {String(addrView.address).split(",").map(s => s.trim()).filter(Boolean).map((line, i) => (
                    <p key={i} className="flex gap-2">
                      <span className="text-slate-400 w-4 shrink-0">{i + 1}.</span>{line}
                    </p>
                  ))}
                </div>
              </div>
              {addrView.phone && (
                <div>
                  <span className="text-xs text-slate-500">Phone</span>
                  <p className="font-mono" data-testid="or-addr-phone">{addrView.phone}</p>
                </div>
              )}
              <p className="text-[10px] text-slate-400">Read-only view — use the pencil icon on the row to edit the address.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddrView(null)} data-testid="or-addr-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 183 — Invoice Parcel Manager (double-click Invoice column) */}
      <Dialog open={!!invMgr} onOpenChange={(o) => { if (!o) setInvMgr(null); }}>
        <DialogContent className="max-w-lg" data-testid="or-invoice-manager">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Invoice Parcel Manager
              {invMgr?.derived && (
                <span className="text-[10px] font-normal text-slate-400">(prefilled from register rows)</span>
              )}
            </DialogTitle>
          </DialogHeader>
          {invMgr && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 rounded border border-slate-200 bg-slate-50 p-2.5 text-xs"
                   data-testid="or-inv-header">
                <div>
                  <span className="text-slate-500">Invoice</span>
                  <p className="font-mono font-semibold text-sm" data-testid="or-inv-no">{invMgr.invoice_no}</p>
                </div>
                <div>
                  <span className="text-slate-500">Client</span>
                  <p className="font-semibold" data-testid="or-inv-client">{invMgr.client_name || "—"}</p>
                </div>
                <div>
                  <span className="text-slate-500">Parcels</span>
                  <p className={`font-semibold ${invMgrStatus === 'completed' ? 'text-emerald-700' : 'text-amber-700'}`}
                     data-testid="or-inv-count">
                    {invMgr.total_parcel_groups ?? invMgr.parcels?.length ?? 0} / {invMgr.parcel_total || MAX_PARCELS}
                    {(invMgr.total_parcel_groups ?? 0) === (invMgr.parcel_total || MAX_PARCELS) - 1 && (
                      <span className="ml-1 text-amber-600 font-semibold"
                            data-testid="or-inv-count-nearly-full">· almost full</span>
                    )}
                  </p>
                </div>
                <div>
                  <span className="text-slate-500">Status</span>
                  <p className={`font-semibold ${invMgrStatus === 'completed' ? 'text-emerald-700' : 'text-amber-600'}`}
                     data-testid="or-inv-status-label">
                    {invMgrStatus === "completed" ? "Completed" : "In Progress"}
                  </p>
                </div>
              </div>

              {invMgr.over_limit && (
                <div className="rounded border-2 border-red-500 bg-red-50 text-red-700 p-2.5 text-xs"
                     data-testid="or-inv-limit-alert">
                  <p className="font-bold">Maximum Parcel Limit Reached</p>
                  <p>This invoice has {invMgr.total_parcel_groups} parcel groups — maximum allowed in one invoice is {invMgr.parcel_total || MAX_PARCELS} (30 for Emirates). Split the extra rows into a new invoice.</p>
                </div>
              )}

              {/* Iter 220 — active-invoice ownership badge + admin handover */}
              {invMgrStatus !== 'completed' && (invMgr.owner_name || invMgr.owner_email) && (
                <div className="rounded border border-indigo-200 bg-indigo-50/60 p-2 text-xs flex items-center justify-between"
                     data-testid="or-inv-owner-banner">
                  <span className="text-slate-700">
                    <span className="text-slate-500">Active owner:&nbsp;</span>
                    <strong data-testid="or-inv-owner-name">{invMgr.owner_name || invMgr.owner_email || 'No one (free)'}</strong>
                    {(invMgr.owner_email || '').toLowerCase() === (user?.email || '').toLowerCase() && (
                      <span className="ml-1 text-[10px] text-indigo-600 font-semibold">(you)</span>
                    )}
                  </span>
                  {isAdmin && (
                    <div className="flex items-center gap-1.5" data-testid="or-inv-handover">
                      <select
                        className="h-7 text-xs border border-slate-300 rounded px-1.5 bg-white"
                        value={handoverTo}
                        onChange={(e) => setHandoverTo(e.target.value)}
                        disabled={handoverBusy}
                        data-testid="or-inv-handover-select">
                        <option value="">Transfer to…</option>
                        <option value="__release__" data-testid="or-inv-release-option">No one — release ownership</option>
                        {teamMembers
                          .filter(u => u.id !== invMgr.owner_id)
                          .map(u => (
                            <option key={u.id} value={u.id}>
                              {(u.name || u.email)}{u.team ? ` · ${u.team}` : ''}
                            </option>
                          ))}
                      </select>
                      <Button size="sm" className="h-7 text-xs shrink-0"
                        disabled={!handoverTo || handoverBusy}
                        onClick={runHandover}
                        data-testid="or-inv-handover-apply">
                        {handoverBusy ? 'Working…' : handoverTo === "__release__" ? 'Release' : 'Transfer'}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <div className="rounded border border-slate-200 bg-blue-50/60 p-2 text-[11px] text-slate-600">
                Parcels are filled automatically from the register rows sharing this invoice
                (one parcel per Code + Customer + Country). Edit the rows to change them.
              </div>

              {!isViewer && (<div className="rounded border border-indigo-200 bg-indigo-50/50 p-2 space-y-1.5"
                   data-testid="or-inv-rename">
                <p className="text-[10px] font-semibold text-indigo-700 uppercase">
                  Change Invoice #
                </p>
                <div className="flex gap-2">
                  <Input value={invRename}
                    onChange={(e) => setInvRename(e.target.value.toUpperCase())}
                    placeholder="New invoice number" className="h-7 text-xs"
                    data-testid="or-inv-rename-input" />
                  <Input type="password" autoComplete="new-password" value={invRenamePwd}
                    onChange={(e) => setInvRenamePwd(e.target.value)}
                    placeholder="Your login or supervisor password" className="h-7 text-xs"
                    data-testid="or-inv-rename-password" />
                  <Button size="sm" className="h-7 text-xs shrink-0"
                    disabled={invMgrBusy || !invRename.trim() || !invRenamePwd}
                    onClick={renameInvoice}
                    data-testid="or-inv-rename-apply">
                    Apply
                  </Button>
                </div>
                <p className="text-[10px] text-indigo-500">
                  Renames every register row on this invoice. Your own login password
                  works; the change is recorded in the Audit Log with your name.
                </p>
                </div>)}

              <div className="max-h-64 overflow-y-auto border rounded" data-testid="or-inv-slots">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left w-16">Parcel No.</th>
                      <th className="px-2 py-1 text-left w-24">Code</th>
                      <th className="px-2 py-1 text-left">Customer Name</th>
                      <th className="px-2 py-1 text-left w-28">Country</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(invMgr.parcels || []).map(p => (
                      <tr key={p.no} className="border-t border-slate-100"
                          data-testid={`or-inv-slot-${p.no}`}>
                        <td className="px-2 py-1 font-mono font-semibold" data-testid={`or-inv-slot-no-${p.no}`}>{p.no}</td>
                        <td className="px-2 py-1 font-mono" data-testid={`or-inv-slot-code-${p.no}`}>{p.code || "—"}</td>
                        <td className="px-2 py-1" data-testid={`or-inv-slot-customer-${p.no}`}>{p.customer_name || "—"}</td>
                        <td className="px-2 py-1" data-testid={`or-inv-slot-country-${p.no}`}>{p.country || "—"}</td>
                      </tr>
                    ))}
                    {(!invMgr.parcels || invMgr.parcels.length === 0) && (
                      <tr><td colSpan={4} className="text-center text-slate-400 py-4"
                              data-testid="or-inv-slots-empty">
                        No register rows carry this invoice number yet.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-end gap-2">
                <Label className="text-xs">Invoice Status</Label>
                <Select value={invMgrStatus} onValueChange={setInvMgrStatus} disabled={invMgrBusy || isViewer}>
                  <SelectTrigger className="w-36 h-8" data-testid="or-inv-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_progress" data-testid="or-inv-status-in-progress">In Progress</SelectItem>
                    <SelectItem value="completed" data-testid="or-inv-status-completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInvMgr(null)}
                    data-testid="or-inv-cancel">Cancel</Button>
            {!isViewer && <Button onClick={saveInvoiceManager} disabled={invMgrBusy}
              className="bg-indigo-600 hover:bg-indigo-700" data-testid="or-inv-save">
              {invMgrBusy ? "Saving…" : "Save"}
            </Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 182 — Recover deleted (24h) */}
      <Dialog open={recoverOpen} onOpenChange={setRecoverOpen}>
        <DialogContent className="max-w-2xl" data-testid="or-recover-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <RefreshCw className="w-4 h-4" /> Recover deleted rows (last 24h)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded border-2 border-emerald-400 bg-emerald-50 text-emerald-800 p-3 text-xs">
              <strong>Undo recent deletes.</strong> Any Order Register row deleted in the last 24 hours can be restored here.
              Rows re-added manually in the meantime are preserved (skipped).
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Supervisor Password *</label>
              <Input type="password" autoComplete="new-password" value={recoverPassword}
                placeholder="Enter supervisor password"
                onChange={(e) => setRecoverPassword(e.target.value)}
                data-testid="or-recover-password"
                autoComplete="new-password" />
            </div>
            {deletedBatches.length === 0 ? (
              <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500 text-center"
                   data-testid="or-recover-empty">
                No delete batches in the last 24 hours.
              </div>
            ) : (
              <div className="max-h-72 overflow-y-auto border border-emerald-200 rounded"
                   data-testid="or-recover-list">
                <table className="w-full text-[11px]">
                  <thead className="bg-emerald-100 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1">When</th>
                      <th className="text-left px-2 py-1">By</th>
                      <th className="text-left px-2 py-1">Rows</th>
                      <th className="text-left px-2 py-1">Services</th>
                      <th className="text-left px-2 py-1">Sample customers</th>
                      <th className="text-right px-2 py-1">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deletedBatches.map(b => (
                      <tr key={b.delete_batch_id} className="border-t border-emerald-100"
                          data-testid={`or-recover-batch-${b.delete_batch_id.slice(0, 8)}`}>
                        <td className="px-2 py-1 whitespace-nowrap">{new Date(b.ts).toLocaleString()}</td>
                        <td className="px-2 py-1">{b.user_email}</td>
                        <td className="px-2 py-1 font-semibold">{b.count}</td>
                        <td className="px-2 py-1">{b.modes.join(', ') || '—'}</td>
                        <td className="px-2 py-1">{b.sample_customers.slice(0, 2).join(', ')}{b.sample_customers.length > 2 ? '…' : ''}</td>
                        <td className="px-2 py-1 text-right">
                          <Button size="sm" variant="outline"
                            onClick={() => restoreBatch(b.delete_batch_id)}
                            disabled={!recoverPassword.trim() || restoringBatch === b.delete_batch_id}
                            data-testid={`or-recover-btn-${b.delete_batch_id.slice(0, 8)}`}
                            className="h-6 text-[11px] border-emerald-400 text-emerald-700 hover:bg-emerald-50">
                            {restoringBatch === b.delete_batch_id
                              ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Restoring…</>
                              : <>Restore</>}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRecoverOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl" data-testid="or-editor">
          <DialogHeader>
            <DialogTitle>{draft.id ? "Edit Order" : "New Order"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Date</Label>
              <Input value={draft.date} onChange={(e) => setDraft({...draft, date: e.target.value})}
                     placeholder="DD/MM/YYYY" data-testid="or-editor-date" /></div>
            <div><Label className="text-xs">Code (Client)</Label>
              <Input value={draft.code} onChange={(e) => setDraft({...draft, code: e.target.value})}
                     placeholder="BHAMUK" data-testid="or-editor-code" /></div>
            <div className="col-span-2"><Label className="text-xs">Customer Name</Label>
              <Input value={draft.customer_name} onChange={(e) => setDraft({...draft, customer_name: e.target.value})}
                     data-testid="or-editor-customer" /></div>
            <div className="col-span-2"><Label className="text-xs">Address</Label>
              <Input value={draft.address} onChange={(e) => setDraft({...draft, address: e.target.value})}
                     placeholder="Street, City, State, ZIP, Country" data-testid="or-editor-address" /></div>
            <div><Label className="text-xs">Medicine</Label>
              <MedicineSuggestInput value={draft.medicine || ""}
                     onChange={(v) => setDraft(d => ({ ...d, medicine: v }))}
                     onPick={(x) => {
                       setDraft(d => ({ ...d, medicine: x.medicine, second_name: (d.second_name || "").trim() ? d.second_name : (x.second_name || d.second_name) }));
                       if (x.second_name && !(draft.second_name || "").trim()) toast.success(`Second name auto-filled: ${x.second_name}`);
                     }}
                     placeholder="Type 2+ letters for suggestions"
                     testId="or-editor-medicine"
                     onBlur={async (e) => {
                       // Iter 150: alias auto-fill. If Second Name is empty
                       // and the medicine matches an alias, prefill it.
                       const med = (e.target.value || '').trim();
                       if (!med || (draft.second_name || '').trim()) return;
                       try {
                         const r = await axios.get(`${API}/product-aliases/lookup`,
                           { headers: getAuthHeader(), params: { medicine: med } });
                         const sn = r.data?.second_name;
                         if (sn) {
                           setDraft(d => ({ ...d, second_name: sn }));
                           toast.success(`Second name auto-filled: ${sn}`);
                         }
                       } catch { /* silent */ }
                     }} /></div>
            <div><Label className="text-xs">Quantity</Label>
              <Input value={draft.quantity} onChange={(e) => setDraft({...draft, quantity: e.target.value})}
                     data-testid="or-editor-qty" /></div>
            <div><Label className="text-xs">Tracking #</Label>
              <Input value={draft.tracking} onChange={(e) => setDraft({...draft, tracking: e.target.value})}
                     className="font-mono" data-testid="or-editor-tracking" /></div>
            <div><Label className="text-xs">Mode of Shipment</Label>
              <Select value={draft.mode || "__none__"}
                onValueChange={(v) => setDraft({...draft, mode: v === "__none__" ? "" : v})}>
                <SelectTrigger data-testid="or-editor-mode"><SelectValue placeholder="Select mode" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">(none)</SelectItem>
                  {modeOptions.filter(Boolean).map(m => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-xs">Second Name (Brand alias)</Label>
              <SecondNameSuggestInput value={draft.second_name || ""} onChange={(v) => setDraft(d => ({ ...d, second_name: v }))}
                     placeholder="Type 3+ letters for suggestions" testId="or-editor-second" /></div>
            <div className="col-span-2"><Label className="text-xs">Reason (of pending parcel)</Label>
              <Input value={draft.reason} onChange={(e) => setDraft({...draft, reason: e.target.value})}
                     placeholder="e.g. address incomplete, awaiting invoice…" data-testid="or-editor-reason" /></div>
            <div><Label className="text-xs">Invoice #</Label>
              <Input value={draft.invoice_no} onChange={(e) => setDraft({...draft, invoice_no: e.target.value.toUpperCase()})}
                     className="font-mono uppercase" placeholder="INV-2026-0001" data-testid="or-editor-invoice" /></div>
            <div><Label className="text-xs">Invoice Date</Label>
              <Input type="date" value={dmyToIso(draft.invoice_date)}
                     onChange={(e) => setDraft({...draft, invoice_date: fmtDmy(e.target.value)})}
                     data-testid="or-editor-invoice-date" /></div>
            <div className="col-span-2"><Label className="text-xs">Notes</Label>
              <Input value={draft.notes} onChange={(e) => setDraft({...draft, notes: e.target.value})}
                     data-testid="or-editor-notes" /></div>
          </div>
          {draft.id && (() => {
            const orig = rows.find(r => r.id === draft.id);
            const pk = orig ? parcelKey(orig) : "";
            const size = pk ? rows.filter(r => parcelKey(r) === pk).length : 1;
            const locked = orig?.cancelled ? "this parcel is cancelled"
              : orig?.invoice_status === "completed" ? `invoice ${orig.invoice_no} is completed` : "";
            return <AddProductsSection items={addItems} setItems={setAddItems} parcelSize={size} locked={locked} />;
          })()}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={saveDraft} disabled={saving} data-testid="or-editor-save">
              {saving ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Saving…</> : <><FileSpreadsheet className="w-3 h-3 mr-1" /> Save</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 155 — Alias Suggestions Tray */}
      <Sheet open={trayOpen} onOpenChange={setTrayOpen}>
        <SheetContent side="right" className="w-[420px] sm:w-[480px] flex flex-col"
                      data-testid="or-alias-tray">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" />
              Alias Suggestions
              <span className="ml-auto text-[11px] font-normal text-slate-500">
                {unmatchedList.length} medicine{unmatchedList.length === 1 ? '' : 's'} on this page
              </span>
            </SheetTitle>
          </SheetHeader>
          <div className="text-[11px] text-slate-500 mt-1">
            Pending alias mappings for this page. <b className="text-emerald-700">Green rows</b> are
            pre-filled from the page's own Second Names; <b className="text-amber-700">amber rows</b> are
            auto-suggested — review, edit if wrong, then click <b>Save All</b> to store
            every mapping in one click.
            {traySuggesting && (
              <span className="ml-1 inline-flex items-center gap-1 text-amber-600"
                    data-testid="or-tray-suggesting">
                <RefreshCw className="w-3 h-3 animate-spin" /> finding suggestions…
              </span>
            )}
          </div>
          <div className="flex-1 overflow-y-auto mt-3 space-y-2 pr-1">
            {unmatchedList.length === 0 ? (
              <div className="text-center text-slate-400 py-10 text-xs"
                   data-testid="or-tray-empty">
                🎉 Every medicine on this page already has an alias match.
              </div>
            ) : unmatchedList.map(({ medicine, count, kind }) => (
              <div key={medicine}
                   className="border border-slate-200 rounded p-2 bg-slate-50/60"
                   data-testid={`or-tray-item-${medicine}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-medium text-slate-800 break-words">
                    {medicine}
                  </div>
                  <span className="text-[10px] rounded-full bg-indigo-100 text-indigo-700 px-1.5 font-semibold whitespace-nowrap">
                    {count} row{count === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="mt-1.5 flex items-center gap-1.5">
                  <div className="flex-1">
                    <SecondNameSuggestInput
                      value={trayDrafts[medicine] || ''}
                      onChange={(v) => setTrayDraft(medicine, v)}
                      onEnter={() => saveTrayOne(medicine)}
                      placeholder="Canonical Second Name — type 3+ letters for suggestions"
                      className={`h-7 text-xs ${traySuggested[medicine] === 'ready'
                        ? 'border-emerald-300 bg-emerald-50/60'
                        : traySuggested[medicine] === 'suggested'
                          ? 'border-amber-300 bg-amber-50/60' : ''}`}
                      testId={`or-tray-input-${medicine}`}
                    />
                    {traySuggested[medicine] === 'ready' && (
                      <div className="text-[9px] text-emerald-600 mt-0.5"
                           data-testid={`or-tray-ready-${medicine}`}>
                        ✓ from this page's rows — review &amp; Save All
                      </div>
                    )}
                    {traySuggested[medicine] === 'suggested' && (
                      <div className="text-[9px] text-amber-600 mt-0.5"
                           data-testid={`or-tray-suggested-${medicine}`}>
                        ⚡ suggested — edit if wrong
                      </div>
                    )}
                  </div>
                  <Button size="sm" className="h-7 px-2 bg-indigo-600 hover:bg-indigo-700"
                          onClick={() => saveTrayOne(medicine)}
                          disabled={!(trayDrafts[medicine] || '').trim()}
                          data-testid={`or-tray-save-${medicine}`}>
                    <BookmarkPlus className="w-3 h-3 mr-1" /> Save
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <SheetFooter className="pt-2 border-t border-slate-100 flex-row justify-between gap-2">
            <Button variant="ghost" size="sm" onClick={() => setTrayOpen(false)}
                    data-testid="or-tray-close">Close</Button>
            <Button size="sm" variant="outline"
                    onClick={saveTrayAll}
                    disabled={traySaving || unmatchedList.length === 0 || trayFilledCount === 0}
                    data-testid="or-tray-save-all">
              {traySaving ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Saving…</> :
                            <><BookmarkPlus className="w-3 h-3 mr-1" /> Save All{trayFilledCount > 0 ? ` (${trayFilledCount})` : ''}</>}
            </Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={saveTrayAllAndFill}
                    disabled={traySaving || unmatchedList.length === 0 || trayFilledCount === 0}
                    data-testid="or-tray-save-and-fill"
                    title="Save every filled mapping AND back-fill the matching empty Second Name cells on this page">
              {traySaving ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Saving…</> :
                            <><Wand2 className="w-3 h-3 mr-1" /> Save All &amp; Fill</>}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Iter 157 — Missing-Invoice Bulk-Fill dialog */}
      <Dialog open={bulkInvOpen} onOpenChange={setBulkInvOpen}>
        <DialogContent className="max-w-md" data-testid="or-bulk-inv-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
              Fill Missing Invoice
            </DialogTitle>
          </DialogHeader>
          <div className="text-[11px] text-slate-500 -mt-1">
            Applies to every row in the <b>current filter</b> that has no invoice yet.
            Rows that already have an invoice are left untouched.
          </div>
          <div className="grid gap-3 py-2">
            <div>
              <Label className="text-xs">Invoice # *</Label>
              <Input value={bulkInvNo}
                     onChange={(e) => setBulkInvNo(e.target.value.toUpperCase())}
                     placeholder="e.g. IN-2026-004123"
                     className="uppercase font-mono"
                     data-testid="or-bulk-inv-no" />
            </div>
            <div>
              <Label className="text-xs">Invoice Date</Label>
              <Input type="date" value={dmyToIso(bulkInvDate)}
                     onChange={(e) => setBulkInvDate(fmtDmy(e.target.value))}
                     data-testid="or-bulk-inv-date" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkInvOpen(false)}
                    data-testid="or-bulk-inv-cancel">Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={runBulkFillInvoice}
                    disabled={bulkInvSaving || !bulkInvNo.trim()}
                    data-testid="or-bulk-inv-submit">
              {bulkInvSaving ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Filling…</> :
                                'Fill All Missing'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 176 — Pending Parcel List modal */}
      <Dialog open={pendingOpen} onOpenChange={setPendingOpen}>
        <DialogContent className="max-w-md" data-testid="or-pending-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-600" />
              Generate Pending Parcel List
            </DialogTitle>
          </DialogHeader>
          <div className="text-[11px] text-slate-500 -mt-1">
            Only rows whose <b>Reason</b> column is filled in are included.
            Choose how they should be grouped and the output format.
          </div>
          <div className="grid gap-3 py-2">
            <div>
              <Label className="text-xs mb-1 block">Grouping</Label>
              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { v: "full",    lbl: "Full data" },
                  { v: "service", lbl: "Service-wise" },
                  { v: "client",  lbl: "Client-code-wise" },
                ].map(o => (
                  <button key={o.v} type="button"
                    onClick={() => setPendingGrouping(o.v)}
                    className={`text-[11px] font-semibold rounded px-2 py-1.5 border transition ${
                      pendingGrouping === o.v
                        ? 'bg-amber-600 text-white border-amber-600'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid={`or-pending-grouping-${o.v}`}>
                    {o.lbl}
                  </button>
                ))}
              </div>
              {pendingGrouping === "client" && pendingFmt === "excel" && (
                <p className="text-[10px] text-amber-700 mt-1" data-testid="or-pending-zip-hint">
                  You'll get a ZIP with one workbook per client — perfect for printing each client's list separately.
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs mb-1 block">Format</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  { v: "excel", lbl: "Excel (.xlsx)" },
                  { v: "pdf",   lbl: "PDF" },
                ].map(o => (
                  <button key={o.v} type="button"
                    onClick={() => setPendingFmt(o.v)}
                    className={`text-[11px] font-semibold rounded px-2 py-1.5 border transition ${
                      pendingFmt === o.v
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid={`or-pending-fmt-${o.v}`}>
                    {o.lbl}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPendingOpen(false)}
                    data-testid="or-pending-cancel">Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700"
                    onClick={runPendingReport}
                    disabled={pendingRunning}
                    data-testid="or-pending-submit">
              {pendingRunning ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Generating…</> :
                                <><Download className="w-4 h-4 mr-1" /> Generate</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 176 — Emirates Tracking Fill: confirmation + result modal */}
      <Dialog open={!!emiratesFile}
              onOpenChange={(v) => { if (!v) { setEmiratesFile(null); setEmiratesResult(null); } }}>
        <DialogContent className="max-w-lg" data-testid="or-emirates-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-4 h-4 text-purple-600" />
              Emirates Tracking Fill
            </DialogTitle>
          </DialogHeader>
          <div className="text-[11px] text-slate-500 -mt-1">
            Fills the Tracking column for Order Register rows that match on
            <b> Client Code + Customer Name</b> (both case-insensitive). Never
            overwrites rows that already have a tracking value.
          </div>
          <div className="grid gap-2 py-2">
            <div className="text-[11px] flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
              <b className="truncate">{emiratesFile?.name}</b>
              <span className="text-slate-500">
                ({Math.round((emiratesFile?.size || 0) / 1024)} KB)
              </span>
            </div>
            {emiratesResult && (
              <div className="rounded border border-slate-200 p-2 space-y-1.5 bg-slate-50">
                <div className="text-[11px]">
                  <b className="text-emerald-700">Filled:</b>{' '}
                  {emiratesResult.rows_modified} row{emiratesResult.rows_modified === 1 ? '' : 's'}
                  {' '}across {emiratesResult.updated} customer{emiratesResult.updated === 1 ? '' : 's'}
                </div>
                {emiratesResult.skipped_no_tracking > 0 && (
                  <div className="text-[11px] text-slate-600">
                    Skipped (no tracking): {emiratesResult.skipped_no_tracking}
                  </div>
                )}
                {emiratesResult.unmatched?.length > 0 && (
                  <details className="text-[11px]">
                    <summary className="cursor-pointer text-amber-700 font-semibold">
                      Unmatched: {emiratesResult.unmatched.length} — click to view
                    </summary>
                    <div className="mt-1 max-h-40 overflow-y-auto text-[10px] font-mono bg-white p-2 rounded">
                      {emiratesResult.unmatched.slice(0, 40).map((u, i) => (
                        <div key={i}>{u.client_code} · {u.customer_name} · {u.tracking}</div>
                      ))}
                      {emiratesResult.unmatched.length > 40 && <div>…</div>}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost"
                    onClick={() => { setEmiratesFile(null); setEmiratesResult(null); }}
                    data-testid="or-emirates-cancel">
              {emiratesResult ? 'Close' : 'Cancel'}
            </Button>
            {!emiratesResult && (
              <Button className="bg-purple-600 hover:bg-purple-700"
                      onClick={runEmiratesFill}
                      disabled={emiratesRunning}
                      data-testid="or-emirates-submit">
                {emiratesRunning ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Filling…</> :
                                     <><Truck className="w-4 h-4 mr-1" /> Fill Tracking</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PendingCenter open={pcOpen} onClose={() => setPcOpen(false)} isAdmin={isAdmin}
        onShowInTable={(pf) => {
          setPendingOnly(true); setPendingScope(pf.reason_scope === "with_summary" ? "with" : (pf.reason_scope || "with")); setAge7Filter(!!pf.age7);
          setDateFrom(pf.date_from || ""); setDateTo(pf.date_to || "");
          setClientFilter(pf.client_code || ""); setTeamView("all"); setInvoiceStatusFilter(""); setPage(1);
          if (pf.service || pf.user_id) toast.info("Service / user filters apply to downloads only — table shows all pending for the other filters");
        }} />
      <OrderUploadWizard open={wizardOpen} onClose={() => setWizardOpen(false)} onImported={fetchRows} />
      {canManageOverwrites && (
        <OverwriteHistory open={owhOpen} onClose={() => setOwhOpen(false)} onRestored={fetchRows} />
      )}

      {/* Iter 254 — Upload Analytics (Test 8-9) */}
      <Dialog open={uaOpen} onOpenChange={setUaOpen}>
        <DialogContent className="max-w-5xl" data-testid="or-uploads-dialog">
          <DialogHeader>
            <DialogTitle>Order Register Uploads</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="text-slate-500">From</span>
            <input type="date" value={uaFrom} onChange={e => setUaFrom(e.target.value)}
              className="px-2 py-1 border border-slate-300 rounded text-xs" data-testid="or-uploads-from" />
            <span className="text-slate-500">to</span>
            <input type="date" value={uaTo} onChange={e => setUaTo(e.target.value)}
              className="px-2 py-1 border border-slate-300 rounded text-xs" data-testid="or-uploads-to" />
            {uaData.can_see_all && (
              <select value={uaUploader} onChange={e => setUaUploader(e.target.value)}
                className="px-2 py-1 border border-slate-300 rounded text-xs" data-testid="or-uploads-uploader">
                <option value="">All uploaders</option>
                {(uaData.uploaders || []).map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            )}
            <Button size="sm" onClick={loadUploadAnalytics} disabled={uaLoading}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="or-uploads-apply">
              {uaLoading ? "…" : "Apply"}
            </Button>
            {["admin", "supervisor", "manager"].includes(user?.role) && (
              <Button size="sm" variant="outline" onClick={downloadScorecard}
                      className="text-violet-700 border-violet-300 hover:bg-violet-50"
                      title="Download the latest weekly client scorecard Excel"
                      data-testid="or-scorecard-download">
                <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Weekly scorecard
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={exportUploads}
                    disabled={uaLoading || !(uaData.uploads || []).length}
                    className="relative text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                    data-testid="or-uploads-export">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Excel
              {uaWithSnapshot && (
                <span className="absolute -top-1.5 -right-1.5 px-1 rounded-full bg-purple-600 text-white text-[8px] font-bold leading-3"
                      title="Export will include the audit snapshot sheet"
                      data-testid="or-uploads-export-snapshot-badge">
                  +SNAP
                </span>
              )}
            </Button>
            <label className="flex items-center gap-1 text-slate-600 select-none cursor-pointer"
                   title="Include a Details sheet with the upload snapshot (invoices + modes)">
              <input type="checkbox" checked={uaWithSnapshot}
                     onChange={e => setUaWithSnapshot(e.target.checked)}
                     data-testid="or-uploads-snapshot" />
              With snapshot
            </label>
            <span className="ml-auto text-slate-500">{uaData.count || 0} upload{(uaData.count || 0) === 1 ? '' : 's'}</span>
          </div>
          {/* Iter 255 — uploads-per-day trend chart (IST). */}
          {(uaData.trend || []).length > 0 && (
            <div className="mt-2 rounded border border-slate-200 bg-slate-50 p-2"
                 data-testid="or-uploads-trend">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
                  Uploads per day (IST)
                </span>
                <span className="text-[10px] text-slate-500">
                  {uaData.trend.length} day{uaData.trend.length === 1 ? '' : 's'} ·{' '}
                  <span className="text-emerald-700">■ verified</span>{' · '}
                  <span className="text-amber-700">■ mismatch</span>
                </span>
              </div>
              {(() => {
                const bars = uaData.trend;
                const max = Math.max(1, ...bars.map(b => b.uploads));
                const CHART_H = 64; // px, usable bar height
                return (
                  <div className="flex items-end gap-1" style={{ height: `${CHART_H + 24}px` }}>
                    {bars.map(b => {
                      const total = b.uploads || 0;
                      const barH = Math.max(4, Math.round((total / max) * CHART_H));
                      const vH = total ? Math.round((b.verified / total) * barH) : 0;
                      const mH = Math.max(0, barH - vH);
                      const label = b.date.slice(5); // MM-DD
                      return (
                        <div key={b.date}
                             className="flex-1 flex flex-col items-center justify-end min-w-0"
                             data-testid={`or-uploads-trend-bar-${b.date}`}
                             title={`${b.date} · ${total} upload${total === 1 ? '' : 's'} · ${b.verified} verified · ${b.mismatched} mismatch · ${b.rows_inserted} rows`}>
                          <span className="text-[9px] text-slate-600 font-mono leading-none mb-0.5">
                            {total}
                          </span>
                          <div className="w-full flex flex-col justify-end rounded-sm overflow-hidden"
                               style={{ height: `${barH}px` }}>
                            {mH > 0 && (
                              <div className="bg-amber-400 w-full" style={{ height: `${mH}px` }} />
                            )}
                            {vH > 0 && (
                              <div className="bg-emerald-500 w-full" style={{ height: `${vH}px` }} />
                            )}
                          </div>
                          <span className="text-[9px] text-slate-500 font-mono mt-0.5 truncate w-full text-center">
                            {label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}
          <div className="max-h-[60vh] overflow-y-auto border rounded">
            <table className="w-full text-[11px]">
              <thead className="bg-slate-100 text-slate-600 uppercase sticky top-0">
                <tr>
                  <th className="px-2 py-1 text-left w-24">Date</th>
                  <th className="px-2 py-1 text-left w-20">Time</th>
                  <th className="px-2 py-1 text-left">Uploader</th>
                  <th className="px-2 py-1 text-left">File</th>
                  <th className="px-2 py-1 text-right w-16">Rows</th>
                  <th className="px-2 py-1 text-right w-16">Invoices</th>
                  <th className="px-2 py-1 text-right w-16">Parcels</th>
                  <th className="px-2 py-1 text-right w-16">Inserted</th>
                  <th className="px-2 py-1 text-center w-20">Verified</th>
                  <th className="px-2 py-1 text-center w-20">Audit</th>
                </tr>
              </thead>
              <tbody>
                {(uaData.uploads || []).map(u => {
                  const dt = u.created_at
                    ? new Date(u.created_at).toLocaleString('en-IN',
                        { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
                          hour: '2-digit', minute: '2-digit', hour12: true })
                    : '';
                  const [d, t] = dt.split(', ');
                  return (
                    <tr key={u.id} className="border-t hover:bg-slate-50" data-testid={`or-upload-row-${u.id}`}>
                      <td className="px-2 py-1 font-mono">{d}</td>
                      <td className="px-2 py-1 font-mono">{t}</td>
                      <td className="px-2 py-1">{u.uploader_name}
                        {u.uploader_team && <span className="text-slate-400 ml-1">({u.uploader_team})</span>}</td>
                      <td className="px-2 py-1 truncate" title={u.filename}>{u.filename}</td>
                      <td className="px-2 py-1 text-right font-mono">{u.rows_detected}</td>
                      <td className="px-2 py-1 text-right font-mono">{u.invoices_detected}</td>
                      <td className="px-2 py-1 text-right font-mono">{u.parcels_detected}</td>
                      <td className="px-2 py-1 text-right font-mono">{u.rows_inserted}</td>
                      <td className="px-2 py-1 text-center">
                        {u.verified
                          ? <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">✓</span>
                          : <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">⚠</span>}
                      </td>
                      <td className="px-2 py-1 text-center">
                        <button type="button"
                          className="px-1.5 py-0.5 rounded border border-sky-300 text-sky-700 hover:bg-sky-50 text-[10px] font-semibold transition"
                          title={u.compare_ref
                            ? "Download the compare sheet saved with this upload"
                            : "Rebuild the compare sheet from this upload's rows vs the current register"}
                          onClick={() => downloadUploadCompare(u)}
                          data-testid={`or-upload-compare-${u.id}`}>
                          {u.compare_ref ? "Compare" : "Rebuild"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {(uaData.uploads || []).length === 0 && !uaLoading && (
                  <tr><td colSpan={10} className="text-center text-slate-400 py-6">No uploads in this range.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Iter 262 — Cancel parcel: warns with customer + tracking, then
          asks for the caller's OWN login password. */}
      <Dialog open={!!cancelTarget} onOpenChange={(o) => { if (!o) { setCancelTarget(null); setCancelPassword(""); } }}>
        <DialogContent className="max-w-md" data-testid="or-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" /> Cancel this parcel?
            </DialogTitle>
            <DialogDescription>
              This marks the parcel as cancelled — it leaves Pending and
              moves to the Cancelled view. Search still finds it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 space-y-1">
              <div data-testid="or-cancel-customer">
                <span className="text-slate-500">Customer:</span>{" "}
                <b>{cancelTarget?.customer_name || "—"}</b>
              </div>
              {cancelTarget?.tracking && (
                <div data-testid="or-cancel-tracking">
                  <span className="text-slate-500">Tracking:</span>{" "}
                  <b className="font-mono">{cancelTarget.tracking}</b>
                </div>
              )}
              <div className="text-xs text-slate-500 pt-1" data-testid="or-cancel-rowcount">
                {cancelTarget
                  ? `${rows.filter(rr => parcelKey(rr) === parcelKey(cancelTarget)).length} row(s) in this parcel will be cancelled.`
                  : ""}
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Reason (required) — recorded in the audit log</Label>
              {cancelTarget && <ReasonChips onChange={setCancelReason} testId="or-cancel-reason" />}
            </div>
            <div>
              <Label htmlFor="or-cancel-password">Confirm with your login password</Label>
              <Input id="or-cancel-password" type="password" autoComplete="new-password"
                     value={cancelPassword}
                     onChange={(e) => setCancelPassword(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Enter') cancelParcel(); }}
                     placeholder="Your own password"
                     data-testid="or-cancel-password" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline"
                    onClick={() => { setCancelTarget(null); setCancelPassword(""); setCancelReason(""); }}
                    data-testid="or-cancel-abort">
              Keep parcel
            </Button>
            <Button className="bg-rose-600 hover:bg-rose-700"
                    onClick={cancelParcel} disabled={cancelling}
                    data-testid="or-cancel-confirm">
              {cancelling
                ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                : <Ban className="w-4 h-4 mr-1" />}
              {cancelling ? "Cancelling…" : "Yes, cancel parcel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 265 — bulk cancel: N parcels, one password, one reason. */}
      <Dialog open={bulkCancelOpen} onOpenChange={(o) => { if (!o) { setBulkCancelOpen(false); setBulkCancelPassword(""); setBulkCancelReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="or-bulk-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" /> Cancel {selected.size} selected row{selected.size === 1 ? "" : "s"}?
            </DialogTitle>
            <DialogDescription>
              Multi-row parcels are collapsed to a single parcel-level cancel
              on the server. Every cancellation gets its own audit entry, plus
              a bulk summary entry so admins can trace the batch.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-xs text-rose-700" data-testid="or-bulk-cancel-count">
              <b>{selected.size}</b> selected row{selected.size === 1 ? "" : "s"} across the current page.
              Each parcel will show the red CANCELLED badge and leave Pending.
            </div>
            <div>
              <Label className="text-xs text-slate-500">Reason (required) — recorded in the audit log</Label>
              {bulkCancelOpen && <ReasonChips onChange={setBulkCancelReason} testId="or-bulk-cancel-reason" />}
            </div>
            <div>
              <Label htmlFor="or-bulk-cancel-password">Confirm with your login password</Label>
              <Input id="or-bulk-cancel-password" type="password" autoComplete="new-password"
                     value={bulkCancelPassword}
                     onChange={(e) => setBulkCancelPassword(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Enter') bulkCancel(); }}
                     placeholder="Your own password"
                     data-testid="or-bulk-cancel-password"
                     autoFocus />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline"
                    onClick={() => { setBulkCancelOpen(false); setBulkCancelPassword(""); setBulkCancelReason(""); }}
                    data-testid="or-bulk-cancel-abort">
              Keep parcels
            </Button>
            <Button className="bg-rose-600 hover:bg-rose-700"
                    onClick={bulkCancel} disabled={bulkCancelling}
                    data-testid="or-bulk-cancel-confirm">
              {bulkCancelling
                ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                : <Ban className="w-4 h-4 mr-1" />}
              {bulkCancelling ? "Cancelling…" : `Yes, cancel ${selected.size}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InvoiceSlipCheckDialog invoiceNo={slipCheckInv} isAdmin={isAdmin} deferred={slipDeferred}
        onDone={() => { setSlipCheckInv(null); setSlipDeferred(false); setSlipRefresh(n => n + 1); }} />
      <BulkReasonDialog open={bulkReasonOpen} onClose={() => setBulkReasonOpen(false)}
        ids={Array.from(selected)} suggestions={reasonSuggestions}
        onDone={() => { setBulkReasonOpen(false); setSelected(new Set()); fetchRows(); }} />

      {/* Iter 264 — Restore cancelled parcel (same own-password guard). */}
      <Dialog open={!!restoreTarget} onOpenChange={(o) => { if (!o) { setRestoreTarget(null); setRestorePassword(""); } }}>
        <DialogContent className="max-w-md" data-testid="or-restore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Undo2 className="w-5 h-5" /> Restore this parcel?
            </DialogTitle>
            <DialogDescription>
              This undoes the cancel — the parcel will go back to the
              register and reappear in the normal views.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 space-y-1">
              <div data-testid="or-restore-customer">
                <span className="text-slate-500">Customer:</span>{" "}
                <b>{restoreTarget?.customer_name || "—"}</b>
              </div>
              {restoreTarget?.tracking && (
                <div data-testid="or-restore-tracking">
                  <span className="text-slate-500">Tracking:</span>{" "}
                  <b className="font-mono">{restoreTarget.tracking}</b>
                </div>
              )}
              {restoreTarget?.cancelled_by_name && (
                <div className="text-xs text-slate-500">
                  Originally cancelled by{" "}
                  <b>{restoreTarget.cancelled_by_name}</b>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="or-restore-password">Confirm with your login password</Label>
              <Input id="or-restore-password" type="password" autoComplete="new-password"
                     value={restorePassword}
                     onChange={(e) => setRestorePassword(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Enter') restoreParcel(); }}
                     placeholder="Your own password"
                     data-testid="or-restore-password"
                     autoFocus />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline"
                    onClick={() => { setRestoreTarget(null); setRestorePassword(""); }}
                    data-testid="or-restore-abort">
              Keep cancelled
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={restoreParcel} disabled={restoring}
                    data-testid="or-restore-confirm">
              {restoring
                ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                : <Undo2 className="w-4 h-4 mr-1" />}
              {restoring ? "Restoring…" : "Yes, restore"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
